import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Layout,
  Row,
  Col,
  Card,
  Tabs,
  Tag,
  Table,
  Icon,
  DatePicker,
  Checkbox,
  Select,
  Button,
  Input,
  InputNumber,
  Alert,
  Modal,
  Radio,
  Badge,
  Tooltip,
  Divider,
  Spin,
  Typography,
  message,
  Popover,
  Empty,
  Drawer,
  Descriptions,
  Timeline,
} from 'antd';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import moment from 'moment';

// Apply animated theme
am4core.useTheme(am4themes_animated);
import { 
  fetchLastFullTankEvents, 
  transformToTrendData,
  fetchAllFuelEntriesBetweenFullTanks,
  transformFuelEntryForTable,
  fetchIotRefuelEvents,
  transformIotRefuelEvent,
  fetchCombinedIotData,
  transformIotDataForUI,
  findAssetIdByCode,
  fetchFuelEntries,
  fetchDailyIotSummary,
  METER_PRIORITY,
  METER_LABELS,
  getMeterRunsBetween,
  computeOperatingLevel,
  operatingLevelPercentOfMax,
  operatingLevelBand
} from './utils/fuelService';
import { apiGet, apiPost } from './utils/apiService';

const { Content } = Layout;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { RadioGroup } = Radio.Group;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// ---------------------------------------------------------------------------
// Alert History (open + closed fuel alerts) - server-side scoping
// ---------------------------------------------------------------------------
// The alert history tab lists the asset's PAST fuel alerts, i.e. alerts created
// at or before the refuel event under analysis. An alert that is still open is
// as relevant as a resolved one - arguably more so - so BOTH lifecycle states
// are fetched. The backend splits them across two endpoints, and both support
// the same server-side creation-time window (see assetpro-knowledge /
// API_SWAGGER_DISCOVERY.md):
//
//   GET /alerts/closed?from=<epochMs>&to=<epochMs>       -> resolved alerts
//   GET /alerts/non-closed?from=<epochMs>&to=<epochMs>   -> still-open alerts
//
// - epoch unit is MILLISECONDS
// - the server filters on the alert's CREATION time (alertDateTime), not on the
//   closure time - which is exactly the field this tab filters on
// - `from` is inclusive, `to` is the half-open upper bound
//
// The endpoint an alert came back from is the authoritative record of its
// lifecycle state - it is stamped onto the row as `_lifecycle` rather than
// being inferred from `currentStatus`, which is a workflow stage, not a
// closed/open flag.
//
// Same window contract as src/ClosedAlertsReport.js.

// Which endpoint a history row came from. Set from the response it arrived in,
// never guessed from the record's own fields.
const ALERT_LIFECYCLE = { OPEN: 'OPEN', CLOSED: 'CLOSED' };

const ALERT_HISTORY_SOURCES = [
  { lifecycle: ALERT_LIFECYCLE.CLOSED, path: '/alerts/closed', label: 'closed' },
  { lifecycle: ALERT_LIFECYCLE.OPEN, path: '/alerts/non-closed', label: 'open' },
];

// Ignore closures: the backend has NO `IGNORED` currentStatus. An alert closed
// with "Ignore" comes back as `currentStatus = ACTION_TAKEN`; the only record of
// the closure type is the terminal ACTION_TAKEN statusHistory entry, whose
// `remarks` field reads "Ignore" / "Ignored".
//
// That remark is SYSTEM-GENERATED - the backend writes it when the Ignore action
// is taken. It is not free text a user types, so the exact-match test below is
// reading a fixed marker, not interpreting prose.
//
// Same rule, same regex and same scan direction as
// src/ClosedAlertsReport.js and the AssetPro Ops Dashboard alert KPIs
// (aiasl-ops-hub/src/services/alertsService.js -> wasIgnoredClosure). The three
// screens must never disagree about what "Ignored" means.
const IGNORE_REMARK_RE = /^ignore[d]?$/i;

// True when this alert's history encodes an "Ignore" closure. Only the LATEST
// ACTION_TAKEN entry is examined - that is the closure that produced the current
// status; earlier ones belong to superseded workflow passes.
const wasIgnoredClosure = (history) => {
  if (!Array.isArray(history)) return false;
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry && entry.status === 'ACTION_TAKEN') {
      return IGNORE_REMARK_RE.test(String(entry.remarks || '').trim());
    }
  }
  return false;
};

// The status this screen displays. Equal to the API's `currentStatus` in every
// case except an Ignore closure, which the API reports as ACTION_TAKEN. Returns
// a missing status unchanged - absence is never turned into a real status. The
// raw `currentStatus` is left on the record so the derivation stays auditable.
const getEffectiveAlertStatus = (alert) => {
  if (!alert) return null;
  const raw = alert.currentStatus;
  if (raw === 'ACTION_TAKEN' && wasIgnoredClosure(alert.statusHistory)) {
    return 'IGNORED';
  }
  return raw;
};

// Stable identity for de-duplication. `_id` is the document key; `alertId` is
// the human-facing code and is only used when `_id` is absent. Returns null
// when neither exists - such a record cannot be matched and is left alone.
const getAlertIdentity = (alert) => {
  if (!alert) return null;
  if (alert._id) return `id:${alert._id}`;
  if (alert.alertId) return `code:${alert.alertId}`;
  return null;
};

// How far back the alert history query looks, relative to the refuel event
// being analysed. This is the query scope, so it is user-selectable and is
// always stated in the UI - alerts older than the window are simply not fetched
// and must never be presented as "no alerts existed".
const ALERT_HISTORY_LOOKBACK_OPTIONS = [
  { value: '7d', label: 'Last 7 days', amount: 7, unit: 'days' },
  { value: '14d', label: 'Last 14 days', amount: 14, unit: 'days' },
  { value: '1m', label: 'Last 1 month', amount: 1, unit: 'months' },
  { value: '2m', label: 'Last 2 months', amount: 2, unit: 'months' },
  { value: '3m', label: 'Last 3 months', amount: 3, unit: 'months' },
  { value: '6m', label: 'Last 6 months', amount: 6, unit: 'months' },
  { value: '1y', label: 'Last 1 year', amount: 1, unit: 'years' },
  { value: '2y', label: 'Last 2 years', amount: 2, unit: 'years' },
  { value: '5y', label: 'Last 5 years', amount: 5, unit: 'years' },
];

// Sentinel for the user-supplied date range. Not a preset: both bounds come
// from the picker, and without two valid dates no query is issued at all.
const ALERT_HISTORY_CUSTOM_LOOKBACK = 'custom';

// Default deliberately kept short: the tab opens on every alert analysis, and a
// year of closed alerts is a large query for a question that is usually about
// the last few days. Widening is one click and the loaded window is always
// stated, so a short default never reads as "no older alerts exist".
const DEFAULT_ALERT_HISTORY_LOOKBACK = '7d';

// Resolve an alert's CREATION timestamp (epoch ms).
// `alertDateTime` is the backend's authoritative creation field (epoch ms) and
// is what the server filters on; `createdAt` is the ISO document timestamp and
// is used only when `alertDateTime` is absent. Returns null when neither is
// present - the caller must flag that record rather than assume a date.
const getAlertCreatedMs = (alert) => {
  const alertDateTime = Number(alert && alert.alertDateTime);
  if (Number.isFinite(alertDateTime) && alertDateTime > 0) return alertDateTime;
  const parsed = Date.parse(alert && alert.createdAt);
  return Number.isFinite(parsed) ? parsed : null;
};

// Build the half-open epoch-ms window for the alert history query.
//   to   = refuel moment + 1ms, so alerts created exactly at the refuel moment
//          are still returned (`to` is exclusive) and the window matches the
//          "created at or before the refuel event" rule used when filtering.
//   from = start of the day one lookback period before the refuel moment, or
//          the start of the picked "from" day for a custom range.
//
// Returns { window, error }. `window` is null whenever no honest query can be
// built (missing refuel time, incomplete custom range); `error` then says which
// input is missing so the caller can surface it instead of falling back to an
// unscoped or default-scoped request.
const buildAlertHistoryWindowMs = (refuelTime, lookbackValue, customRange) => {
  if (!refuelTime) {
    return {
      window: null,
      error: 'Alert history needs the refuel date and time of the alert being analysed - it was missing, so no alerts were loaded.',
    };
  }
  const refuelMoment = moment(refuelTime, 'YYYY-MM-DD HH:mm:ss');
  if (!refuelMoment.isValid()) {
    return {
      window: null,
      error: 'Alert history needs the refuel date and time of the alert being analysed - it could not be read, so no alerts were loaded.',
    };
  }

  // Upper bound of any window: the refuel event itself. Past alerts only.
  const refuelToMs = refuelMoment.valueOf() + 1;

  if (lookbackValue === ALERT_HISTORY_CUSTOM_LOOKBACK) {
    const [customFrom, customTo] = Array.isArray(customRange) ? customRange : [];
    if (!customFrom || !customTo || !moment(customFrom).isValid() || !moment(customTo).isValid()) {
      return {
        window: null,
        error: 'Select both a start and an end date for the custom look-back range - no alerts were loaded until a complete range is chosen.',
      };
    }

    const fromMs = moment(customFrom).startOf('day').valueOf();
    const pickedToMs = moment(customTo).endOf('day').valueOf() + 1;
    // This tab lists alerts created at or before the refuel event, so a range
    // ending after it is clamped to the refuel moment. The clamp is recorded
    // rather than applied silently - the UI states the window actually queried.
    const clampedToRefuel = pickedToMs > refuelToMs;
    const toMs = clampedToRefuel ? refuelToMs : pickedToMs;

    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
      return {
        window: null,
        error: 'The custom look-back range does not cover any time at or before this refuel event, so no alerts were loaded.',
      };
    }

    return {
      window: {
        fromMs,
        toMs,
        label: `custom range (${moment(fromMs).format('DD-MMM-YYYY')} to ${moment(toMs - 1).format('DD-MMM-YYYY HH:mm')})`,
        isCustom: true,
        clampedToRefuel,
      },
      error: null,
    };
  }

  const option =
    ALERT_HISTORY_LOOKBACK_OPTIONS.find(o => o.value === lookbackValue) ||
    ALERT_HISTORY_LOOKBACK_OPTIONS.find(o => o.value === DEFAULT_ALERT_HISTORY_LOOKBACK);

  const fromMs = refuelMoment.clone().subtract(option.amount, option.unit).startOf('day').valueOf();
  if (!Number.isFinite(fromMs) || refuelToMs <= fromMs) {
    return {
      window: null,
      error: 'The selected look-back period does not produce a valid date range, so no alerts were loaded.',
    };
  }

  return {
    window: {
      fromMs,
      toMs: refuelToMs,
      label: option.label,
      isCustom: false,
      clampedToRefuel: false,
    },
    error: null,
  };
};

const normalizeStandardRateForDisplay = (rate, unitHint = '') => {
  if (rate === null || rate === undefined || isNaN(rate) || Number(rate) <= 0) {
    return null;
  }

  const normalizedUnitHint = String(unitHint).toUpperCase();
  const requiresInverse =
    normalizedUnitHint.includes('L_PER_KM') ||
    normalizedUnitHint.includes('L/KM') ||
    normalizedUnitHint.includes('L_PER_KWH') ||
    normalizedUnitHint.includes('L/KWH');

  if (!requiresInverse) {
    return Number(rate);
  }

  return Number(rate) > 0 ? (1 / Number(rate)) : null;
};

// Split IoT day rows into those expressed in the chart's unit and those that are not.
// A day's rate can only be compared with the standard when both share one unit, so a
// day in another unit (e.g. L/Hours on a DG day with no KWH reading) is never plotted
// on the axis — it is returned separately so the chart can say which days were left out.
const splitIotDaysByUnit = (data = [], unit = '') => {
  const plotted = [];
  const otherUnit = [];
  (data || []).forEach(item => {
    if (item && item.unit === unit) plotted.push(item);
    else if (item) otherUnit.push(item);
  });
  return { plotted, otherUnit };
};

// AMCharts component for consumption trend
const ConsumptionTrendChart = ({ data, standardRate, unit, operatingLevelUnit, maxOperatingLevel }) => {
  const chartRef = useRef(null);
  const chartDivRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!chartDivRef.current) return;

    // Create chart instance
    const chart = am4core.create(chartDivRef.current, am4charts.XYChart);
    chartRef.current = chart;

    // Sort data by refuelTime (oldest first for chart)
    const sortedData = [...data].sort((a, b) => {
      return moment(a.refuelTime).valueOf() - moment(b.refuelTime).valueOf();
    });

    // The standard rate reaching this chart is already normalized to the SAME display unit
    // as the actual series (KWH/L for KWH assets, KM/L for KM, L/HOURS for hours) by the
    // caller, and is the same value the Standard column of the table prints. Plot it as-is.
    // Re-deriving its orientation here from the shape of the actual series is what let the
    // chart show an L/HOURS standard against a KWH/L actual — a guess the report cannot make.
    const plottedStandardRate =
      standardRate !== null && standardRate !== undefined && !isNaN(standardRate) && Number(standardRate) > 0
        ? Number(standardRate)
        : null;

    const hasMaxOperatingLevel =
      typeof maxOperatingLevel === 'number' && isFinite(maxOperatingLevel) && maxOperatingLevel > 0;

    // Prepare chart data
    chart.data = sortedData.map((item, index) => ({
      date: moment(item.refuelTime).format('DD-MM-YY'),
      refuelTime: item.refuelTime,
      actualRate: item.actualRate !== null && item.actualRate !== undefined ? item.actualRate : null,
      standardRate: plottedStandardRate,
      // Operating Level for the same interval. Left null — and therefore not plotted — when
      // the row has no derivable Operating Level; the series does not bridge those gaps.
      operatingLevel:
        item.operatingLevel && typeof item.operatingLevel.value === 'number' && isFinite(item.operatingLevel.value)
          ? item.operatingLevel.value
          : null,
      maxOperatingLevel: hasMaxOperatingLevel ? maxOperatingLevel : null,
      fuelAdded: item.fuelAdded || 0,
      index: index + 1
    }));

    // Create category axis (X-axis) - dates
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'date';
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.labels.template.rotation = -45;
    categoryAxis.renderer.labels.template.horizontalCenter = 'right';
    categoryAxis.renderer.labels.template.verticalCenter = 'middle';
    categoryAxis.renderer.labels.template.fontSize = 11;
    categoryAxis.title.text = 'Refueling Date';
    categoryAxis.title.fontSize = 12;
    categoryAxis.title.fontWeight = 'bold';

    // Create value axis (Y-axis) - consumption rate
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = `Consumption Rate (${unit})`;
    valueAxis.title.fontSize = 12;
    valueAxis.title.fontWeight = 'bold';
    valueAxis.renderer.grid.template.strokeOpacity = 0.1;

    // Create series for actual rate (line with bullets)
    const actualSeries = chart.series.push(new am4charts.LineSeries());
    actualSeries.dataFields.categoryX = 'date';
    actualSeries.dataFields.valueY = 'actualRate';
    actualSeries.name = 'Actual Rate';
    actualSeries.stroke = am4core.color('#1890ff');
    actualSeries.strokeWidth = 3;
    actualSeries.fill = am4core.color('#1890ff');
    actualSeries.fillOpacity = 0.1;
    actualSeries.tensionX = 0.5;
    
    // Add bullets
    const bullet = actualSeries.bullets.push(new am4charts.CircleBullet());
    bullet.circle.radius = 5;
    bullet.circle.fill = am4core.color('#1890ff');
    bullet.circle.stroke = am4core.color('#ffffff');
    bullet.circle.strokeWidth = 2;
    
    actualSeries.tooltipText = `Date: {refuelTime}\nActual Rate: {valueY.formatNumber('#.00')} ${unit}\nFuel Added: {fuelAdded.formatNumber('#.00')} L`;

    // Color code bullets based on standard rate
    const isHigherBetter = unit === 'KM/L' || unit === 'KWH/L';
    bullet.circle.adapter.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (!dataItem || !plottedStandardRate) return am4core.color('#1890ff');
      
      const actualRate = dataItem.dataContext.actualRate;
      if (actualRate === null || actualRate === undefined) return am4core.color('#999999');
      
      if (isHigherBetter) {
        return actualRate >= plottedStandardRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
      } else {
        return actualRate <= plottedStandardRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
      }
    });

    // Add standard rate line if available
    if (plottedStandardRate) {
      const standardSeries = chart.series.push(new am4charts.LineSeries());
      standardSeries.dataFields.categoryX = 'date';
      standardSeries.dataFields.valueY = 'standardRate';
      standardSeries.name = `Standard Rate (${unit})`;
      standardSeries.stroke = am4core.color('#faad14');
      standardSeries.strokeWidth = 2;
      standardSeries.strokeDasharray = '5,5';
      standardSeries.data = chart.data.map(item => ({
        ...item,
        standardRate: plottedStandardRate
      }));
      standardSeries.tooltipText = `Standard Rate: ${plottedStandardRate.toFixed(2)} ${unit}`;
    }

    // Operating Level on its own right-hand axis — it is a different unit (KWH/Hour, KM/Hour)
    // from the consumption rate, so it must never share the left axis's scale. Plotted so the
    // operator can see whether a consumption swing lines up with a change in load or speed.
    const hasOperatingLevelData = chart.data.some(item => item.operatingLevel !== null);
    if (hasOperatingLevelData && operatingLevelUnit) {
      const operatingAxis = chart.yAxes.push(new am4charts.ValueAxis());
      operatingAxis.title.text = `Operating Level (${operatingLevelUnit})`;
      operatingAxis.title.fontSize = 12;
      operatingAxis.title.fontWeight = 'bold';
      operatingAxis.renderer.opposite = true;
      operatingAxis.renderer.grid.template.disabled = true;
      operatingAxis.syncWithAxis = undefined;

      const operatingSeries = chart.series.push(new am4charts.LineSeries());
      operatingSeries.dataFields.categoryX = 'date';
      operatingSeries.dataFields.valueY = 'operatingLevel';
      operatingSeries.yAxis = operatingAxis;
      operatingSeries.name = `Operating Level (${operatingLevelUnit})`;
      operatingSeries.stroke = am4core.color('#722ed1');
      operatingSeries.strokeWidth = 2;
      operatingSeries.tensionX = 0.5;
      // Do not draw across intervals that have no Operating Level — a bridged line would
      // imply a measurement that was never taken.
      operatingSeries.connect = false;
      operatingSeries.tooltipText = `Operating Level: {valueY.formatNumber('#.00')} ${operatingLevelUnit}`;

      const operatingBullet = operatingSeries.bullets.push(new am4charts.CircleBullet());
      operatingBullet.circle.radius = 4;
      operatingBullet.circle.fill = am4core.color('#722ed1');
      operatingBullet.circle.stroke = am4core.color('#ffffff');
      operatingBullet.circle.strokeWidth = 2;

      // Rated ceiling, drawn only when one has actually been entered.
      if (hasMaxOperatingLevel) {
        const maxSeries = chart.series.push(new am4charts.LineSeries());
        maxSeries.dataFields.categoryX = 'date';
        maxSeries.dataFields.valueY = 'maxOperatingLevel';
        maxSeries.yAxis = operatingAxis;
        maxSeries.name = `Max Operating Level (${maxOperatingLevel} ${operatingLevelUnit})`;
        maxSeries.stroke = am4core.color('#722ed1');
        maxSeries.strokeWidth = 1;
        maxSeries.strokeOpacity = 0.6;
        maxSeries.strokeDasharray = '3,4';
        maxSeries.tooltipText = `Max Operating Level: ${maxOperatingLevel} ${operatingLevelUnit}`;
      }
    }

    // Add legend
    chart.legend = new am4charts.Legend();
    chart.legend.position = 'top';
    chart.legend.paddingBottom = 20;
    chart.legend.labels.template.maxWidth = 95;

    // Add cursor
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.opacity = 0;

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [data, standardRate, unit, operatingLevelUnit, maxOperatingLevel]);

  if (!data || data.length === 0) {
    return (
      <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        No data available
      </div>
    );
  }

  return (
    <div 
      ref={chartDivRef} 
      style={{ width: '100%', height: '400px', minHeight: '300px', marginBottom: 16 }}
    />
  );
};

// IoT Consumption Chart Component
const IoTConsumptionChart = ({ data, standardRate, unit }) => {
  const chartRef = useRef(null);
  const chartDivRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!chartDivRef.current) return;

    // Create chart instance
    const chart = am4core.create(chartDivRef.current, am4charts.XYChart);
    chartRef.current = chart;

    // Sort data by date (oldest first for chart). Only days in the chart's unit are drawn;
    // the rest are listed in a warning below the chart instead of sharing this axis.
    const sortedData = [...splitIotDaysByUnit(data, unit).plotted].sort((a, b) => {
      return moment(a.date).valueOf() - moment(b.date).valueOf();
    });

    // standardRate arrives in the chart's unit (resolvedStandardRate: KM/L, KWH/L or
    // L/Hours) and is drawn exactly as given — the same number the Standard column prints.
    // It is never inverted to "fit" the series; a mismatch must show, not be hidden.
    const alignedStandardRate = (typeof standardRate === 'number' && isFinite(standardRate) && standardRate > 0)
      ? standardRate
      : null;

    // Prepare chart data
    chart.data = sortedData.map((item) => ({
      date: moment(item.date).format('DD-MM-YY'),
      fullDate: item.date,
      consumptionRate: item.consumptionRate !== null && item.consumptionRate !== undefined ? item.consumptionRate : null,
      standardRate: alignedStandardRate,
      fuelConsumed: item.fuelConsumed || 0,
    }));

    // Create category axis (X-axis) - dates
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'date';
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.labels.template.rotation = -45;
    categoryAxis.renderer.labels.template.horizontalCenter = 'right';
    categoryAxis.renderer.labels.template.verticalCenter = 'middle';
    categoryAxis.renderer.labels.template.fontSize = 11;
    categoryAxis.title.text = 'Date';
    categoryAxis.title.fontSize = 12;
    categoryAxis.title.fontWeight = 'bold';

    // Create value axis (Y-axis) - consumption rate
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = `Consumption Rate (${unit})`;
    valueAxis.title.fontSize = 12;
    valueAxis.title.fontWeight = 'bold';
    valueAxis.renderer.grid.template.strokeOpacity = 0.1;

    // Create series for IoT consumption rate (line with bullets)
    const iotSeries = chart.series.push(new am4charts.LineSeries());
    iotSeries.dataFields.categoryX = 'date';
    iotSeries.dataFields.valueY = 'consumptionRate';
    iotSeries.name = 'IoT Consumption Rate';
    iotSeries.stroke = am4core.color('#1890ff');
    iotSeries.strokeWidth = 3;
    iotSeries.fill = am4core.color('#1890ff');
    iotSeries.fillOpacity = 0.1;
    iotSeries.tensionX = 0.5;
    
    // Add bullets
    const bullet = iotSeries.bullets.push(new am4charts.CircleBullet());
    bullet.circle.radius = 5;
    bullet.circle.fill = am4core.color('#1890ff');
    bullet.circle.stroke = am4core.color('#ffffff');
    bullet.circle.strokeWidth = 2;
    
    iotSeries.tooltipText = `Date: {fullDate}\nIoT Consumption Rate: {valueY.formatNumber('#.00')} ${unit}\nFuel Consumed: {fuelConsumed.formatNumber('#.00')} L`;

    // Color code bullets based on standard rate
    const isHigherBetter = unit === 'KM/L' || unit === 'KWH/L';
    bullet.circle.adapter.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (!dataItem || !alignedStandardRate) return am4core.color('#1890ff');
      
      const consumptionRate = dataItem.dataContext.consumptionRate;
      if (consumptionRate === null || consumptionRate === undefined) return am4core.color('#999999');
      
      if (isHigherBetter) {
        return consumptionRate >= alignedStandardRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
      } else {
        return consumptionRate <= alignedStandardRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
      }
    });

    // Add standard rate line if available
    if (alignedStandardRate) {
      const standardSeries = chart.series.push(new am4charts.LineSeries());
      standardSeries.dataFields.categoryX = 'date';
      standardSeries.dataFields.valueY = 'standardRate';
      standardSeries.name = 'Standard Rate';
      standardSeries.stroke = am4core.color('#faad14');
      standardSeries.strokeWidth = 2;
      standardSeries.strokeDasharray = '5,5';
      standardSeries.data = chart.data.map(item => ({
        ...item,
        standardRate: alignedStandardRate
      }));
      standardSeries.tooltipText = `Standard Rate: ${alignedStandardRate.toFixed(2)} ${unit}`;
    }

    // Add legend
    chart.legend = new am4charts.Legend();
    chart.legend.position = 'top';
    chart.legend.paddingBottom = 20;
    chart.legend.labels.template.maxWidth = 95;

    // Add cursor
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.opacity = 0;

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [data, standardRate, unit]);

  const { plotted, otherUnit } = splitIotDaysByUnit(data, unit);

  if (!data || data.length === 0) {
    return (
      <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        No data available
      </div>
    );
  }

  const otherUnitWarning = otherUnit.length > 0 ? (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={`${otherUnit.length} day(s) not plotted: rate is not in ${unit}`}
      description={otherUnit
        .map(item => `${item.date}: ${item.consumptionRate !== null && item.consumptionRate !== undefined ? Number(item.consumptionRate).toFixed(2) : '-'} ${item.unit || '(no unit)'}`)
        .join(' · ')}
    />
  ) : null;

  if (plotted.length === 0) {
    return (
      <div>
        {otherUnitWarning}
        <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          No days with a rate in {unit} to plot
        </div>
      </div>
    );
  }

  return (
    <div>
      {otherUnitWarning}
      <div 
        ref={chartDivRef} 
        style={{ width: '100%', height: '400px', minHeight: '300px', marginBottom: 16 }}
      />
    </div>
  );
};

// Utility function to convert table data to CSV
const convertTableToCSV = (columns, dataSource, filename) => {
  // Extract headers from columns
  const headers = columns.map(col => col.title || col.key || '').filter(h => h);
  
  // Convert data rows
  const rows = dataSource.map((row, rowIndex) => {
    return columns.map((col, colIndex) => {
      if (!col.dataIndex && !col.key && !col.render) return '';
      
      let value = '';
      
      // If there's a render function, try to extract the text value
      if (col.render && typeof col.render === 'function') {
        try {
          const dataIndex = col.dataIndex || col.key;
          const rawValue = dataIndex ? row[dataIndex] : row;
          const rendered = col.render(rawValue, row, rowIndex);
          
          // Extract text from rendered React elements or use raw value
          if (typeof rendered === 'string' || typeof rendered === 'number') {
            value = rendered;
          } else if (rendered && rendered.props && rendered.props.children) {
            // Try to extract text from React element
            const children = rendered.props.children;
            if (typeof children === 'string' || typeof children === 'number') {
              value = children;
            } else if (Array.isArray(children)) {
              value = children.map(c => {
                if (typeof c === 'string' || typeof c === 'number') {
                  return c;
                } else if (c && c.props && c.props.children) {
                  // Handle nested React elements (like div with multiple children)
                  const nestedChildren = c.props.children;
                  if (typeof nestedChildren === 'string' || typeof nestedChildren === 'number') {
                    return nestedChildren;
                  } else if (Array.isArray(nestedChildren)) {
                    return nestedChildren.map(nc => typeof nc === 'string' || typeof nc === 'number' ? nc : '').join(' ');
                  }
                }
                return '';
              }).filter(v => v).join(' | ');
            } else {
              value = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
            }
          } else {
            // Fallback to raw value
            const dataIndex = col.dataIndex || col.key;
            value = dataIndex ? row[dataIndex] : '';
          }
        } catch (e) {
          // If render fails, use raw value
          const dataIndex = col.dataIndex || col.key;
          value = dataIndex ? (row[dataIndex] !== null && row[dataIndex] !== undefined ? row[dataIndex] : '') : '';
        }
      } else {
        // No render function, use raw value
        const dataIndex = col.dataIndex || col.key;
        value = dataIndex ? row[dataIndex] : '';
      }
      
      // Format the value
      if (value === null || value === undefined || value === '') {
        value = '';
      } else if (typeof value === 'number') {
        value = value.toFixed(2);
      } else if (moment.isMoment(value)) {
        value = value.format('YYYY-MM-DD HH:mm:ss');
      } else if (typeof value === 'object') {
        value = JSON.stringify(value);
      } else {
        value = String(value);
      }
      
      // Handle date fields
      if (col.dataIndex === 'date' && value && !moment.isMoment(value)) {
        try {
          value = moment(value).format('YYYY-MM-DD');
        } catch (e) {
          // Keep original value if parsing fails
        }
      }
      
      // Escape CSV special characters
      if (String(value).includes(',') || String(value).includes('"') || String(value).includes('\n')) {
        value = `"${String(value).replace(/"/g, '""')}"`;
      }
      
      return value;
    });
  });
  
  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename || 'table'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to extract calculated reading from mileage
// Prefers calculatedReading from meterReadingRecord (cumulative, always increasing)
// Falls back to reading (physical meter reading, may reset on meter change)
const getCalculatedReading = (mileage) => {
  if (!mileage) return null;
  // Check for calculatedReading in meterReadingRecord first
  if (mileage.meterReadingRecord && 
      mileage.meterReadingRecord.calculatedReading !== null && 
      mileage.meterReadingRecord.calculatedReading !== undefined) {
    return mileage.meterReadingRecord.calculatedReading;
  }
  // Fall back to regular reading
  return mileage.reading;
};

// Meter runs recorded on a trend row. Rows restored from a cached payload written before
// Operating Level existed have no meterRuns map — rebuild it from the per-type run fields
// that have always been stored, rather than showing readings that do exist as blank.
const getRecordMeterRuns = (record) => {
  if (record && record.meterRuns && Object.keys(record.meterRuns).length > 0) {
    return record.meterRuns;
  }
  const legacyRuns = {};
  if (record) {
    if (typeof record.kwhRun === 'number' && Number.isFinite(record.kwhRun)) legacyRuns.KWH = record.kwhRun;
    if (typeof record.kmsRun === 'number' && Number.isFinite(record.kmsRun)) legacyRuns.KMS = record.kmsRun;
    if (typeof record.hoursRun === 'number' && Number.isFinite(record.hoursRun)) legacyRuns.HOURS = record.hoursRun;
  }
  return legacyRuns;
};

// The current refuel event can have its run values filled in from the selected alert
// (mockAlert) when the fetched entry is missing them. Mirror those filled values into
// meterRuns so the extra meter readings and the Operating Level shown on that row match
// the primary run actually displayed. A key already present in meterRuns is only replaced
// by a real number — never blanked out by a missing fill value.
const syncMeterRunsFromRunFields = (item) => {
  const filledRuns = { KWH: item.kwhRun, KMS: item.kmsRun, HOURS: item.hoursRun };
  const meterRuns = { ...(item.meterRuns || {}) };
  METER_PRIORITY.forEach((type) => {
    const value = filledRuns[type];
    if (typeof value === 'number' && Number.isFinite(value)) {
      meterRuns[type] = value;
    }
  });
  item.meterRuns = meterRuns;
  item.operatingLevel = computeOperatingLevel(meterRuns);
  return item;
};

// Helper function to get both readings for display purposes
const getMeterReadings = (mileage) => {
  if (!mileage) return { reading: null, calculatedReading: null };
  const reading = mileage.reading;
  const calculatedReading = mileage.meterReadingRecord?.calculatedReading ?? null;
  return { reading, calculatedReading };
};

const FuelAlertAnalysis = () => {
  // State for data input modal (keeping for backward compatibility, but will be replaced)
  const [dataInputModalVisible, setDataInputModalVisible] = useState(false);
  const [dataInputText, setDataInputText] = useState('');
  
  // New state for asset and date range selection
  const [assetSelectionModalVisible, setAssetSelectionModalVisible] = useState(false);
  const [selectedAssetCode, setSelectedAssetCode] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loadingFuelEntries, setLoadingFuelEntries] = useState(false);
  const [fuelEntriesList, setFuelEntriesList] = useState([]);
  const [fullTankEvents, setFullTankEvents] = useState([]);
  const [selectedFullTankEvent, setSelectedFullTankEvent] = useState(null);
  const [assetList, setAssetList] = useState([]);
  const [assetCodeMap, setAssetCodeMap] = useState({});
  const [loadingAssets, setLoadingAssets] = useState(false);
  
  // Load asset list for searchable dropdown
  useEffect(() => {
    const loadAssets = async () => {
      setLoadingAssets(true);
      try {
        const result = await apiGet('/asset/asset-all', {
          headers: {
            'Accept': '*/*',
          },
        });

        if (!result.success) {
          console.error('Failed to load asset list:', result.error);
          return;
        }

        const assets = result.data || [];
        const codeMap = {};
        const assetCodes = [];

        assets.forEach(asset => {
          const code = asset.assetCode || '';
          const ownerCode = asset.ownerCode || '';
          const altName = asset.altName || '';
          const assetId = asset._id || '';
          
          if (code) {
            assetCodes.push(code);
            codeMap[code] = {
              assetCode: code,
              ownerCode: ownerCode,
              altName: altName,
              assetId: assetId,
              assetModel: asset.assetModel,
              assetSubType: asset.equipmentSubType,
            };
          }
        });

        assetCodes.sort();
        setAssetList(assetCodes);
        setAssetCodeMap(codeMap);
      } catch (error) {
        console.error('Error loading asset list:', error);
      } finally {
        setLoadingAssets(false);
      }
    };

    loadAssets();
  }, []);

  // Ref to track if we've already auto-fetched from URL parameters
  const hasAutoFetchedRef = useRef(false);
  const urlParamsRef = useRef(null);
  const previousDataReloadTriggerRef = useRef(0);

  // Read URL parameters on mount to auto-populate data from Full Tank Mileage Report
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('?')) {
      const queryString = hash.split('?')[1];
      const params = new URLSearchParams(queryString);
      
      const assetCode = params.get('assetCode');
      const fromDate = params.get('fromDate');
      const toDate = params.get('toDate');
      const voucherNo = params.get('voucherNo');
      
      if (assetCode && fromDate && toDate) {
        // Store URL parameters in ref
        urlParamsRef.current = { assetCode, fromDate, toDate, voucherNo };
        
        // Set asset code
        setSelectedAssetCode(assetCode);
        
        // Set date range
        const fromMoment = moment(fromDate, 'YYYY-MM-DD HH:mm:ss');
        const toMoment = moment(toDate, 'YYYY-MM-DD HH:mm:ss');
        if (fromMoment.isValid() && toMoment.isValid()) {
          setDateRange([fromMoment, toMoment]);
        }
      }
    }
  }, []); // Only run once on mount

  // Auto-fetch fuel entries when URL parameters are set and asset list is loaded
  useEffect(() => {
    if (!hasAutoFetchedRef.current && urlParamsRef.current && assetList.length > 0 && selectedAssetCode && dateRange[0] && dateRange[1]) {
      // Mark that we've auto-fetched to prevent multiple fetches
      hasAutoFetchedRef.current = true;
      // Trigger fetch - use a small delay to ensure state is fully set
      const timer = setTimeout(() => {
        handleFetchFuelEntries();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [assetList.length, selectedAssetCode, dateRange]);

  // Fetch fuel entries when asset and date range are selected
  const handleFetchFuelEntries = async () => {
    if (!selectedAssetCode || !dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select an asset and date range');
      return;
    }

    setLoadingFuelEntries(true);
    setFuelEntriesList([]);
    setFullTankEvents([]);
    setSelectedFullTankEvent(null);

    try {
      let assetId = null;
      const assetInfo = assetCodeMap[selectedAssetCode];
      
      if (assetInfo && assetInfo.assetId) {
        // Asset ID is already in the map
        assetId = assetInfo.assetId;
        setSelectedAssetId(assetId);
      } else {
        // Try to find asset ID
        const assetResult = await findAssetIdByCode(selectedAssetCode);
        if (!assetResult.success) {
          message.error(`Failed to find asset: ${assetResult.error}`);
          setLoadingFuelEntries(false);
          return;
        }
        assetId = assetResult.assetId;
        setSelectedAssetId(assetId);
        
        // Update the asset code map with full asset info
        const assetData = {
          assetId: assetId,
          altName: assetResult.altName,
          ownerCode: assetResult.ownerCode,
          assetModel: assetResult.assetModel,
          location: assetResult.location,
          subLocation: assetResult.subLocation
        };
        
        if (assetInfo) {
          Object.assign(assetInfo, assetData);
      } else {
          // Create entry in map if it doesn't exist
          assetCodeMap[selectedAssetCode] = assetData;
        }
        setAssetCodeMap({ ...assetCodeMap });
      }

      // Widen the request bounds by 1 minute on each side so that full-tank events
      // sitting exactly on the boundary (e.g. when navigated here from the Full Tank
      // Consumption Report with the exact event timestamps in the URL) are still
      // returned. The displayed dateRange / URL stay at the exact times.
      const periodFrom = moment(dateRange[0]).subtract(1, 'minute').toISOString();
      const periodTo = moment(dateRange[1]).add(1, 'minute').toISOString();

      console.log(`Fetching fuel entries for asset ${selectedAssetCode} from ${periodFrom} to ${periodTo}`);

      const result = await apiPost('/fuel/mileage-report', {
        asset: assetId,
        location: 'all',
        subLocation: 'all',
        periodFrom: periodFrom,
        periodTo: periodTo,
      });

      if (!result.success) {
        message.error(`Failed to fetch fuel entries: ${result.error}`);
        setLoadingFuelEntries(false);
        return;
      }

      const entries = result.data || [];
      console.log(`Fetched ${entries.length} fuel entries`);

      // Sort by date
      entries.sort((a, b) => {
        const dateA = moment(a.date || a.datetime);
        const dateB = moment(b.date || b.datetime);
        return dateA.valueOf() - dateB.valueOf();
      });

      setFuelEntriesList(entries);

      // Filter for full tank events
      const fullTanks = entries.filter(e => e.isFullTank === true);
      console.log(`Found ${fullTanks.length} full tank events`);
      setFullTankEvents(fullTanks);

      if (fullTanks.length === 0) {
        message.warning('No full tank events found in the selected period');
      } else {
        // Check if we should auto-select a full tank event based on URL parameters
        const hash = window.location.hash;
        if (hash && hash.includes('?')) {
          const queryString = hash.split('?')[1];
          const params = new URLSearchParams(queryString);
          const voucherNo = params.get('voucherNo');
          const toDate = params.get('toDate');
          
          let matchingEvent = null;
          
          if (voucherNo && voucherNo !== '') {
            // First try to find by exact voucher number match
            matchingEvent = fullTanks.find(e => e.voucherNo === voucherNo);
            
            // If not found by voucher number, try to find by date (within 5 minutes of the end date)
            if (!matchingEvent && toDate) {
              const targetDate = moment(toDate, 'YYYY-MM-DD HH:mm:ss');
              if (targetDate.isValid()) {
                matchingEvent = fullTanks.find(e => {
                  const eventDate = moment(e.date || e.datetime);
                  // Check if event is within 5 minutes of the target date
                  return Math.abs(eventDate.diff(targetDate, 'minutes')) <= 5;
                });
              }
            }
          } else if (toDate) {
            // If no voucher number but we have an end date, find the event closest to that date
            const targetDate = moment(toDate, 'YYYY-MM-DD HH:mm:ss');
            if (targetDate.isValid()) {
              // Find the event closest to the target date (within 5 minutes)
              matchingEvent = fullTanks.reduce((closest, event) => {
                const eventDate = moment(event.date || event.datetime);
                const diff = Math.abs(eventDate.diff(targetDate, 'minutes'));
                if (diff <= 5) {
                  if (!closest) return event;
                  const closestDiff = Math.abs(moment(closest.date || closest.datetime).diff(targetDate, 'minutes'));
                  return diff < closestDiff ? event : closest;
                }
                return closest;
              }, null);
            }
          }
          
          // If still no match, select the last (most recent) full tank event
          if (!matchingEvent && fullTanks.length > 0) {
            matchingEvent = fullTanks[fullTanks.length - 1];
          }
          
          if (matchingEvent) {
            // Auto-select the matching full tank event
            // Pass entries directly to avoid state timing issues
            setTimeout(() => {
              handleSelectFullTankEvent(matchingEvent, entries);
            }, 500);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching fuel entries:', error);
      message.error(`Error fetching fuel entries: ${error.message}`);
    } finally {
      setLoadingFuelEntries(false);
    }
  };

  // Handle full tank event selection
  const handleSelectFullTankEvent = async (event, entriesOverride = null) => {
    setSelectedFullTankEvent(event);
    
    // Use provided entries or fall back to state
    const entriesToSearch = entriesOverride || fuelEntriesList;
    
    // Find previous full tank event from ALL fetched entries (not just fullTankEvents array)
    // This ensures we find the previous event even if it's at the start of the date range
    const currentEventDate = moment(event.date || event.datetime);
    
    // Search through all fuel entries to find the previous full tank event
    let previousEvent = null;
    for (let i = entriesToSearch.length - 1; i >= 0; i--) {
      const entry = entriesToSearch[i];
      if (entry.isFullTank === true) {
        const entryDate = moment(entry.date || entry.datetime);
        if (entryDate.isBefore(currentEventDate)) {
          previousEvent = entry;
          break;
        }
      }
    }

    // Calculate consumption rate
    const assetInfo = assetCodeMap[selectedAssetCode];
    const currentDate = moment(event.date || event.datetime);
    const previousDate = previousEvent ? moment(previousEvent.date || previousEvent.datetime) : null;

    // Get meter readings - extract KWH, HOURS, and KMs when available
    let currentMileage = null;
    let previousMileage = null;
    let currentHoursMileage = null;
    let previousHoursMileage = null;
    let currentKwhMileage = null;
    let previousKwhMileage = null;
    let currentKmsMileage = null;
    let previousKmsMileage = null;
    let meterName = 'N/A';

    const isKmsMeter = (m) => m && m.name && (
      m.name.toUpperCase().includes('KM') ||
      m.name.toUpperCase().includes('ODO') ||
      m.name.toUpperCase().includes('DISTANCE')
    );

    if (event.mileage && event.mileage.length > 0) {
      currentMileage = event.mileage[0];
      meterName = currentMileage.name || 'N/A';

      // Find hours, KWH, and KMs meters
      currentHoursMileage = event.mileage.find(m =>
        m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
      ) || null;
      currentKwhMileage = event.mileage.find(m =>
        m.name && m.name.toUpperCase().includes('KWH')
      ) || null;
      currentKmsMileage = event.mileage.find(isKmsMeter) || null;
    }

    if (previousEvent && previousEvent.mileage && previousEvent.mileage.length > 0) {
      previousMileage = previousEvent.mileage.find(m =>
        m.name === currentMileage?.name
      ) || previousEvent.mileage[0];

      // Find hours, KWH, and KMs meters for previous event
      previousHoursMileage = previousEvent.mileage.find(m =>
        m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
      ) || null;
      previousKwhMileage = previousEvent.mileage.find(m =>
        m.name && m.name.toUpperCase().includes('KWH')
      ) || null;
      previousKmsMileage = previousEvent.mileage.find(isKmsMeter) || null;
    }

    // Calculate fuel consumed (current event + all entries between previous and current)
    let fuelConsumed = 0;
    if (previousEvent) {
      const entriesBetween = entriesToSearch.filter(e => {
        const entryDate = moment(e.date || e.datetime);
        return entryDate.isAfter(previousDate) && 
               (entryDate.isBefore(currentDate) || entryDate.isSame(currentDate));
      });
      fuelConsumed = entriesBetween.reduce((sum, e) => sum + (e.quantity || 0), 0);
    } else {
      fuelConsumed = event.quantity || 0;
    }

    // Get standard rate from asset model API FIRST - this determines which unit to use
    let standardRate = null;
    let standardUnitFromModel = null;
    
    try {
      // Get asset model ID from event
      const assetModelId = event.assetModel 
        ? (typeof event.assetModel === 'string' ? event.assetModel : event.assetModel._id)
        : null;
      
      if (assetModelId) {
        // Fetch all asset models from API (same approach as HDI Consumption Comparison)
        const modelsResult = await apiGet('/asset-model', {
          headers: {
            'Accept': '*/*',
          },
        });

        if (modelsResult.success) {
          // Handle different response structures (same as HDI Consumption Comparison)
          let modelsData = [];
          if (Array.isArray(modelsResult.data)) {
            modelsData = modelsResult.data;
          } else if (modelsResult.data && Array.isArray(modelsResult.data.data)) {
            modelsData = modelsResult.data.data;
          } else if (modelsResult.data && Array.isArray(modelsResult.data.models)) {
            modelsData = modelsResult.data.models;
          }
          
          // Find the matching asset model
          const model = modelsData.find(m => m._id === assetModelId);
          
          if (model && model.standardFuelConsumptionRate && Array.isArray(model.standardFuelConsumptionRate) && model.standardFuelConsumptionRate.length > 0) {
            // Priority for standard rate: KWH > KMS > HOURS
            // But ONLY use the rate that is actually defined in the model
            let kwhRate = null;
            let kmRate = null;
            let hoursRate = null;
            
            for (const rateObj of model.standardFuelConsumptionRate) {
              if (!rateObj.perType || !rateObj.fuelQty || isNaN(rateObj.fuelQty) || rateObj.fuelQty <= 0) continue;
              
              const perTypeName = typeof rateObj.perType === 'string' 
                ? null 
                : (rateObj.perType.name || '').toUpperCase();
              
              if (!perTypeName) continue;
              
              if (perTypeName.includes('KWH')) {
                kwhRate = rateObj;
              } else if (perTypeName.includes('KM') || perTypeName === 'KMS') {
                kmRate = rateObj;
              } else if (perTypeName.includes('HOUR') || perTypeName === 'HOURS' || perTypeName === 'HRS') {
                hoursRate = rateObj;
              }
            }
            
            // Check which meter types are actually available in the fuel entry
            const availableMeterTypes = new Set();
            if (event.mileage && event.mileage.length > 0) {
              for (const m of event.mileage) {
                const mName = (m.name || '').toUpperCase();
                if (mName.includes('KWH')) availableMeterTypes.add('KWH');
                else if (mName.includes('KM') || mName.includes('ODO') || mName.includes('DISTANCE')) availableMeterTypes.add('KMS');
                else if (mName.includes('HOUR') || mName.includes('HRS')) availableMeterTypes.add('HOURS');
              }
            }

            // Select rate: priority KWH > KMS > HOURS, but only if matching meter data exists
            let matchingRate = null;
            if (kwhRate && availableMeterTypes.has('KWH')) matchingRate = kwhRate;
            else if (kmRate && availableMeterTypes.has('KMS')) matchingRate = kmRate;
            else if (hoursRate && availableMeterTypes.has('HOURS')) matchingRate = hoursRate;
            if (!matchingRate) matchingRate = kwhRate || kmRate || hoursRate || model.standardFuelConsumptionRate[0];
            
            if (matchingRate && matchingRate.fuelQty !== null && matchingRate.fuelQty !== undefined && !isNaN(matchingRate.fuelQty) && matchingRate.fuelQty > 0) {
              standardRate = matchingRate.fuelQty;
              
              // Determine standard unit from perType
              if (matchingRate.perType) {
                const perTypeName = typeof matchingRate.perType === 'string' 
                  ? null 
                  : (matchingRate.perType.name || '').toUpperCase();
                
                if (perTypeName) {
                  if (perTypeName.includes('KWH')) {
                    standardUnitFromModel = 'L_PER_KWH';
                  } else if (perTypeName.includes('KM') || perTypeName === 'KMS') {
                    standardUnitFromModel = 'L_PER_KM';
                  } else if (perTypeName.includes('HOUR') || perTypeName === 'HOURS' || perTypeName === 'HRS') {
                    standardUnitFromModel = 'L_PER_HOUR';
                  }
                }
              }
              
              console.log('Selected standard rate from model:', {
                rate: standardRate,
                unit: standardUnitFromModel,
                perTypeName: matchingRate.perType?.name,
                availableMeters: [...availableMeterTypes],
                availableRates: {
                  kwh: kwhRate ? kwhRate.fuelQty : null,
                  km: kmRate ? kmRate.fuelQty : null,
                  hours: hoursRate ? hoursRate.fuelQty : null
                }
              });
            }
            
            // Extract variance threshold from asset model (fuelMileageThreshold)
            if (model && model.fuelMileageThreshold && Array.isArray(model.fuelMileageThreshold) && model.fuelMileageThreshold.length > 0) {
              const firstThreshold = model.fuelMileageThreshold[0];
              if (firstThreshold && firstThreshold.value !== null && firstThreshold.value !== undefined && !isNaN(firstThreshold.value) && firstThreshold.value > 0) {
                // Store variance threshold - can be used for alert determination
                setVarianceThreshold(firstThreshold.value);
                console.log(`Extracted variance threshold from asset model: ${firstThreshold.value}`);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching asset model for standard rate:', error);
      // Continue without standard rate if API call fails
    }
    
    // Determine the unit to use: Standard unit from model takes priority
    // If no standard defined, fall back to meter type from the fuel entry
    let standardUnit = standardUnitFromModel || 'L_PER_HOUR'; // Default to hours if nothing defined
    
    // Calculate run value and consumption rate BASED ON THE STANDARD UNIT (not meter availability)
    let kmsRun = null;
    let hoursRun = null;
    let kwhRun = null;
    let consumptionRate = null;
    
    // Find appropriate meters based on standard unit
    let targetCurrentMileage = null;
    let targetPreviousMileage = null;
    
    if (event.mileage && event.mileage.length > 0 && previousEvent?.mileage && previousEvent.mileage.length > 0) {
      if (standardUnit === 'L_PER_KWH') {
        // Use KWH meter
        targetCurrentMileage = event.mileage.find(m => m.name && m.name.toUpperCase().includes('KWH'));
        targetPreviousMileage = previousEvent.mileage.find(m => m.name && m.name.toUpperCase().includes('KWH'));
      } else if (standardUnit === 'L_PER_KM') {
        // Use KM meter
        targetCurrentMileage = event.mileage.find(m => 
          m.name && (m.name.toUpperCase().includes('KM') || m.name.toUpperCase().includes('ODO') || m.name.toUpperCase().includes('DISTANCE'))
        );
        targetPreviousMileage = previousEvent.mileage.find(m => 
          m.name && (m.name.toUpperCase().includes('KM') || m.name.toUpperCase().includes('ODO') || m.name.toUpperCase().includes('DISTANCE'))
        );
      } else if (standardUnit === 'L_PER_HOUR') {
        // Use HOURS meter
        targetCurrentMileage = event.mileage.find(m => 
          m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
        );
        targetPreviousMileage = previousEvent.mileage.find(m => 
          m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
        );
      }
      
      // If target meter not found, do NOT fall back to a different meter type
      // — calculating with a mismatched meter produces nonsensical rates
    } else {
      targetCurrentMileage = currentMileage;
      targetPreviousMileage = previousMileage;
    }
    
    // Calculate run value and consumption rate based on standard unit
    // Use calculatedReading (cumulative) for calculations - handles meter resets
    if (targetCurrentMileage && targetPreviousMileage) {
      const currentReading = getCalculatedReading(targetCurrentMileage);
      const previousReading = getCalculatedReading(targetPreviousMileage);
      
      if (currentReading !== null && previousReading !== null && currentReading > previousReading) {
        const runValue = currentReading - previousReading;
        
        if (standardUnit === 'L_PER_KM') {
          kmsRun = runValue;
          consumptionRate = fuelConsumed > 0 ? kmsRun / fuelConsumed : null; // KM/L
        } else if (standardUnit === 'L_PER_HOUR') {
          hoursRun = runValue;
          consumptionRate = fuelConsumed > 0 ? fuelConsumed / hoursRun : null; // L/Hours
        } else if (standardUnit === 'L_PER_KWH') {
          kwhRun = runValue;
          consumptionRate = fuelConsumed > 0 ? kwhRun / fuelConsumed : null; // KWH/L
        }
      }
    }
    
    // Also extract hours/kms/kwh runs for comparison display when secondary meters exist.
    // Use calculatedReading for accuracy. These are populated even when not the primary unit
    // so the Fuel Entry Details can show every meter present in the entry.
    const currentHoursCalcReading = getCalculatedReading(currentHoursMileage);
    const previousHoursCalcReading = getCalculatedReading(previousHoursMileage);
    if (currentHoursCalcReading !== null && previousHoursCalcReading !== null) {
      const hoursRunValue = currentHoursCalcReading - previousHoursCalcReading;
      if (hoursRunValue > 0 && hoursRun === null) {
        hoursRun = hoursRunValue;
      }
    }

    const currentKmsCalcReading = getCalculatedReading(currentKmsMileage);
    const previousKmsCalcReading = getCalculatedReading(previousKmsMileage);
    if (currentKmsCalcReading !== null && previousKmsCalcReading !== null) {
      const kmsRunValue = currentKmsCalcReading - previousKmsCalcReading;
      if (kmsRunValue > 0 && kmsRun === null) {
        kmsRun = kmsRunValue;
      }
    }

    const currentKwhCalcReading = getCalculatedReading(currentKwhMileage);
    const previousKwhCalcReading = getCalculatedReading(previousKwhMileage);
    if (currentKwhCalcReading !== null && previousKwhCalcReading !== null) {
      const kwhRunValue = currentKwhCalcReading - previousKwhCalcReading;
      if (kwhRunValue > 0 && kwhRun === null) {
        kwhRun = kwhRunValue;
      }
    }

    // Convert standard rate for KWH and KM: API returns L/KWH or L/KM, but we display KWH/L or KM/L
    // So convert: if standardRate is 0.1 L/KWH, that means 10 KWH/L
    // If standardRate is 0.34 L/KM, that means 2.94 KM/L
    if (standardRate && standardRate > 0) {
      if (standardUnit === 'L_PER_KWH') {
        standardRate = 1 / standardRate; // Convert L/KWH to KWH/L
      } else if (standardUnit === 'L_PER_KM') {
        standardRate = 1 / standardRate; // Convert L/KM to KM/L
      }
      // For L_PER_HOUR, standardRate is already in L/Hours format, no conversion needed
    }

    // Calculate deviation
    let deviation = null;
    if (standardRate && consumptionRate) {
      // For KWH/L and KM/L, higher is better, so deviation should be inverted
      // (negative deviation = actual > standard = good performance)
      if (standardUnit === 'L_PER_KWH' || standardUnit === 'L_PER_KM') {
        deviation = ((standardRate - consumptionRate) / standardRate) * 100;
      } else {
        // For L/HOUR: lower is better (positive deviation = actual > standard = bad performance)
      deviation = ((consumptionRate - standardRate) / standardRate) * 100;
      }
    }

    // Build mockAlert data
    // hasKms, hasHours, hasKwh are now based on STANDARD UNIT, not meter availability
    const currentAssetInfo = assetInfo || assetCodeMap[selectedAssetCode] || {};
    
    // Helper function to extract location/subLocation name (can be object or string)
    const extractLocationName = (field) => {
      if (!field) return null;
      if (typeof field === 'string') {
        // Check if it looks like a MongoDB ObjectId
        const isObjectId = /^[0-9a-fA-F]{24}$/i.test(field.trim());
        if (isObjectId) return null; // It's an ID, not a name
        return field; // It's likely a name string
      }
      if (typeof field === 'object') {
        return field.name || field.code || field.title || field.label || null;
      }
      return null;
    };
    
    // Extract location and subLocation from event or currentAssetInfo
    const location = extractLocationName(event.location) || currentAssetInfo.location || null;
    const subLocation = extractLocationName(event.subLocation) || currentAssetInfo.subLocation || null;
    
    const newMockAlert = {
      ...mockAlert,
      assetCode: selectedAssetCode,
      altName: currentAssetInfo.altName !== undefined ? currentAssetInfo.altName : (mockAlert.altName || ''),
      ownerCode: currentAssetInfo.ownerCode !== undefined ? currentAssetInfo.ownerCode : (mockAlert.ownerCode || ''),
      assetModel: event.assetModel?.name || currentAssetInfo?.assetModel?.name || '',
      assetSubType: event.assetSubType?.name || '',
      location: location,
      subLocation: subLocation,
      voucherNo: event.voucherNo || '',
      refuelTime: currentDate.format('YYYY-MM-DD HH:mm:ss'),
      alertTime: currentDate.format('YYYY-MM-DD HH:mm:ss'),
      fuelAdded: event.quantity || 0, // Fuel added at current full tank event only (for IoT matching)
      totalFuelConsumed: fuelConsumed, // Total fuel consumed including intermediate non-full-tank entries (for rate calculation)
      currentOdo: targetCurrentMileage?.reading || currentMileage?.reading || null,
      lastFullTankOdo: targetPreviousMileage?.reading || previousMileage?.reading || null,
      // Also store hours, KWH, and KMs readings separately.
      // Prefer .reading (physical meter reading shown in the entry); fall back to calculatedReading
      // (cumulative across resets) so the display stays populated when only the cumulative value exists.
      currentHoursOdo: currentHoursMileage?.reading ?? getCalculatedReading(currentHoursMileage),
      lastFullTankHoursOdo: previousHoursMileage?.reading ?? getCalculatedReading(previousHoursMileage),
      currentKwhOdo: currentKwhMileage?.reading ?? getCalculatedReading(currentKwhMileage),
      lastFullTankKwhOdo: previousKwhMileage?.reading ?? getCalculatedReading(previousKwhMileage),
      currentKmsOdo: currentKmsMileage?.reading ?? getCalculatedReading(currentKmsMileage),
      lastFullTankKmsOdo: previousKmsMileage?.reading ?? getCalculatedReading(previousKmsMileage),
      lastFullTankTime: previousDate ? previousDate.format('YYYY-MM-DD HH:mm:ss') : null,
      kmsRun: kmsRun,
      hoursRun: hoursRun,
      kwhRun: kwhRun,
      actualRate: consumptionRate,
      standardRate: standardRate, // This is the converted rate (KWH/L for KWH assets)
      deviation: deviation,
      // IMPORTANT: hasKms, hasHours, hasKwh are now based on STANDARD UNIT, not meter availability
      hasKms: standardUnit === 'L_PER_KM',
      hasHours: standardUnit === 'L_PER_HOUR',
      hasKwh: standardUnit === 'L_PER_KWH',
      standardUnit: standardUnit,
    };
    
    // Log to verify standard rate is correct
    console.log('Setting mockAlert with standardRate:', {
      standardRate,
      standardUnit,
      standardUnitFromModel,
      hasKwh: standardUnit === 'L_PER_KWH',
      hasHours: standardUnit === 'L_PER_HOUR',
      hasKms: standardUnit === 'L_PER_KM',
      consumptionRate,
      deviation,
      kmsRun,
      hoursRun,
      kwhRun
    });

    setMockAlert(newMockAlert);
    setAssetSelectionModalVisible(false);
    setFetchedIotData(null);
    setDataLoaded(false);
    
    // Trigger reload of fuel entries by incrementing the reload trigger
    // This ensures fuel entries are refetched when new base data is selected
    setDataReloadTrigger(prev => prev + 1);
    
    message.success('Fuel alert data loaded successfully');
  };

  // Function to parse the input text and extract fuel alert data (keeping for backward compatibility)
  const parseFuelAlertData = (text) => {
    const data = {};
    
    try {
      // Parse Asset Code/Alt Name/Owner Code
      const assetMatch = text.match(/Asset Code\/Alt Name\/Owner Code:\s*([^/]+)\s*\/\s*([^/]+)\s*\/\s*([^\n]+)/);
      if (assetMatch) {
        data.assetCode = assetMatch[1].trim();
        data.altName = assetMatch[2].trim();
        data.ownerCode = assetMatch[3].trim();
      }
      
      // Parse Asset Model
      const modelMatch = text.match(/Asset Model:\s*([^\n]+)/);
      if (modelMatch) {
        data.assetModel = modelMatch[1].trim();
      }
      
      // Parse Asset Sub-Type
      const subTypeMatch = text.match(/Asset Sub-Type:\s*([^\n]+)/);
      if (subTypeMatch) {
        data.assetSubType = subTypeMatch[1].trim();
      }
      
      // Parse Voucher No
      const voucherMatch = text.match(/Voucher No:\s*([^\n]+)/);
      if (voucherMatch) {
        data.voucherNo = voucherMatch[1].trim();
      }
      
      // Parse Refueling Date and Time (input is in GMT, convert to IST for display)
      const refuelTimeMatch = text.match(/Refueling Date and Time:\s*(\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2})\s*GMT/);
      if (refuelTimeMatch) {
        // Convert DD-MM-YYYY to YYYY-MM-DD
        const dateTimeStr = refuelTimeMatch[1].trim();
        const [datePart, timePart] = dateTimeStr.split(' ');
        const [day, month, year] = datePart.split('-');
        // Parse as GMT/UTC and convert to IST (GMT+5:30)
        const gmtMoment = moment.utc(`${year}-${month}-${day} ${timePart}`, 'YYYY-MM-DD HH:mm:ss');
        const istMoment = gmtMoment.utcOffset('+05:30');
        data.refuelTime = istMoment.format('YYYY-MM-DD HH:mm:ss');
        data.alertTime = istMoment.format('YYYY-MM-DD HH:mm:ss');
      }
      
      // Parse Location
      const locationMatch = text.match(/Location of Refueling:\s*([\d.]+),([\d.]+)/);
      if (locationMatch) {
        data.locationLat = parseFloat(locationMatch[1].trim());
        data.locationLng = parseFloat(locationMatch[2].trim());
      }
      
      // Parse Fuel Added
      const fuelMatch = text.match(/Fuel Added:\s*(\d+(?:\.\d+)?)\s*litre/i);
      if (fuelMatch) {
        data.fuelAdded = parseFloat(fuelMatch[1].trim());
        data.totalFuelConsumed = parseFloat(fuelMatch[1].trim());
      }
      
      // Parse Refueled Odometer Reading
      const currentOdoMatch = text.match(/Refueled Odometer Reading:\s*(\d+(?:\.\d+)?)\s*KMS/i);
      if (currentOdoMatch) {
        data.currentOdo = parseFloat(currentOdoMatch[1].trim());
      }
      
      // Parse Last Full Tank Odometer Reading
      const lastOdoMatch = text.match(/Last Full Tank Odometer Reading:\s*(\d+(?:\.\d+)?)\s*KMS/i);
      if (lastOdoMatch) {
        data.lastFullTankOdo = parseFloat(lastOdoMatch[1].trim());
      }
      
      // Parse Last Full Tank Date (input is in GMT, convert to IST for display)
      const lastDateMatch = text.match(/Last Full Tank Date:\s*(\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2})\s*GMT/);
      if (lastDateMatch) {
        const dateTimeStr = lastDateMatch[1].trim();
        const [datePart, timePart] = dateTimeStr.split(' ');
        const [day, month, year] = datePart.split('-');
        // Parse as GMT/UTC and convert to IST (GMT+5:30)
        const gmtMoment = moment.utc(`${year}-${month}-${day} ${timePart}`, 'YYYY-MM-DD HH:mm:ss');
        const istMoment = gmtMoment.utcOffset('+05:30');
        data.lastFullTankTime = istMoment.format('YYYY-MM-DD HH:mm:ss');
      }
      
      // Parse KMS-run
      const kmsRunValueMatch = text.match(/KMS[\s-]?run:\s*(\d+(?:\.\d+)?)\s*KMS/i);
      if (kmsRunValueMatch) {
        data.kmsRun = parseFloat(kmsRunValueMatch[1].trim());
      }
      
      // Parse Hours-run (if available)
      const hoursRunValueMatch = text.match(/Hours[\s-]?run:\s*(\d+(?:\.\d+)?)\s*HOURS?/i);
      if (hoursRunValueMatch) {
        data.hoursRun = parseFloat(hoursRunValueMatch[1].trim());
      }
      
      // Parse Consumption Rate - check for both Qty/KMS and Qty/HOURS formats
      const actualRateMatch = text.match(/Consumption Rate Calculation:\s*(\d+(?:\.\d+)?)\s*(?:Qty\/)?(KMS|HOURS?|KM\/L|L\/KM|L\/H|H\/L)/i);
      if (actualRateMatch) {
        data.actualRate = parseFloat(actualRateMatch[1].trim());
      }
      
      // Parse Standard Consumption Rate - check format to determine unit
      let standardRateFormat = null;
      const standardRateMatch = text.match(/Standard Consumption Rate:\s*(\d+(?:\.\d+)?)\s*(?:Qty\/)?(KMS|HOURS?|KM\/L|L\/KM|L\/H|H\/L)/i);
      if (standardRateMatch) {
        data.standardRate = parseFloat(standardRateMatch[1].trim());
        standardRateFormat = standardRateMatch[2].trim().toUpperCase();
      }
      
      // Parse Deviation
      const deviationMatch = text.match(/Deviation from Standard:\s*([\d.]+)%/i);
      if (deviationMatch) {
        data.deviation = parseFloat(deviationMatch[1].trim());
      }
      
      // Determine asset type based on standard consumption rate format
      // Check if it's hours-based (HOURS, HOUR, L/H, H/L) or km-based (KMS, KM, KM/L, L/KM)
      const isHoursBased = standardRateFormat && (
        standardRateFormat.includes('HOUR') || 
        standardRateFormat === 'L/H' ||
        standardRateFormat === 'H/L' ||
        standardRateFormat === 'LTR/HOUR' ||
        standardRateFormat === 'L/HOUR'
      );
      
      const isKmBased = standardRateFormat && (
        standardRateFormat.includes('KM') || 
        standardRateFormat === 'KM/L' ||
        standardRateFormat === 'L/KM'
      );
      
      // Also check if there's hours run data
      const hasHoursRunData = hoursRunValueMatch !== null || data.hoursRun !== null;
      
      // Also check if there's KMS run data
      const hasKmsRunData = kmsRunValueMatch !== null || data.kmsRun !== null || currentOdoMatch !== null || lastOdoMatch !== null;
      
      // Determine asset type: prioritize standard rate format, then check available data
      if (isHoursBased) {
        data.hasHours = true;
        data.hasKms = false;
        data.hasKwh = false;
        data.standardUnit = 'L_PER_HOUR'; // L/Hours format
        // Keep hoursRun if parsed, otherwise null
        if (!data.hoursRun) data.hoursRun = null;
        data.kmsRun = null;
        data.kwhRun = null;
      } else if (isKmBased || hasKmsRunData) {
        data.hasKms = true;
        data.hasHours = false;
        data.hasKwh = false;
        data.standardUnit = 'L_PER_KM'; // Qty/KMS means L/KM
        data.hoursRun = null;
        // Keep kmsRun if parsed, otherwise null
        if (!data.kmsRun) data.kmsRun = null;
        data.kwhRun = null;
      } else if (hasHoursRunData) {
        // Fallback: if hours run is mentioned but no standard rate format, assume hours-based
        data.hasHours = true;
        data.hasKms = false;
        data.hasKwh = false;
        data.standardUnit = 'L_PER_HOUR';
        // Keep hoursRun if parsed, otherwise null
        if (!data.hoursRun) data.hoursRun = null;
        data.kmsRun = null;
        data.kwhRun = null;
      } else {
        // Default to KM-based if no clear indicator
        data.hasKms = true;
        data.hasHours = false;
        data.hasKwh = false;
        data.standardUnit = 'L_PER_KM';
        data.hoursRun = null;
        // Keep kmsRun if parsed, otherwise null
        if (!data.kmsRun) data.kmsRun = null;
        data.kwhRun = null;
      }
      
      return data;
    } catch (error) {
      console.error('Error parsing fuel alert data:', error);
      return null;
    }
  };
  
  // State for mock alert data - default values
  const defaultMockAlert = {
    id: null,
    assetCode: null,
    altName: null,
    ownerCode: null,
    assetModel: null,
    assetSubType: null,
    voucherNo: null,
    refuelTime: null,
    locationLat: null,
    locationLng: null,
    fuelAdded: null,
    currentOdo: null,
    lastFullTankOdo: null,
    currentHoursOdo: null,
    lastFullTankHoursOdo: null,
    currentKwhOdo: null,
    lastFullTankKwhOdo: null,
    lastFullTankTime: null,
    hoursRun: null,
    kmsRun: null,
    kwhRun: null,
    actualRate: null,
    standardRate: null,
    deviation: null,
    alertTime: null,
    hasHours: false,
    hasKms: false,
    hasKwh: false,
    standardUnit: null,
    totalFuelConsumed: null,
  };
  
  // Load mockAlert from localStorage on mount, or use default
  const [mockAlert, setMockAlert] = useState(() => {
    try {
      const savedData = localStorage.getItem('fuelAlertMockData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        return { ...defaultMockAlert, ...parsed };
      }
    } catch (error) {
      console.error('Error loading saved fuel alert data:', error);
    }
    return defaultMockAlert;
  });
  
  // State to trigger API refetch when data changes
  const [dataReloadTrigger, setDataReloadTrigger] = useState(0);
  
  // Handle data input submission
  const handleDataInputSubmit = () => {
    if (!dataInputText.trim()) {
      Modal.error({
        title: 'Validation Error',
        content: 'Please enter the fuel alert data.',
      });
      return;
    }
    
    const parsedData = parseFuelAlertData(dataInputText);
    
    if (!parsedData) {
      Modal.error({
        title: 'Parsing Error',
        content: 'Failed to parse the data. Please check the format and try again.',
      });
      return;
    }
    
    // Merge parsed data with default mock alert
    const updatedAlert = {
      ...defaultMockAlert,
      ...parsedData,
      id: parsedData.assetCode ? `ALERT-${parsedData.assetCode}` : defaultMockAlert.id,
    };
    
    // Save to localStorage
    try {
      localStorage.setItem('fuelAlertMockData', JSON.stringify(updatedAlert));
    } catch (error) {
      console.error('Error saving fuel alert data to localStorage:', error);
      Modal.warning({
        title: 'Save Warning',
        content: 'Data loaded but could not be saved to localStorage. It will not persist on reload.',
      });
    }
    
    setMockAlert(updatedAlert);
    setDataInputModalVisible(false);
    setDataInputText('');
    setFetchedIotData(null);
    setDataLoaded(false);
    
    // Trigger API refetch by incrementing the reload trigger
    setDataReloadTrigger(prev => prev + 1);
    
    Modal.success({
      title: 'Data Loaded',
      content: (
        <div>
          <p>Fuel alert data loaded for asset <strong>{parsedData.assetCode || 'N/A'}{parsedData.altName ? ` / ${parsedData.altName}` : ''}{parsedData.ownerCode ? ` / ${parsedData.ownerCode}` : ''}</strong>.</p>
          <p>All API data (trend data, fuel entries, IoT refuel events) is being refetched automatically.</p>
          <p style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Note: IoT Timeline data can be fetched manually using the "Fetch IoT Data" button in the IoT Timeline tab.
          </p>
          <p style={{ marginTop: 8, fontSize: 12, color: '#1890ff' }}>
            Page will reload in 2 seconds to refresh all data...
          </p>
        </div>
      ),
      duration: 2,
    });
    
    // Auto-reload the page after a short delay to refresh all data
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  // No mock data - use empty array
  const mockFuelEntries = [];

  const [analysisRange, setAnalysisRange] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState('7');
  // Maximum Operating Level for the asset under analysis — the rated ceiling the measured
  // Operating Level is compared against (a DG's rated KWH/Hour, a vehicle's maximum KM/Hour).
  // Entered by hand for now; this is intended to move onto the asset model definition, at
  // which point this input becomes a display of the model value.
  // Deliberately starts EMPTY: with no maximum stated, no percentage is shown at all rather
  // than a percentage of an assumed ceiling.
  const [maxOperatingLevel, setMaxOperatingLevel] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState(['fuel', 'speed']);
  // Analysis data state
  const [analysis, setAnalysis] = useState('');
  const [suggestedAction, setSuggestedAction] = useState('');
  const [finalAction, setFinalAction] = useState('');
  const [alertStatus, setAlertStatus] = useState(null); // 'confirmed', 'discarded', or null
  const [currentUser] = useState('Current User'); // Replace with actual user from auth context
  
  // Helper function to format values - show "-" if null/undefined/empty
  const formatValue = (value, formatter = null) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (formatter && typeof formatter === 'function') {
      return formatter(value);
    }
    return value;
  };

  const formatNumberSafe = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num) || !isFinite(num)) return '-';
    return num.toFixed(decimals);
  };

  // Format delay in seconds as "X days, X hours, X minutes, X seconds" - omit zero units
  const formatDelay = (seconds) => {
    if (seconds < 0 || !Number.isFinite(seconds)) return '0s';
    const s = Math.floor(seconds);
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    return parts.join(' ');
  };
  
  // Generate unique alert ID
  const generateAlertId = () => {
    // Format: ASSETCODE-FCA-DATE
    // Example: 20E-290-FCA-20251126
    if (!mockAlert.alertTime || !mockAlert.assetCode) {
      return '-';
    }
    const dateStr = moment(mockAlert.alertTime).format('YYYYMMDD');
    return `${mockAlert.assetCode}-FCA-${dateStr}`;
  };
  
  const [alertId] = useState(generateAlertId());
  const [selectedTrendRow, setSelectedTrendRow] = useState(null);
  const [selectedDayForCharts, setSelectedDayForCharts] = useState(null);
  const [iotChartsModalVisible, setIotChartsModalVisible] = useState(false);
  const [startEventsModalVisible, setStartEventsModalVisible] = useState(false);
  const [endEventsModalVisible, setEndEventsModalVisible] = useState(false);
  const [selectedStartEventId, setSelectedStartEventId] = useState(null);
  const [selectedEndEventId, setSelectedEndEventId] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [refuelDetailsExpanded, setRefuelDetailsExpanded] = useState(false);
  const [photoViewerVisible, setPhotoViewerVisible] = useState(false);
  const [selectedVoucherPhotos, setSelectedVoucherPhotos] = useState([]);
  const [failedPhotoUrls, setFailedPhotoUrls] = useState(new Set());
  const [selectedVoucherNo, setSelectedVoucherNo] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrendData, setIsLoadingTrendData] = useState(false);
  const [iotConsumptionPeriod, setIotConsumptionPeriod] = useState('7');
  const [iotConsumptionData, setIotConsumptionData] = useState([]);
  const [isLoadingIotConsumption, setIsLoadingIotConsumption] = useState(false);
  const [trendDataError, setTrendDataError] = useState(null);
  const [standardRate, setStandardRate] = useState(null); // Will be updated from API or mockAlert
  const [varianceThreshold, setVarianceThreshold] = useState(null); // Variance threshold from asset model
  const [fuelEntries, setFuelEntries] = useState([]);
  const [isLoadingFuelEntries, setIsLoadingFuelEntries] = useState(false);
  const [fuelEntriesError, setFuelEntriesError] = useState(null);
  const [iotRefuelEvents, setIotRefuelEvents] = useState(() => {
    // Try to load from localStorage on mount
    try {
      const savedData = localStorage.getItem('fuelAlertApiData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.iotRefuelEvents && Array.isArray(parsed.iotRefuelEvents)) {
          // Convert timeMoment strings back to moment objects
          return parsed.iotRefuelEvents.map(event => ({
            ...event,
            timeMoment: event.timeMoment ? moment(event.timeMoment) : moment(event.timeText || event.time),
          }));
        }
      }
    } catch (error) {
      console.error('Error loading iotRefuelEvents from localStorage:', error);
    }
    return [];
  });
  const [isLoadingIotEvents, setIsLoadingIotEvents] = useState(false);
  const [iotEventsError, setIotEventsError] = useState(null);
  // Track metadata for loaded IoT refuel events to avoid unnecessary API calls
  const [iotEventsMetadata, setIotEventsMetadata] = useState(() => {
    // Try to load from localStorage on mount to match with cached iotRefuelEvents
    try {
      const savedData = localStorage.getItem('fuelAlertApiData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.iotEventsMetadata) {
          console.log('Loaded iotEventsMetadata from localStorage:', parsed.iotEventsMetadata);
          return parsed.iotEventsMetadata;
        }
      }
    } catch (error) {
      console.error('Error loading iotEventsMetadata from localStorage:', error);
    }
    return {
      assetCode: null,
      refuelTime: null,
      lastFullTankTime: null,
    };
  });
  const [fetchedIotData, setFetchedIotData] = useState(null);
  const iotTimelineContextRef = useRef(null);
  
  // Alert History Tab state
  const [alertHistory, setAlertHistory] = useState([]);
  const [isLoadingAlertHistory, setIsLoadingAlertHistory] = useState(false);
  const [alertHistoryError, setAlertHistoryError] = useState(null);
  const [alertHistoryLimit, setAlertHistoryLimit] = useState('7'); // Display cap within the loaded window
  const [selectedHistoryAlert, setSelectedHistoryAlert] = useState(null);
  const [alertHistoryDrawerVisible, setAlertHistoryDrawerVisible] = useState(false);
  // Query scope for the alert history (see buildAlertHistoryWindowMs).
  const [alertHistoryLookback, setAlertHistoryLookback] = useState(DEFAULT_ALERT_HISTORY_LOOKBACK);
  // Custom look-back range ([fromMoment, toMoment]); only used when the
  // look-back selector is set to ALERT_HISTORY_CUSTOM_LOOKBACK.
  const [alertHistoryCustomRange, setAlertHistoryCustomRange] = useState(null);
  // Window the currently displayed alert history was actually fetched for.
  // Null until a successful load - an empty list is never presented without it.
  const [alertHistoryWindow, setAlertHistoryWindow] = useState(null);
  // Data-quality counters for the loaded set - surfaced, never silently dropped.
  const [alertHistoryIssues, setAlertHistoryIssues] = useState({ outOfWindow: 0, missingCreatedAt: 0, duplicateAcrossSets: 0 });
  // Lifecycle filter over the loaded set: 'ALL' | 'OPEN' | 'CLOSED'. A display
  // filter only - both endpoints are always queried, so switching it costs no
  // request and never changes what "no alerts" means.
  const [alertHistoryLifecycle, setAlertHistoryLifecycle] = useState('ALL');
  // Guards against duplicate fetches when unrelated state changes re-run the effect.
  const alertHistoryFetchKeyRef = useRef(null);
  
  // Temporarily excluded full tank events for analysis (mark as non-full-tank)
  // Keys are refuelTime strings, values are boolean (true = excluded)
  const [excludedFullTankEvents, setExcludedFullTankEvents] = useState({});
  
  // IoT Device Status state
  const [iotDeviceStatus, setIotDeviceStatus] = useState({
    hasLiveSensor: false,
    tagDeviceId: null,
    lastDataTimestamp: null, // tts field - when device recorded data
    lastDataReceiptTime: null, // incoming_time field - when server received data
    isLoading: false,
    error: null,
    fuelSensorConnected: false,
  });

  // AI Analysis state
  const [aiAnalysisModalVisible, setAiAnalysisModalVisible] = useState(false);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [aiAnalysisError, setAiAnalysisError] = useState(null);
  const [aiAnalysisFromCache, setAiAnalysisFromCache] = useState(false);
  const [aiAnalysisGeneratedAt, setAiAnalysisGeneratedAt] = useState(null); // ISO string when result was generated
  // API key asked every time (not stored) - only the person with the key can use AI analysis
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [apiKeyModalInput, setApiKeyModalInput] = useState('');
  const [pendingAiAction, setPendingAiAction] = useState(null); // 'analyze' | 'regenerate' when key modal is open

  // Cache key for current full-tank alert (assetCode + refuelTime uniquely identify the alert)
  // Normalize refuelTime to canonical format so ISO vs "YYYY-MM-DD HH:mm:ss" always match
  const getAiAnalysisCacheKey = () => {
    if (!mockAlert.assetCode || !mockAlert.refuelTime) return null;
    const parsed = moment(mockAlert.refuelTime);
    if (!parsed.isValid()) return null;
    const refuelNorm = parsed.format('YYYY-MM-DD HH:mm:ss');
    return `fuelAlertAiAnalysis_${mockAlert.assetCode}_${refuelNorm}`;
  };

  const saveAiAnalysisToCache = (result, generatedAt) => {
    const key = getAiAnalysisCacheKey();
    console.log('[AI Cache] Saving - key:', key);
    if (!key) {
      console.warn('[AI Cache] No key, cannot save');
      return;
    }
    try {
      const dataToSave = {
        result,
        generatedAt: generatedAt || new Date().toISOString(),
      };
      localStorage.setItem(key, JSON.stringify(dataToSave));
      console.log('[AI Cache] Saved successfully, generatedAt:', dataToSave.generatedAt);
    } catch (e) {
      console.warn('[AI Cache] Could not save:', e);
    }
  };

  const loadAiAnalysisFromCache = () => {
    const key = getAiAnalysisCacheKey();
    console.log('[AI Cache] Loading - key:', key);
    if (!key) {
      console.log('[AI Cache] No key generated, skipping cache');
      return null;
    }
    try {
      const raw = localStorage.getItem(key);
      console.log('[AI Cache] Raw from localStorage:', raw ? 'found' : 'not found');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.result === 'string') {
        console.log('[AI Cache] Valid cache found, generatedAt:', parsed.generatedAt);
        return parsed;
      }
      console.log('[AI Cache] Parsed but invalid structure');
      return null;
    } catch (e) {
      console.error('[AI Cache] Error loading:', e);
      return null;
    }
  };

  // Function to call OpenAI API for fuel alert analysis (forceRegenerate = true skips cache, apiKey required each time)
  const performAIAnalysis = async (forceRegenerate = false, apiKey = null) => {
    const keyToUse = (apiKey && apiKey.trim()) || null;
    if (!keyToUse) {
      setAiAnalysisError('OPENAI_KEY_REQUIRED');
      setAiAnalysisModalVisible(true);
      setAiAnalysisLoading(false);
      return;
    }

    console.log('[AI Analysis] Starting, forceRegenerate:', forceRegenerate);
    console.log('[AI Analysis] mockAlert.assetCode:', mockAlert.assetCode, 'refuelTime:', mockAlert.refuelTime);

    // Check cache FIRST (before opening modal or clearing state) so we show cached data when available
    if (!forceRegenerate && mockAlert.assetCode && mockAlert.refuelTime) {
      const cached = loadAiAnalysisFromCache();
      if (cached && cached.result) {
        console.log('[AI Analysis] Using cached result');
        setAiAnalysisResult(cached.result);
        setAiAnalysisFromCache(true);
        setAiAnalysisGeneratedAt(cached.generatedAt || null);
        setAiAnalysisError(null);
        setAiAnalysisLoading(false);
        setAiAnalysisModalVisible(true);
        return;
      }
      console.log('[AI Analysis] No cache found, will call API');
    } else {
      console.log('[AI Analysis] Skipping cache check - forceRegenerate or missing data');
    }

    // No cache: show loading, clear previous result, open modal, then call API
    setAiAnalysisLoading(true);
    setAiAnalysisError(null);
    setAiAnalysisResult(null);
    setAiAnalysisFromCache(false);
    setAiAnalysisGeneratedAt(null);
    setAiAnalysisModalVisible(true);

    try {
      // Gather all the data for AI analysis
      const analysisData = {
        // Current alert information
        currentAlert: {
          assetCode: mockAlert.assetCode,
          altName: mockAlert.altName,
          ownerCode: mockAlert.ownerCode,
          assetModel: mockAlert.assetModel,
          voucherNo: mockAlert.voucherNo,
          refuelTime: mockAlert.refuelTime,
          lastFullTankTime: mockAlert.lastFullTankTime,
          fuelAdded: mockAlert.fuelAdded,
          actualRate: mockAlert.actualRate,
          standardRate: mockAlert.standardRate,
          deviation: mockAlert.deviation,
          hasHours: mockAlert.hasHours,
          hasKms: mockAlert.hasKms,
          hasKwh: mockAlert.hasKwh,
          standardUnit: mockAlert.standardUnit,
          hoursRun: mockAlert.hoursRun,
          kmsRun: mockAlert.kmsRun,
          kwhRun: mockAlert.kwhRun,
          currentOdo: mockAlert.currentOdo,
          lastFullTankOdo: mockAlert.lastFullTankOdo,
        },

        // Past 14 full tank trend data
        fullTankTrend: (filteredTrendData || []).slice(0, 14).map(item => ({
          refuelTime: item.refuelTime,
          fuelAdded: item.fuelAdded,
          actualRate: item.actualRate,
          standardRate: item.standardRate,
          deviation: item.deviation,
          kmsRun: item.kmsRun,
          hoursRun: item.hoursRun,
          kwhRun: item.kwhRun,
          voucherNo: item.voucherNo,
        })),

        // IoT daily consumption data (14 days)
        iotDailyData: (iotConsumptionData || []).slice(0, 14).map(item => ({
          date: item.date,
          fuelConsumed: item.fuelConsumed,
          consumptionRate: item.consumptionRate,
          distanceTravelled: item.distanceTravelled,
          possibleRefuel: item.possibleRefuel,
          possibleDrain: item.possibleDrain,
        })),

        // IoT data for full tank to full tank period
        iotFullTankPeriodData: fetchedIotData ? {
          summary: fetchedIotData.summary || null,
          dailyData: (fetchedIotData.dailyData || []).slice(0, 30).map(item => ({
            date: item.date,
            fuelConsumed: item.fuelConsumed,
            consumptionRate: item.consumptionRate,
            distanceTravelled: item.distanceTravelled,
            possibleRefuel: item.possibleRefuel,
            possibleDrain: item.possibleDrain,
          })),
        } : null,

        // IoT Sensor Data (period stats between full tanks)
        iotSensorData: fetchedIotData?.iotPeriodStats ? {
          totalMovingTime: fetchedIotData.iotPeriodStats.totalMovingTime,
          totalMovingTimeDIN: fetchedIotData.iotPeriodStats.totalMovingTimeDIN,
          totalPauseTime: fetchedIotData.iotPeriodStats.totalPauseTime,
          totalPauseTimeDIN: fetchedIotData.iotPeriodStats.totalPauseTimeDIN,
          totalIgnitionTime: fetchedIotData.iotPeriodStats.totalIgnitionTime,
          totalIgnitionTimeDIN: fetchedIotData.iotPeriodStats.totalIgnitionTimeDIN,
          totalStopTime: fetchedIotData.iotPeriodStats.totalStopTime,
          totalStopTimeDIN: fetchedIotData.iotPeriodStats.totalStopTimeDIN,
          distanceTravelled: fetchedIotData.iotPeriodStats.distanceTravelled,
          fuelConsumed: fetchedIotData.iotPeriodStats.fuelConsumed,
          possibleRefuel: fetchedIotData.iotPeriodStats.possibleRefuel,
          possibleDrain: fetchedIotData.iotPeriodStats.possibleDrain,
          avgFuelConsumedKmPerLtr: fetchedIotData.iotPeriodStats.avgFuelConsumedKmPerLtr,
          avgFuelConsumedLtrPerHr: fetchedIotData.iotPeriodStats.avgFuelConsumedLtrPerHr,
          avgFuelConsumedLtrPerHrDIN: fetchedIotData.iotPeriodStats.avgFuelConsumedLtrPerHrDIN,
          avgFuelConsumedKwhPerLtr: fetchedIotData.iotPeriodStats.avgFuelConsumedKwhPerLtr,
          averageSpeed: fetchedIotData.iotPeriodStats.averageSpeed,
        } : null,

        // Comparison: IoT Sensor Data vs Manual Full Tank Entry
        iotVsManualComparison: {
          // Time comparison
          iotIgnitionTime: fetchedIotData?.iotPeriodStats?.totalIgnitionTime || null,
          manualHoursRun: mockAlert.hoursRun,
          
          // Distance comparison
          iotDistanceTravelled: fetchedIotData?.iotPeriodStats?.distanceTravelled || null,
          manualKmsRun: mockAlert.kmsRun,
          
          // Fuel comparison
          iotFuelConsumed: fetchedIotData?.iotPeriodStats?.fuelConsumed || null,
          iotPossibleRefuel: fetchedIotData?.iotPeriodStats?.possibleRefuel || null,
          iotPossibleDrain: fetchedIotData?.iotPeriodStats?.possibleDrain || null,
          manualFuelAdded: mockAlert.fuelAdded,
          
          // Rate comparison (KM/L)
          iotAvgKmPerLtr: fetchedIotData?.iotPeriodStats?.avgFuelConsumedKmPerLtr || null,
          manualActualRateKmPerL: mockAlert.hasKms ? mockAlert.actualRate : null,
          
          // Rate comparison (L/Hours)
          iotAvgLtrPerHr: fetchedIotData?.iotPeriodStats?.avgFuelConsumedLtrPerHr || null,
          manualActualRateLPerHr: mockAlert.hasHours ? mockAlert.actualRate : null,
          
          // Rate comparison (KWH/L)
          iotAvgKwhPerLtr: fetchedIotData?.iotPeriodStats?.avgFuelConsumedKwhPerLtr || null,
          manualActualRateKwhPerL: mockAlert.hasKwh ? mockAlert.actualRate : null,
        },

        // Scope of the past-alert list below. The alert history is fetched for a
        // creation-time window, so an empty list means "none in this window",
        // not "none ever" - state that explicitly for the model.
        pastAlertsScope: alertHistoryWindow
          ? {
              createdFrom: moment(alertHistoryWindow.fromMs).format('YYYY-MM-DD HH:mm:ss'),
              createdTo: moment(alertHistoryWindow.toMs - 1).format('YYYY-MM-DD HH:mm:ss'),
              lookback: alertHistoryWindow.label,
              lifecycleStatesLoaded: alertHistoryWindow.loadedSources || [],
              note: (alertHistoryWindow.failedSources || []).length > 0
                ? `INCOMPLETE: only ${(alertHistoryWindow.loadedSources || []).join(' and ')} alerts loaded; ` +
                  `${alertHistoryWindow.failedSources.map(f => f.label).join(', ')} alerts failed to load. ` +
                  'Alerts created before this window were not fetched either.'
                : 'Both open and closed alerts were fetched. Alerts created before this window were not fetched.',
            }
          : { note: 'Alert history was not loaded for this alert.' },

        // Past alerts history (within pastAlertsScope). `lifecycle` is the
        // authoritative open/closed state - an OPEN alert is an unresolved
        // issue on this asset, not a historical one.
        pastAlerts: (alertHistory || []).slice(0, 10).map(alert => {
          const workflowInfo = extractWorkflowInfo ? extractWorkflowInfo(alert) : {};
          return {
            alertId: alert.alertId,
            alertTitle: alert.alertTitle,
            lifecycle: alert._lifecycle,
            // Effective status (Ignore closures resolved); the raw API value is
            // carried alongside so the reader can tell the two apart.
            status: alert._status,
            currentStatusFromApi: alert.currentStatus,
            createdAt: alert.createdAt,
            analysis: workflowInfo.analysis,
            actionSuggested: workflowInfo.actionSuggested,
            actionTaken: workflowInfo.actionTaken,
          };
        }),
      };

      // Construct the prompt for OpenAI
      const systemPrompt = `You are a fuel consumption analysis expert for fleet management. Your role is to analyze fuel alerts and provide actionable insights.

When analyzing fuel data:
1. Look at consumption patterns across full tank to full tank periods
2. Compare actual rates vs standard rates to identify anomalies
3. Consider IoT sensor data for validation - IMPORTANT: Compare IoT readings with manual entries
4. Review historical alerts and their resolutions
5. Check for signs of fuel theft, sensor issues, or operational inefficiencies

CRITICAL COMPARISON POINTS (IoT vs Manual):
- Compare iotIgnitionTime with manualHoursRun - significant differences may indicate meter tampering or sensor issues
- Compare iotDistanceTravelled with manualKmsRun - discrepancies may indicate odometer issues
- Compare iotFuelConsumed with manualFuelAdded - should be close; large differences are red flags
- Compare iotPossibleRefuel with manualFuelAdded - IoT should detect the refuel event; mismatch needs investigation
- Check iotPossibleDrain - any value > 0 indicates potential fuel theft or leakage
- Compare consumption rates (IoT vs Manual) - rates should be consistent; variances indicate data issues

Provide your analysis in the following format:
## Analysis Summary
[Brief overview of findings]

## Key Observations
[List of specific observations from the data]

## IoT vs Manual Data Comparison
[Specific analysis of discrepancies between IoT sensor readings and manual entries]

## Potential Issues
[Any red flags or concerns identified]

## Recommended Actions
[Specific actions the operator should take]

## Confidence Level
[High/Medium/Low with explanation]`;

      const userPrompt = `Please analyze the following fuel alert data and provide your analysis and recommended actions.

CURRENT ALERT:
Asset Code: ${analysisData.currentAlert.assetCode}
Asset Name: ${analysisData.currentAlert.altName || 'N/A'}
Owner Code: ${analysisData.currentAlert.ownerCode || 'N/A'}
Model: ${analysisData.currentAlert.assetModel || 'N/A'}
Refuel Time: ${analysisData.currentAlert.refuelTime}
Last Full Tank: ${analysisData.currentAlert.lastFullTankTime}
Fuel Added: ${analysisData.currentAlert.fuelAdded} L
Actual Rate: ${analysisData.currentAlert.actualRate?.toFixed(2) || 'N/A'} ${analysisData.currentAlert.hasKms ? 'KM/L' : analysisData.currentAlert.hasHours ? 'L/Hours' : analysisData.currentAlert.hasKwh ? 'KWH/L' : ''}
Standard Rate: ${analysisData.currentAlert.standardRate?.toFixed(2) || 'N/A'}
Deviation: ${analysisData.currentAlert.deviation?.toFixed(2) || 'N/A'}%

PAST FULL TANK TREND (last 14 entries):
${JSON.stringify(analysisData.fullTankTrend, null, 2)}

IOT DAILY DATA (last 14 days):
${JSON.stringify(analysisData.iotDailyData, null, 2)}

IOT FULL TANK TO FULL TANK PERIOD DATA:
${analysisData.iotFullTankPeriodData ? JSON.stringify(analysisData.iotFullTankPeriodData, null, 2) : 'No data available'}

IOT SENSOR DATA (period stats between full tanks):
${analysisData.iotSensorData ? JSON.stringify(analysisData.iotSensorData, null, 2) : 'No IoT sensor data available'}

COMPARISON: IOT SENSOR DATA vs MANUAL FULL TANK ENTRY:
This compares what the IoT sensors recorded vs what was manually entered for the full tank event.
${JSON.stringify(analysisData.iotVsManualComparison, null, 2)}

Key comparison points:
- iotIgnitionTime vs manualHoursRun: Compare IoT ignition time with manually recorded hours run
- iotDistanceTravelled vs manualKmsRun: Compare IoT distance with manually recorded KMs
- iotFuelConsumed vs manualFuelAdded: Compare IoT fuel consumption with manual fuel added
- iotPossibleRefuel vs manualFuelAdded: IoT detected refuel amount vs manual entry
- iotPossibleDrain: Any fuel drain detected by IoT (potential theft indicator)
- Rate comparisons: Compare IoT consumption rates with manually calculated rates

PAST ALERTS HISTORY:
Scope of this list (alerts outside it were not fetched - do not read an empty or
short list as proof that no earlier alerts exist):
${JSON.stringify(analysisData.pastAlertsScope, null, 2)}
${JSON.stringify(analysisData.pastAlerts, null, 2)}

Please provide your analysis and recommended actions.`;

      // Use the API key provided when user clicked AI Analysis (key is not stored)
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${keyToUse}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          temperature: 0.7,
          max_tokens: 2000,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `OpenAI API request failed with status ${response.status}`);
      }

      const data = await response.json();
      const aiResponse = data.choices?.[0]?.message?.content;

      if (!aiResponse) {
        throw new Error('No response received from OpenAI');
      }

      const generatedAt = new Date().toISOString();
      saveAiAnalysisToCache(aiResponse, generatedAt);
      setAiAnalysisResult(aiResponse);
      setAiAnalysisGeneratedAt(generatedAt);
    } catch (error) {
      console.error('AI Analysis error:', error);
      setAiAnalysisError(error.message);
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  // Load API data from localStorage on mount
  const loadApiDataFromStorage = () => {
    try {
      const savedData = localStorage.getItem('fuelAlertApiData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // Only load if it matches current asset
        if (parsed.assetCode === mockAlert.assetCode) {
          if (parsed.trendData) setTrendData(parsed.trendData);
          if (parsed.fuelEntries) setFuelEntries(parsed.fuelEntries);
          if (parsed.iotRefuelEvents) {
            setIotRefuelEvents(parsed.iotRefuelEvents);
            // Set metadata if available, otherwise infer from current mockAlert
            if (parsed.iotEventsMetadata) {
              setIotEventsMetadata(parsed.iotEventsMetadata);
            } else {
              // Infer metadata from current mockAlert if events exist
              if (parsed.iotRefuelEvents.length > 0) {
                setIotEventsMetadata({
                  assetCode: parsed.assetCode,
                  refuelTime: mockAlert.refuelTime,
                  lastFullTankTime: mockAlert.lastFullTankTime,
                });
              }
            }
          }
          if (parsed.standardRate) setStandardRate(parsed.standardRate);
          console.log('Loaded API data from localStorage for asset:', parsed.assetCode);
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading API data from localStorage:', error);
    }
    return false;
  };

  // Save API data to localStorage (with optional parameters to save immediately)
  const saveApiDataToStorage = (dataOverrides = {}) => {
    try {
      const dataToSave = {
        assetCode: mockAlert.assetCode,
        trendData: dataOverrides.trendData !== undefined ? dataOverrides.trendData : trendData,
        fuelEntries: dataOverrides.fuelEntries !== undefined ? dataOverrides.fuelEntries : fuelEntries,
        iotRefuelEvents: dataOverrides.iotRefuelEvents !== undefined ? dataOverrides.iotRefuelEvents : iotRefuelEvents,
        fetchedIotData: dataOverrides.fetchedIotData !== undefined ? dataOverrides.fetchedIotData : fetchedIotData,
        standardRate: dataOverrides.standardRate !== undefined ? dataOverrides.standardRate : standardRate,
        iotEventsMetadata: dataOverrides.iotEventsMetadata !== undefined ? dataOverrides.iotEventsMetadata : iotEventsMetadata,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('fuelAlertApiData', JSON.stringify(dataToSave));
      console.log('Saved API data to localStorage');
    } catch (error) {
      console.error('Error saving API data to localStorage:', error);
    }
  };

  // Auto-save to localStorage when data changes
  useEffect(() => {
    if (mockAlert.assetCode && (trendData.length > 0 || fuelEntries.length > 0 || iotRefuelEvents.length > 0 || fetchedIotData)) {
      saveApiDataToStorage();
    }
  }, [trendData, fuelEntries, iotRefuelEvents, fetchedIotData, standardRate, mockAlert.assetCode]);

  // Update mockAlert with altName and ownerCode when assetCodeMap is updated
  useEffect(() => {
    if (mockAlert.assetCode && assetCodeMap[mockAlert.assetCode]) {
      const assetInfo = assetCodeMap[mockAlert.assetCode];
      // Always update from API data, even if null (to clear old localStorage values)
      setMockAlert(prev => {
        // Only update if values are different to avoid unnecessary re-renders
        if (prev.altName !== assetInfo.altName || prev.ownerCode !== assetInfo.ownerCode) {
          return {
            ...prev,
            altName: assetInfo.altName !== undefined ? assetInfo.altName : prev.altName,
            ownerCode: assetInfo.ownerCode !== undefined ? assetInfo.ownerCode : prev.ownerCode
          };
        }
        return prev;
      });
    }
  }, [assetCodeMap, mockAlert.assetCode]);

  // Handler functions for analysis
  const handleSaveAnalysis = () => {
    if (!analysis.trim() || !suggestedAction.trim()) {
      Modal.error({
        title: 'Validation Error',
        content: 'Please fill in Analysis and Suggested Action before saving.'
      });
      return;
    }

    const now = moment();
    const dataToSave = {
      alertId: alertId,
      analysis: analysis.trim(),
      suggestedAction: suggestedAction.trim(),
      finalAction: finalAction.trim() || '',
      alertStatus: alertStatus,
      analyzedBy: currentUser,
      analyzedAt: now.format('YYYY-MM-DD HH:mm:ss'),
      updatedAt: now.format('YYYY-MM-DD HH:mm:ss'),
    };

    // In real app, this would be an API call
    console.log('Saving analysis:', dataToSave);
    
    Modal.success({
      title: 'Analysis Saved',
      content: 'Analysis and suggested action have been saved successfully.',
    });
  };

  const handleSaveActionTaken = () => {
    if (!finalAction.trim()) {
      Modal.error({
        title: 'Validation Error',
        content: 'Please enter the action taken before saving.'
      });
      return;
    }

    const now = moment();
    const dataToSave = {
      alertId: alertId,
      analysis: analysis.trim(),
      suggestedAction: suggestedAction.trim(),
      finalAction: finalAction.trim(),
      alertStatus: alertStatus,
      actionTakenUpdatedBy: currentUser,
      actionTakenUpdatedAt: now.format('YYYY-MM-DD HH:mm:ss'),
      updatedAt: now.format('YYYY-MM-DD HH:mm:ss'),
    };

    // In real app, this would be an API call
    console.log('Saving action taken:', dataToSave);
    
    Modal.success({
      title: 'Action Taken Saved',
      content: 'Action taken has been saved successfully.',
    });
  };

  const handleMarkAlertStatus = (status) => {
    const now = moment();
    const dataToSave = {
      alertId: alertId,
      analysis: analysis.trim(),
      suggestedAction: suggestedAction.trim(),
      finalAction: finalAction.trim(),
      alertStatus: status,
      alertStatusUpdatedBy: currentUser,
      alertStatusUpdatedAt: status ? now.format('YYYY-MM-DD HH:mm:ss') : null,
      updatedAt: now.format('YYYY-MM-DD HH:mm:ss'),
    };

    setAlertStatus(status);
    
    // In real app, this would be an API call
    console.log('Updating alert status:', dataToSave);
    
    if (status) {
      Modal.success({
        title: 'Alert Status Updated',
        content: `Alert has been marked as ${status === 'confirmed' ? 'confirmed' : 'discarded'}.`,
      });
    }
  };

  // Initialize analysis range with manual times
  useEffect(() => {
    if (mockAlert.lastFullTankTime && mockAlert.refuelTime) {
      // Parse times as IST (GMT+5:30) since they're stored as IST strings
      setAnalysisRange([
        moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30'),
        moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30'),
      ]);
    }
  }, [mockAlert.lastFullTankTime, mockAlert.refuelTime]);

  // Clear fetched IoT timeline data whenever alert context changes (asset/full tank pair)
  useEffect(() => {
    if (!mockAlert.assetCode || !mockAlert.lastFullTankTime || !mockAlert.refuelTime) return;

    const currentContextKey = `${mockAlert.assetCode}__${mockAlert.lastFullTankTime}__${mockAlert.refuelTime}`;
    if (iotTimelineContextRef.current && iotTimelineContextRef.current !== currentContextKey) {
      setFetchedIotData(null);
      setDataLoaded(false);
    }
    iotTimelineContextRef.current = currentContextKey;
  }, [mockAlert.assetCode, mockAlert.lastFullTankTime, mockAlert.refuelTime]);

  // Sync standardRate state with mockAlert.standardRate when it changes
  // This ensures the table uses the correct KWH rate from mockAlert
  useEffect(() => {
    if (mockAlert.standardRate !== null && mockAlert.standardRate !== undefined) {
      console.log('Syncing standardRate from mockAlert:', mockAlert.standardRate);
      setStandardRate(mockAlert.standardRate);
    }
  }, [mockAlert.standardRate]);

  // Fetch trend data from API - re-run when mockAlert or dataReloadTrigger changes
  useEffect(() => {
    if (!mockAlert.assetCode || !mockAlert.refuelTime) return;
    
    const loadTrendData = async () => {
      setIsLoadingTrendData(true);
      setTrendDataError(null);

      try {
        // Step 1: Fetch last N full tank events before current refuel (based on trendPeriod)
        const count = parseInt(trendPeriod, 10) || 7;
        console.log(`Loading trend data: fetching ${count} full tank events for asset ${mockAlert.assetCode}`);
        const pastEventsResult = await fetchLastFullTankEvents(
          mockAlert.assetCode,
          mockAlert.refuelTime,
          count // Get last N full tank events based on trendPeriod
        );

        if (!pastEventsResult.success) {
          setTrendDataError(pastEventsResult.error || 'Failed to fetch trend data');
          setTrendData([]);
          setIsLoadingTrendData(false);
          return;
        }
        
        console.log(`Fetched ${pastEventsResult.events.length} past full tank events`);

        // Step 2: Fetch the current refuel event
        let currentRefuelEvent = null;
        const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');
        const currentRefuelMomentValue = currentRefuelMoment.valueOf();
        
        // First, check if current refuel event is already in past events
        // (This can happen if the API includes events at or after the beforeDate)
        const currentInPastEvents = pastEventsResult.events.find(event => {
          const eventMoment = moment(event.date);
          const timeDiff = Math.abs(eventMoment.valueOf() - currentRefuelMomentValue);
          // Match if within 5 minutes (more flexible than 1 minute)
          return timeDiff < 300000; // 5 minutes
        });
        
        if (currentInPastEvents) {
          console.log('Current refuel event found in past events list');
          currentRefuelEvent = currentInPastEvents;
        } else {
          // Try to fetch it separately
          try {
            // Find asset ID
            const assetResult = await findAssetIdByCode(mockAlert.assetCode);
            
            if (assetResult.success) {
              const assetId = assetResult.assetId;
              
              // Update assetCodeMap with asset info
              if (!assetCodeMap[mockAlert.assetCode]) {
                assetCodeMap[mockAlert.assetCode] = {};
              }
              assetCodeMap[mockAlert.assetCode] = {
                ...assetCodeMap[mockAlert.assetCode],
                assetId: assetId,
                altName: assetResult.altName,
                ownerCode: assetResult.ownerCode,
                assetModel: assetResult.assetModel,
                location: assetResult.location,
                subLocation: assetResult.subLocation
              };
              setAssetCodeMap({ ...assetCodeMap });
              
              // Update mockAlert with asset info (always update, even if null, to clear old localStorage values)
              setMockAlert(prev => ({
                ...prev,
                altName: assetResult.altName !== undefined ? assetResult.altName : prev.altName,
                ownerCode: assetResult.ownerCode !== undefined ? assetResult.ownerCode : prev.ownerCode,
                location: assetResult.location !== undefined ? assetResult.location : prev.location,
                subLocation: assetResult.subLocation !== undefined ? assetResult.subLocation : prev.subLocation
              }));
              
              // Fetch entries around the current refuel time (2 days before to 1 day after for better coverage)
              const startMoment = currentRefuelMoment.clone().subtract(2, 'days');
              const endMoment = currentRefuelMoment.clone().add(1, 'day');
              
              console.log(`Fetching current refuel event for ${mockAlert.assetCode} around ${mockAlert.refuelTime}`);
              const currentRefuelResult = await fetchFuelEntries(
                assetId,
                startMoment.toISOString(),
                endMoment.toISOString()
              );
              
              if (currentRefuelResult.success && currentRefuelResult.data) {
                console.log(`Found ${currentRefuelResult.data.length} entries around current refuel time`);
                
                // Find the current refuel event (matching time and voucher if available)
                // Try multiple matching strategies
                currentRefuelEvent = currentRefuelResult.data.find(entry => {
                  if (!entry.isFullTank) return false;
                  
                  const entryMoment = moment(entry.date);
                  const timeDiff = Math.abs(entryMoment.valueOf() - currentRefuelMomentValue);
                  
                  // Strategy 1: Match by voucher number (most reliable)
                  if (mockAlert.voucherNo && entry.voucherNo === mockAlert.voucherNo) {
                    console.log(`Found current refuel event by voucher number: ${entry.voucherNo}`);
                    return true;
                  }
                  
                  // Strategy 2: Match by time (within 5 minutes)
                  if (timeDiff < 300000) { // 5 minutes
                    console.log(`Found current refuel event by time match: ${entry.date} (diff: ${timeDiff}ms)`);
                    return true;
                  }
                  
                  return false;
                });
                
              if (!currentRefuelEvent) {
                console.warn('Current refuel event not found in API response. Will try to construct from mockAlert data.', {
                  searchedTime: mockAlert.refuelTime,
                  searchedVoucher: mockAlert.voucherNo,
                  foundEntries: currentRefuelResult.data.length,
                  fullTankEntries: currentRefuelResult.data.filter(e => e.isFullTank).length,
                  entryDates: currentRefuelResult.data.filter(e => e.isFullTank).map(e => e.date),
                });
                
                // Fallback: Try to construct current refuel event from mockAlert data
                // Find the most recent full tank entry that matches our criteria
                const recentFullTankEntries = currentRefuelResult.data
                  .filter(e => e.isFullTank)
                  .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
                
                if (recentFullTankEntries.length > 0) {
                  // Use the most recent full tank entry as the current refuel event
                  const mostRecent = recentFullTankEntries[0];
                  const mostRecentTime = moment(mostRecent.date);
                  const timeDiff = Math.abs(mostRecentTime.valueOf() - currentRefuelMomentValue);
                  
                  // If within 1 day, use it as the current refuel event
                  if (timeDiff < 86400000) { // 1 day in milliseconds
                    console.log('Using most recent full tank entry as current refuel event:', {
                      date: mostRecent.date,
                      voucher: mostRecent.voucherNo,
                      timeDiff: timeDiff,
                    });
                    currentRefuelEvent = mostRecent;
                  }
                }
              } else {
                console.log('Current refuel event found:', {
                  date: currentRefuelEvent.date,
                  voucher: currentRefuelEvent.voucherNo,
                  quantity: currentRefuelEvent.quantity,
                });
              }
            }
            }
          } catch (error) {
            console.warn('Error fetching current refuel event:', error);
            // Continue with past events only
          }
        }

        // Step 3: Combine past events with current refuel event
        let allEvents = [...pastEventsResult.events];
        if (currentRefuelEvent && !currentInPastEvents) {
          // Only add if it wasn't already in the past events
          // Check for duplicates more carefully
          const currentEventTime = moment(currentRefuelEvent.date);
          const isDuplicate = allEvents.some(event => {
            const eventTime = moment(event.date);
            const timeDiff = Math.abs(eventTime.valueOf() - currentEventTime.valueOf());
            // Consider duplicate if within 1 minute AND same voucher (if available)
            if (timeDiff < 60000) {
              if (currentRefuelEvent.voucherNo && event.voucherNo) {
                return currentRefuelEvent.voucherNo === event.voucherNo;
              }
              return true; // If no voucher, consider it duplicate if time matches
            }
            return false;
          });
          
          if (!isDuplicate) {
            console.log('Adding current refuel event to trend data');
            allEvents.push(currentRefuelEvent);
          } else {
            console.log('Current refuel event already exists in past events, skipping duplicate');
          }
        }
        
        // Step 4: Deduplicate events by voucher number (vouchers should be unique)
        // If multiple events have the same voucher number, keep the one with the most recent date
        const voucherMap = new Map();
        allEvents.forEach(event => {
          if (event.voucherNo) {
            const existingEvent = voucherMap.get(event.voucherNo);
            if (!existingEvent) {
              // First occurrence of this voucher
              voucherMap.set(event.voucherNo, event);
            } else {
              // Duplicate voucher found - keep the one with the most recent date
              const existingDate = moment(existingEvent.date);
              const currentDate = moment(event.date);
              if (currentDate.isAfter(existingDate)) {
                console.log(`Duplicate voucher ${event.voucherNo} found. Keeping newer event (${event.date} vs ${existingEvent.date})`);
                voucherMap.set(event.voucherNo, event);
              } else {
                console.log(`Duplicate voucher ${event.voucherNo} found. Keeping existing event (${existingEvent.date} vs ${event.date})`);
              }
            }
          } else {
            // Event without voucher number - keep it but check for time-based duplicates
            const eventTime = moment(event.date);
            const isTimeDuplicate = Array.from(voucherMap.values()).some(e => {
              if (!e.voucherNo) {
                const eTime = moment(e.date);
                return Math.abs(eTime.valueOf() - eventTime.valueOf()) < 60000; // Within 1 minute
              }
              return false;
            });
            if (!isTimeDuplicate) {
              // Use a unique key for events without voucher
              voucherMap.set(`no-voucher-${event.date}`, event);
            }
          }
        });
        
        // Convert map back to array and sort by date descending (newest first)
        allEvents = Array.from(voucherMap.values())
          .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
        
        console.log(`After deduplication: ${allEvents.length} unique events (was ${pastEventsResult.events.length + (currentRefuelEvent && !currentInPastEvents ? 1 : 0)})`);
        
        console.log(`Total events in trend data: ${allEvents.length} (past: ${pastEventsResult.events.length}, current: ${currentRefuelEvent ? 'found' : 'not found'})`);
        
        // Log the dates of all events for debugging
        if (allEvents.length > 0) {
          console.log('Event dates (newest first):', allEvents.map(e => ({
            date: e.date,
            voucher: e.voucherNo,
            quantity: e.quantity,
            isCurrent: currentRefuelEvent && moment(e.date).isSame(moment(currentRefuelEvent.date), 'minute')
          })));
        }

        // Step 4: Extract standard rate and variance threshold from the first entry's assetModel if available
        // First, determine asset type from meter data, then match standard rate to asset type
        // Priority: KWH > KMS > HOURS (same as Full Tank Mileage Report)
        let extractedStandardRate = standardRate; // Use current value as default
        
        // Determine asset type from meter data (check first entry's meters)
        let assetMeterType = null; // 'KWH', 'KM', or 'HOURS'
        if (allEvents.length > 0) {
          const firstEntry = allEvents[0];
          if (firstEntry.mileage && firstEntry.mileage.length > 0) {
            const sampleMeter = firstEntry.mileage[0];
            const meterNameUpper = sampleMeter.name ? sampleMeter.name.toUpperCase() : '';
            
            if (meterNameUpper.includes('KWH')) {
              assetMeterType = 'KWH';
            } else if (meterNameUpper.includes('KM') || meterNameUpper.includes('KMS') || 
                       meterNameUpper.includes('ODO') || meterNameUpper.includes('DISTANCE')) {
              assetMeterType = 'KM';
            } else if (meterNameUpper.includes('HOUR') || meterNameUpper.includes('HRS')) {
              assetMeterType = 'HOURS';
            }
          }
          
          if (firstEntry.assetModel && firstEntry.assetModel.standardFuelConsumptionRate) {
            const standardRates = firstEntry.assetModel.standardFuelConsumptionRate;
            // The API response shows standardFuelConsumptionRate is an array with perType
            if (standardRates.length > 0) {
              // First, try to match by asset meter type
              // If asset has KWH meter, prioritize KWH rate
              // If asset has KM meter, prioritize KM rate
              // If asset has HOURS meter, prioritize HOURS rate
              // Fallback to priority: KWH > KMS > HOURS
              let kwhRate = null;
              let kmRate = null;
              let hoursRate = null;
              
              for (const rateObj of standardRates) {
                if (!rateObj.perType) continue;
                const perTypeName = typeof rateObj.perType === 'string' 
                  ? null 
                  : (rateObj.perType.name || '').toUpperCase();
                if (!perTypeName) continue;
                
                if (perTypeName.includes('KWH')) {
                  kwhRate = rateObj;
                } else if (perTypeName.includes('KM') || perTypeName === 'KMS') {
                  kmRate = rateObj;
                } else if (perTypeName.includes('HOUR') || perTypeName === 'HOURS' || perTypeName === 'HRS') {
                  hoursRate = rateObj;
                }
              }
              
              // Select rate: match asset meter type first, then fallback to priority
              let selectedRate = null;
              let selectedRateType = null; // Track which type was selected
              
              if (assetMeterType === 'KWH') {
                // For KWH assets, ONLY use KWH rate - don't fall back to hours
                if (kwhRate) {
                  selectedRate = kwhRate;
                  selectedRateType = 'KWH';
                } else {
                  console.warn('KWH asset detected but no KWH standard rate found in model');
                  // Don't fall back to hours rate for KWH assets
                }
              } else if (assetMeterType === 'KM') {
                if (kmRate) {
                  selectedRate = kmRate;
                  selectedRateType = 'KM';
                } else {
                  // Fallback to priority: KWH > KMS > HOURS
                  selectedRate = kwhRate || hoursRate || standardRates[0];
                  selectedRateType = selectedRate === kwhRate ? 'KWH' : (selectedRate === hoursRate ? 'HOURS' : 'UNKNOWN');
                }
              } else if (assetMeterType === 'HOURS') {
                if (hoursRate) {
                  selectedRate = hoursRate;
                  selectedRateType = 'HOURS';
                } else {
                  // Fallback to priority: KWH > KMS > HOURS
                  selectedRate = kwhRate || kmRate || standardRates[0];
                  selectedRateType = selectedRate === kwhRate ? 'KWH' : (selectedRate === kmRate ? 'KM' : 'UNKNOWN');
                }
              } else {
                // Asset meter type not detected, use priority: KWH > KMS > HOURS
                selectedRate = kwhRate || kmRate || hoursRate || standardRates[0];
                selectedRateType = selectedRate === kwhRate ? 'KWH' : (selectedRate === kmRate ? 'KM' : (selectedRate === hoursRate ? 'HOURS' : 'UNKNOWN'));
              }
              
              if (selectedRate && selectedRate.fuelQty) {
                const rawRate = selectedRate.fuelQty;
                const rateUnitHint = selectedRateType === 'KWH'
                  ? 'L_PER_KWH'
                  : (selectedRateType === 'KM' ? 'L_PER_KM' : 'L_PER_HOUR');
                const normalizedRate = normalizeStandardRateForDisplay(rawRate, rateUnitHint);

                if (normalizedRate !== null) {
                  extractedStandardRate = normalizedRate;
                  setStandardRate(normalizedRate);
                  console.log(`Selected ${selectedRateType} standard rate: ${rawRate} (${rateUnitHint}) -> ${normalizedRate} (display unit)`);
                }
              } else {
                console.warn('No standard rate selected or fuelQty is missing');
              }
            }
          }
          
          // Extract variance threshold from asset model
          if (firstEntry.assetModel && firstEntry.assetModel.fuelMileageThreshold && Array.isArray(firstEntry.assetModel.fuelMileageThreshold) && firstEntry.assetModel.fuelMileageThreshold.length > 0) {
            const firstThreshold = firstEntry.assetModel.fuelMileageThreshold[0];
            if (firstThreshold && firstThreshold.value !== null && firstThreshold.value !== undefined && !isNaN(firstThreshold.value) && firstThreshold.value > 0) {
              setVarianceThreshold(firstThreshold.value);
              console.log(`Extracted variance threshold from asset model: ${firstThreshold.value}`);
            }
          }
        }

        // Step 5: Transform API data to trend data format using extracted standard rate
        // Log the standard rate being used for debugging
        console.log('Transforming trend data with standard rate:', {
          extractedStandardRate,
          assetMeterType,
          hasKwh: mockAlert.hasKwh,
          hasKms: mockAlert.hasKms,
          hasHours: mockAlert.hasHours
        });
        
        // Combine all fuel entries from past events with current refuel entries
        // This includes both full tank and non-full tank entries for consumption calculation
        let allFuelEntries = [...(pastEventsResult.allEntries || [])];
        
        // Also include entries from current refuel event fetch if available
        // (The current refuel event may have been fetched separately with its surrounding entries)
        if (currentRefuelEvent && !currentInPastEvents) {
          // Add current refuel event to allFuelEntries if not already included
          const isCurrentInAllEntries = allFuelEntries.some(e => {
            const entryDate = moment(e.date);
            const currentDate = moment(currentRefuelEvent.date);
            return Math.abs(entryDate.valueOf() - currentDate.valueOf()) < 60000; // Within 1 minute
          });
          if (!isCurrentInAllEntries) {
            allFuelEntries.push(currentRefuelEvent);
          }
        }
        
        console.log(`Total fuel entries for consumption calculation: ${allFuelEntries.length} (full tank events: ${allEvents.length})`);
        
        const transformedData = transformToTrendData(allEvents, extractedStandardRate, mockAlert.standardUnit, allFuelEntries);
        
        // Step 6: Post-process to fill in missing data for current refuel event from mockAlert
        // The current event is the one matching mockAlert.refuelTime
        // Note: currentRefuelMoment is already declared above, so we reuse it
        const transformedDataWithCurrent = transformedData.map(item => {
          const itemMoment = moment(item.refuelTime, 'YYYY-MM-DD HH:mm:ss');
          const timeDiff = Math.abs(itemMoment.valueOf() - currentRefuelMoment.valueOf());
          
          // Check if this is the current refuel event (within 5 minutes)
          if (timeDiff < 300000) { // 5 minutes
            // This is the current event - fill in missing data from mockAlert
            const updatedItem = { ...item };
            
            // Fill in hoursRun, kmsRun, or kwhRun from mockAlert if missing
            // For KWH and KM assets, also ensure hoursRun is populated for comparison
            if (mockAlert.hasHours && mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
              updatedItem.hoursRun = mockAlert.hoursRun;
            }
            if (mockAlert.hasKms && mockAlert.kmsRun !== null && mockAlert.kmsRun !== undefined && mockAlert.kmsRun > 0) {
              updatedItem.kmsRun = mockAlert.kmsRun;
            }
            if (mockAlert.hasKwh && mockAlert.kwhRun !== null && mockAlert.kwhRun !== undefined && mockAlert.kwhRun > 0) {
              updatedItem.kwhRun = mockAlert.kwhRun;
            }
            // For KWH and KM assets, also populate hoursRun from mockAlert if available (for comparison)
            if ((mockAlert.hasKwh || mockAlert.hasKms) && mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
              updatedItem.hoursRun = mockAlert.hoursRun;
            }
            
            // Recalculate actualRate if we now have runValue and fuelAdded
            // Use updatedItem.fuelAdded (from transformToTrendData) which includes intermediate non-full-tank entries;
            // mockAlert.fuelAdded may only be the current full tank quantity
            const runValue = mockAlert.hasHours ? updatedItem.hoursRun : (mockAlert.hasKms ? updatedItem.kmsRun : (mockAlert.hasKwh ? updatedItem.kwhRun : null));
            const fuelAdded = updatedItem.fuelAdded ?? mockAlert.fuelAdded;
            
            if (runValue !== null && runValue > 0 && fuelAdded > 0) {
              if (mockAlert.hasKms) {
                // KM-based: KM/L = kmsRun / fuelAdded
                updatedItem.actualRate = runValue / fuelAdded;
              } else if (mockAlert.hasHours) {
                // HOURS-based: L/Hours = fuelAdded / hoursRun
                updatedItem.actualRate = fuelAdded / runValue;
              } else if (mockAlert.hasKwh) {
                // KWH-based: KWH/L = kwhRun / fuelAdded
                updatedItem.actualRate = runValue / fuelAdded;
              }
              
              // Recalculate deviation — prefer mockAlert.standardRate so deviation matches the
              // Standard column display (extractedStandardRate can diverge when rate selection
              // differs between handleSelectFullTankEvent and loadTrendData).
              const deviationStandardRate = mockAlert.standardRate || extractedStandardRate;
              if (updatedItem.actualRate !== null && deviationStandardRate !== null && deviationStandardRate > 0) {
                // For KWH/L and KM/L: invert sign (higher actual = better = negative deviation)
                if (mockAlert.hasKwh || mockAlert.hasKms) {
                  updatedItem.deviation = ((deviationStandardRate - updatedItem.actualRate) / deviationStandardRate) * 100;
                } else {
                  // For L/Hour: normal calculation
                  updatedItem.deviation = ((updatedItem.actualRate - deviationStandardRate) / deviationStandardRate) * 100;
                }
              }
            }
            
            // NOTE: Do NOT overwrite fuelAdded here - it already contains the correct total
            // (full tank + intermediate entries) as calculated by transformToTrendData
            // Only update fullTankFuel if it's missing or incorrect
            if (mockAlert.fuelAdded && mockAlert.fuelAdded > 0 && !updatedItem.fullTankFuel) {
              updatedItem.fullTankFuel = mockAlert.fuelAdded;
            }
            
            syncMeterRunsFromRunFields(updatedItem);

            console.log('Updated current refuel event in trend data:', {
              refuelTime: updatedItem.refuelTime,
              hoursRun: updatedItem.hoursRun,
              kmsRun: updatedItem.kmsRun,
              fuelAdded: updatedItem.fuelAdded,
              fullTankFuel: updatedItem.fullTankFuel,
              intermediateFuelCount: updatedItem.intermediateFuelCount,
              actualRate: updatedItem.actualRate,
            });
            
            return updatedItem;
          }
          
          return item;
        });
        
        setTrendData(transformedDataWithCurrent);
        
        // Save to localStorage immediately with new data
        saveApiDataToStorage({ trendData: transformedData, standardRate: extractedStandardRate });
      } catch (error) {
        console.error('Error loading trend data:', error);
        setTrendDataError(error.message || 'Failed to load trend data');
        setTrendData([]);
      } finally {
        setIsLoadingTrendData(false);
      }
    };

    // Check if dataReloadTrigger has changed (indicating a forced reload)
    const reloadTriggered = dataReloadTrigger !== previousDataReloadTriggerRef.current;
    previousDataReloadTriggerRef.current = dataReloadTrigger;

    // Only auto-fetch if not already loaded from localStorage
    // But also check that the voucher and times match to avoid stale data
    // Skip localStorage check if dataReloadTrigger has changed (force reload)
    const savedData = localStorage.getItem('fuelAlertApiData');
    if (savedData && !reloadTriggered) {
      // Only check localStorage if dataReloadTrigger hasn't changed (not a forced reload)
      try {
        const parsed = JSON.parse(savedData);
        const assetMatches = parsed.assetCode === mockAlert.assetCode;
        const voucherMatches = parsed.voucherNo === mockAlert.voucherNo;
        const timeMatches = parsed.refuelTime === mockAlert.refuelTime;
        
        // Only use cached data if asset, voucher, and time all match
        if (assetMatches && voucherMatches && timeMatches && parsed.trendData && parsed.trendData.length > 0) {
          console.log('Using trend data from localStorage (all fields match)');
          setTrendData(parsed.trendData);
          // NOTE: Do NOT use cached standardRate - always extract fresh from API/asset model
          // This ensures KWH assets get the correct KWH rate, not cached hours rate
          // The standard rate should come from mockAlert.standardRate which is set in handleSelectFullTankEvent
          if (mockAlert.standardRate) {
            console.log('Using standardRate from mockAlert:', mockAlert.standardRate);
            setStandardRate(mockAlert.standardRate);
          } else if (parsed.standardRate) {
            // Fallback to cached only if mockAlert doesn't have it
            // But log a warning
            console.warn('Using cached standardRate from localStorage (may be stale for KWH assets):', parsed.standardRate);
            setStandardRate(parsed.standardRate);
          }
          return;
        } else {
          console.log('LocalStorage trend data does not match current selection, fetching fresh data');
          // Clear stale localStorage data if it doesn't match
          if (!assetMatches || !voucherMatches || !timeMatches) {
            localStorage.removeItem('fuelAlertApiData');
          }
        }
      } catch (error) {
        console.error('Error loading trend data from localStorage:', error);
      }
    } else if (reloadTriggered) {
      // Force reload when dataReloadTrigger has changed
      console.log('Data reload triggered, fetching fresh trend data');
    }
    
    loadTrendData();
  }, [mockAlert.assetCode, mockAlert.refuelTime, mockAlert.voucherNo, dataReloadTrigger, trendPeriod]); // Re-run when asset code, refuel time, voucher, reload trigger, or trendPeriod changes

  // Fetch all fuel entries between last full tank and current full tank - re-run when mockAlert changes
  useEffect(() => {
    if (!mockAlert.assetCode || !mockAlert.lastFullTankTime || !mockAlert.refuelTime || !mockAlert.voucherNo) return;
    
    const loadFuelEntries = async () => {
      setIsLoadingFuelEntries(true);
      setFuelEntriesError(null);

      try {
        const result = await fetchAllFuelEntriesBetweenFullTanks(
          mockAlert.assetCode,
          mockAlert.lastFullTankTime,
          mockAlert.refuelTime, // (current refuel event)
          mockAlert.voucherNo // (current refuel voucher number)
        );

        if (!result.success) {
          setFuelEntriesError(result.error || 'Failed to fetch fuel entries');
          setFuelEntries([]);
          setIsLoadingFuelEntries(false);
          return;
        }

        // Transform API data to table format
        let transformedEntries = result.entries.map(transformFuelEntryForTable);
        
        // Verify current refuel event is included
        const currentRefuelTime = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');
        const hasCurrentRefuel = transformedEntries.some(entry => {
          const entryTime = moment(entry.dateTime, 'YYYY-MM-DD HH:mm:ss');
          // Check if entry is within 1 minute of current refuel time
          return entryTime.isSame(currentRefuelTime, 'minute');
        });

        if (!hasCurrentRefuel) {
          console.warn('Current refuel event not found in API response. It should be included in the date range.');
        }

        console.log(`Loaded ${transformedEntries.length} fuel entries, including current refuel: ${hasCurrentRefuel}`);
        setFuelEntries(transformedEntries);
        
        // Save to localStorage immediately with new data
        saveApiDataToStorage({ fuelEntries: transformedEntries });
      } catch (error) {
        console.error('Error loading fuel entries:', error);
        setFuelEntriesError(error.message || 'Failed to load fuel entries');
        setFuelEntries([]);
      } finally {
        setIsLoadingFuelEntries(false);
      }
    };

    // Only auto-fetch if not already loaded from localStorage
    // But also check that the voucher and times match to avoid stale data
    const savedData = localStorage.getItem('fuelAlertApiData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const assetMatches = parsed.assetCode === mockAlert.assetCode;
        const voucherMatches = parsed.voucherNo === mockAlert.voucherNo;
        const timeMatches = parsed.refuelTime === mockAlert.refuelTime && 
                           parsed.lastFullTankTime === mockAlert.lastFullTankTime;
        
        // Only use cached data if asset, voucher, and times all match
        if (assetMatches && voucherMatches && timeMatches && parsed.fuelEntries && parsed.fuelEntries.length > 0) {
          console.log('Using fuel entries from localStorage (all fields match)');
          setFuelEntries(parsed.fuelEntries);
          return;
        } else {
          console.log('LocalStorage data does not match current selection, fetching fresh data');
          // Clear stale localStorage data
          localStorage.removeItem('fuelAlertApiData');
        }
      } catch (error) {
        console.error('Error loading fuel entries from localStorage:', error);
      }
    }
    
    loadFuelEntries();
  }, [mockAlert.assetCode, mockAlert.lastFullTankTime, mockAlert.refuelTime, mockAlert.voucherNo, dataReloadTrigger]); // Re-run when relevant mockAlert fields or reload trigger changes

  // Function to load IoT refuel events (can be called manually)
  const loadIotRefuelEvents = async (forceReload = false) => {
    if (!mockAlert.assetCode || !mockAlert.refuelTime || !mockAlert.lastFullTankTime) {
      console.warn('Cannot load IoT refuel events: missing required mockAlert data');
      return;
    }

    // Check if already loaded for the same asset and period (unless forcing reload)
    if (!forceReload && iotRefuelEvents.length > 0 && !iotEventsError) {
      const isSameAsset = iotEventsMetadata.assetCode === mockAlert.assetCode;
      const isSameRefuelTime = iotEventsMetadata.refuelTime === mockAlert.refuelTime;
      const isSameLastFullTankTime = iotEventsMetadata.lastFullTankTime === mockAlert.lastFullTankTime;
      
      if (isSameAsset && isSameRefuelTime && isSameLastFullTankTime) {
        console.log('IoT refuel events already loaded for same asset and period, skipping fetch');
        return;
      } else {
        console.log('IoT refuel events exist but for different asset/period, fetching new data');
      }
    }

    setIsLoadingIotEvents(true);
    setIotEventsError(null);

    try {
      // Pass selectedAssetId to avoid an extra API call to /asset/asset-all
      const result = await fetchIotRefuelEvents(
        mockAlert.assetCode,
        mockAlert.refuelTime, // (current refuel event)
        mockAlert.lastFullTankTime, // (last full tank event)
        selectedAssetId // Pass already-known asset ID to skip lookup
      );

      if (!result.success) {
        setIotEventsError(result.error || 'Failed to fetch IoT refuel events');
        // Don't set to empty array - let it fall back to mock data
        setIsLoadingIotEvents(false);
        return;
      }

      // Get manual fuel quantities for comparison
      const currentManualQuantity = mockAlert.fuelAdded || null;
      
      // Find previous event's fuel quantity from trendData (consumption trend - primary source)
      let previousManualQuantity = null;
      if (trendData.length > 0) {
        // Try to find the event that matches lastFullTankTime
        // First, try exact time match (within 1 minute)
        let previousEvent = trendData.find(e => {
          if (!e.refuelTime) return false;
          const eventTime = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
          const lastFullTankTime = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
          return eventTime.isSame(lastFullTankTime, 'minute');
        });
        
        // If not found by time, try matching by date (same day) - take the closest one
        if (!previousEvent) {
          const lastFullTankMoment = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
          const lastFullTankDate = lastFullTankMoment.format('YYYY-MM-DD');
          
          // Find all events on the same date
          const sameDayEvents = trendData.filter(e => {
            if (!e.refuelTime) return false;
            const eventDate = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD');
            return eventDate === lastFullTankDate;
          });
          
          if (sameDayEvents.length > 0) {
            // Find the event closest in time to lastFullTankTime
            previousEvent = sameDayEvents.reduce((closest, current) => {
              const currentTime = moment(current.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
              const closestTime = moment(closest.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
              const currentDiff = Math.abs(currentTime.diff(lastFullTankMoment, 'minutes'));
              const closestDiff = Math.abs(closestTime.diff(lastFullTankMoment, 'minutes'));
              return currentDiff < closestDiff ? current : closest;
            });
          }
        }
        
        if (previousEvent) {
          // Use fullTankFuel (fuel at that event only) for IoT matching, not fuelAdded (total consumed)
          previousManualQuantity = previousEvent.fullTankFuel || previousEvent.fuelAdded || null;
          console.log(`Found previous full tank fuel quantity from trendData: ${previousManualQuantity}L (from event at ${previousEvent.refuelTime})`);
        }
      }
      
      // If not found in trendData, try fuelEntries as fallback
      if (!previousManualQuantity && fuelEntries.length > 0) {
        const previousEntry = fuelEntries.find(e => {
          const entryTime = moment(`${e.date} ${e.time}`, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
          const lastFullTankTime = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
          return entryTime.isSame(lastFullTankTime, 'minute') && e.isFullTank;
        });
        if (previousEntry && previousEntry.quantity) {
          previousManualQuantity = previousEntry.quantity;
          console.log(`Found previous full tank fuel quantity from fuelEntries: ${previousManualQuantity}L`);
        }
      }
      
      if (!previousManualQuantity) {
        console.warn('Could not find previous full tank fuel quantity from trendData or fuelEntries');
      }

      // Transform API events to display format with manual quantities for confidence calculation
      let transformedEvents = result.events.map(event => {
        // Determine which manual quantity to compare with based on event time
        const eventMoment = moment(event.timestamp || event.date);
        const lastFullTankMoment = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss');
        const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');
        
        // Check if event is closer to start (last full tank) or end (current refuel)
        const distToStart = Math.abs(eventMoment.diff(lastFullTankMoment, 'hours'));
        const distToEnd = Math.abs(eventMoment.diff(currentRefuelMoment, 'hours'));
        
        // Use the manual quantity for the event it's closer to
        const manualQuantity = distToStart < distToEnd ? previousManualQuantity : currentManualQuantity;
        
        return transformIotRefuelEvent(event, manualQuantity);
      });

      // Determine relatedTo field based on dates
      const lastFullTankMoment = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss');
      const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');

      transformedEvents = transformedEvents.map(event => {
        const eventMoment = event.timeMoment;
        // Determine if event is closer to start (last full tank) or end (current refuel)
        const distToStart = Math.abs(eventMoment.diff(lastFullTankMoment, 'hours'));
        const distToEnd = Math.abs(eventMoment.diff(currentRefuelMoment, 'hours'));
        
        return {
          ...event,
          relatedTo: distToStart < distToEnd ? 'start' : 'end',
        };
      });

      setIotRefuelEvents(transformedEvents);
      
      // Update metadata to track what data we have loaded
      const newMetadata = {
        assetCode: mockAlert.assetCode,
        refuelTime: mockAlert.refuelTime,
        lastFullTankTime: mockAlert.lastFullTankTime,
      };
      setIotEventsMetadata(newMetadata);
      
      console.log(`Loaded ${transformedEvents.length} IoT refuel events for asset ${mockAlert.assetCode}`);
      
      // Save to localStorage immediately with new data and metadata
      saveApiDataToStorage({ 
        iotRefuelEvents: transformedEvents,
        iotEventsMetadata: newMetadata,
      });
    } catch (error) {
      console.error('Error loading IoT refuel events:', error);
      setIotEventsError(error.message || 'Failed to load IoT refuel events');
      // Don't set to empty array - let it fall back to mock data
    } finally {
      setIsLoadingIotEvents(false);
    }
  };

  // Clear cached IoT refuel events when asset or fuel entry dates change
  // This ensures stale data from a different asset/period is not shown
  useEffect(() => {
    if (!mockAlert.assetCode || !mockAlert.refuelTime || !mockAlert.lastFullTankTime) {
      return;
    }
    
    // Check if the cached data matches the current mockAlert
    const isSameAsset = iotEventsMetadata.assetCode === mockAlert.assetCode;
    const isSameRefuelTime = iotEventsMetadata.refuelTime === mockAlert.refuelTime;
    const isSameLastFullTankTime = iotEventsMetadata.lastFullTankTime === mockAlert.lastFullTankTime;
    
    // If any of these don't match and we have cached events, clear them
    if (iotRefuelEvents.length > 0 && (!isSameAsset || !isSameRefuelTime || !isSameLastFullTankTime)) {
      console.log('Clearing cached IoT refuel events - asset/dates changed');
      console.log(`  Previous: ${iotEventsMetadata.assetCode} | ${iotEventsMetadata.refuelTime} | ${iotEventsMetadata.lastFullTankTime}`);
      console.log(`  Current: ${mockAlert.assetCode} | ${mockAlert.refuelTime} | ${mockAlert.lastFullTankTime}`);
      
      // Clear the cached events and metadata
      setIotRefuelEvents([]);
      setIotEventsMetadata({
        assetCode: null,
        refuelTime: null,
        lastFullTankTime: null,
      });
      setIotEventsError(null);
    }
  }, [mockAlert.assetCode, mockAlert.refuelTime, mockAlert.lastFullTankTime]);

  // Fetch IoT sensor detected refuel events - re-run when mockAlert changes
  useEffect(() => {
    // Only auto-fetch if not already loaded for the current asset/period
    if (iotRefuelEvents.length === 0) {
      loadIotRefuelEvents();
    }
  }, [mockAlert.assetCode, mockAlert.refuelTime, mockAlert.lastFullTankTime, dataReloadTrigger]); // Re-run when relevant mockAlert fields or reload trigger changes

  // Fetch IoT consumption data for selected period (7/14/30 days)
  useEffect(() => {
    if (!mockAlert.assetCode || !mockAlert.refuelTime) {
      console.log('IoT Consumption: Skipping load - missing assetCode or refuelTime', {
        assetCode: mockAlert.assetCode,
        refuelTime: mockAlert.refuelTime
      });
      return;
    }
    
    console.log('IoT Consumption: Starting to load data', {
      assetCode: mockAlert.assetCode,
      refuelTime: mockAlert.refuelTime,
      period: iotConsumptionPeriod
    });
    
    const loadIotConsumptionData = async () => {
      setIsLoadingIotConsumption(true);
      
      try {
        // Calculate date range based on selected period
        const endMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
        const days = parseInt(iotConsumptionPeriod, 10);
        const startMoment = endMoment.clone().subtract(days, 'days');
        
        // For IoT Consumption Rate Analysis, we only need daily data
        // Use daily summary API only (no need for period API for partial days)
        // Find asset ID first
        const assetResult = await findAssetIdByCode(mockAlert.assetCode);
        if (!assetResult.success) {
          console.error('Failed to find asset ID:', assetResult.error);
          setIotConsumptionData([]);
          setIsLoadingIotConsumption(false);
          return;
        }
        
        // Calculate date range for full days only (start of first day to end of last day)
        const startOfFirstDay = startMoment.clone().startOf('day');
        const endOfLastDay = endMoment.clone().endOf('day');
        
        // Convert to UTC timestamps for API
        const startTimestamp = startOfFirstDay.clone().utc().valueOf();
        const endTimestamp = endOfLastDay.clone().utc().valueOf();
        
        // Fetch daily summaries only (no period API needed)
        const summaryResult = await fetchDailyIotSummary(assetResult.assetId, startTimestamp, endTimestamp);
        
        if (!summaryResult.success) {
          console.error('Failed to fetch daily IoT summaries:', summaryResult.error);
          message.warning(`Failed to fetch IoT consumption data: ${summaryResult.error || 'Unknown error'}`);
          setIotConsumptionData([]);
          setIsLoadingIotConsumption(false);
          return;
        }
        
        if (!summaryResult.data || summaryResult.data.length === 0) {
          console.warn('Daily IoT summaries returned empty data', {
            assetId: assetResult.assetId,
            startTimestamp,
            endTimestamp,
            startDate: moment(startTimestamp).format('YYYY-MM-DD'),
            endDate: moment(endTimestamp).format('YYYY-MM-DD')
          });
          message.info('No IoT consumption data available for the selected period');
          setIotConsumptionData([]);
          setIsLoadingIotConsumption(false);
          return;
        }
        
        // Helper function to convert milliseconds to time string (HH:MM:SS)
        const msToTimeString = (ms) => {
          if (ms === null || ms === undefined) return null;
          const totalSeconds = Math.floor(ms / 1000);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        };
        
        // Process daily summaries directly to dayWiseData format
        const dayWiseData = summaryResult.data.map((summary) => {
          const processed = summary.deviceProcessedData || summary;
          const newConsumed = processed.newConsumed || {};
          
          // Extract date from summary
          let dateString = null;
          const dateTimestamp = summary.date_timestamp || processed.date_timestamp;
          if (dateTimestamp) {
            const utcMoment = moment.utc(dateTimestamp);
            const istMoment = utcMoment.clone().utcOffset('+05:30');
            dateString = istMoment.format('YYYY-MM-DD');
          } else {
            const rawDate = summary.date || processed.date;
            if (rawDate) {
              const parsedDate = moment.utc(rawDate);
              if (parsedDate.isValid()) {
                const istMoment = parsedDate.clone().utcOffset('+05:30');
                dateString = istMoment.format('YYYY-MM-DD');
              }
            }
          }
          
          if (!dateString) {
            return null; // Skip if no valid date
          }
          
          // Extract data
          const movingTime = processed.moving_time || 0;
          const idealTime = processed.ideal_time || 0;
          const ignitionTime = processed.ignition_time || 0;
          const distanceTravelledMeters = processed.distance_travelled || 0;
          const distanceTravelled = distanceTravelledMeters / 1000; // Convert meters to km
          const fuelConsumed = newConsumed.fuel_consumed || processed.fuel_consumed || 0;
          const movingHours = movingTime / (1000 * 60 * 60);
          const avgSpeedFromSummary = processed.average_speed ?? processed.avg_speed;
          const averageSpeed = avgSpeedFromSummary !== undefined && avgSpeedFromSummary !== null && !isNaN(avgSpeedFromSummary)
            ? Number(avgSpeedFromSummary)
            : (movingHours > 0 && distanceTravelled > 0 ? (distanceTravelled / movingHours) : null);
          
          return {
            date: dateString,
            isFullDay: true,
            dataSource: 'day_summary',
            movingTime: movingTime > 0 ? msToTimeString(movingTime) : null,
            pauseTime: idealTime > 0 ? msToTimeString(idealTime) : null,
            ignitionTime: ignitionTime > 0 ? msToTimeString(ignitionTime) : null,
            stopTime: ignitionTime > 0 && movingTime > 0 && idealTime > 0 ? msToTimeString(ignitionTime - movingTime - idealTime) : null,
            distanceTravelled: distanceTravelled > 0 ? distanceTravelled : null,
            fuelConsumed: fuelConsumed > 0 ? fuelConsumed : null,
            possibleRefuel: (summary.total_refuel || processed.total_refuel || newConsumed.total_refuel) ? Math.abs(summary.total_refuel || processed.total_refuel || newConsumed.total_refuel) : null,
            possibleDrain: (summary.total_theft || processed.total_theft || newConsumed.total_theft) || null,
            avgFuelLtrPerHr: ignitionTime > 0 && fuelConsumed > 0 ? (fuelConsumed / (ignitionTime / (1000 * 60 * 60))).toFixed(2) : null,
            avgFuelKmPerL: fuelConsumed > 0 && distanceTravelled > 0 ? (distanceTravelled / fuelConsumed).toFixed(2) : null,
            averageSpeed: averageSpeed,
          };
        }).filter(day => day !== null);
        
        if (!dayWiseData || dayWiseData.length === 0) {
          console.error('No dayWiseData available');
          setIotConsumptionData([]);
          setIsLoadingIotConsumption(false);
          return;
        }
        
        // Helper function to convert time string (HH:MM:SS) to hours
        const timeStringToHours = (timeStr) => {
          if (!timeStr) return null;
          const parts = timeStr.split(':');
          if (parts.length !== 3) return null;
          const hours = parseInt(parts[0], 10);
          const minutes = parseInt(parts[1], 10);
          const seconds = parseInt(parts[2], 10);
          if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
          return hours + (minutes / 60) + (seconds / 3600);
        };
        
        // Process dayWiseData to calculate daily consumption rates
        const processedData = dayWiseData.map(day => {
          let consumptionRate = null;
          let unit = '';
          
          // Determine unit based on standard unit format
          const isKmBased = mockAlert.hasKms;
          const isHoursBased = mockAlert.hasHours;
          const isKwhBased = mockAlert.hasKwh;
          
          // Get standard unit format
          const standardUnit = mockAlert.standardUnit || '';
          const isLPerKm = standardUnit === 'L_PER_KM' || standardUnit.includes('L_PER_KM');
          const isKmPerL = standardUnit === 'KM_PER_L' || standardUnit.includes('KM_PER_L') || (!isLPerKm && isKmBased);
          const isLPerHour = standardUnit === 'L_PER_HOUR' || standardUnit === 'LTR_PER_HOUR' || standardUnit.includes('HOUR');
          const isHourPerL = standardUnit === 'HOUR_PER_L' || standardUnit === 'HOURS_PER_L';
          
          // Calculate consumption rate based on standard unit format
          // Priority: KM > KWH > HOURS (but fallback to hours if KWH data not available)
          // Note: Even if standard is defined as L/KM in API, we display as KM/L (converted)
          if (isKmBased && day.distanceTravelled && day.fuelConsumed && day.fuelConsumed > 0) {
            // Always calculate as KM/L for display (standard rate will be converted from L/KM to KM/L if needed)
            consumptionRate = day.distanceTravelled / day.fuelConsumed;
            unit = 'KM/L';
          } else if (isKwhBased) {
            // For KWH-based assets, try KWH first, fallback to hours if KWH data not available
            if (day.kwhRun && day.fuelConsumed && day.fuelConsumed > 0) {
              // KWH data available: calculate as KWH/L
              consumptionRate = day.kwhRun / day.fuelConsumed;
              unit = 'KWH/L';
            } else if (day.ignitionTime && day.fuelConsumed && day.fuelConsumed > 0) {
              // KWH data not available, fallback to hours-based calculation
              const hours = timeStringToHours(day.ignitionTime); // Convert time string to hours
              if (hours && hours > 0) {
                if (isLPerHour || (!isHourPerL && !isLPerHour)) {
                  // Standard is L/Hours, calculate as L/Hours
                  consumptionRate = day.fuelConsumed / hours;
                  unit = 'L/Hours';
                } else {
                  // Standard is Hours/L, calculate as Hours/L
                  consumptionRate = hours / day.fuelConsumed;
                  unit = 'Hours/L';
                }
              }
            }
          } else if (isHoursBased && day.ignitionTime && day.fuelConsumed && day.fuelConsumed > 0) {
            const hours = timeStringToHours(day.ignitionTime); // Convert time string to hours
            if (hours && hours > 0) {
              if (isLPerHour || (!isHourPerL && !isLPerHour)) {
                // Standard is L/Hours, calculate as L/Hours
                consumptionRate = day.fuelConsumed / hours;
                unit = 'L/Hours';
              } else {
                // Standard is Hours/L, calculate as Hours/L
                consumptionRate = hours / day.fuelConsumed;
                unit = 'Hours/L';
              }
            }
          }
          
          return {
            date: day.date || day.dateStr || '',
            consumptionRate: consumptionRate,
            unit: unit,
            fuelConsumed: day.fuelConsumed || 0,
            possibleRefuel: day.possibleRefuel ?? null,
            possibleDrain: day.possibleDrain ?? null,
            distanceTravelled: day.distanceTravelled || null,
            averageSpeed: day.averageSpeed ?? null,
            totalIgnitionTime: day.ignitionTime ? timeStringToHours(day.ignitionTime) * 60 : null, // Convert to minutes for display
            kwhRun: day.kwhRun || null,
          };
        }).filter(day => day.consumptionRate !== null) // Only include days with valid consumption rate
        .sort((a, b) => {
          // Sort by date in descending order (newest first)
          return moment(b.date).valueOf() - moment(a.date).valueOf();
        });
        
        setIotConsumptionData(processedData);
        console.log('IoT Consumption: Data loaded successfully', { count: processedData.length });
      } catch (error) {
        console.error('Error loading IoT consumption data:', error);
        setIotConsumptionData([]);
      } finally {
        setIsLoadingIotConsumption(false);
      }
    };
    
    loadIotConsumptionData();
  }, [mockAlert.assetCode, mockAlert.refuelTime, iotConsumptionPeriod, mockAlert.hasKms, mockAlert.hasHours, mockAlert.hasKwh, mockAlert.standardUnit, dataReloadTrigger]);

  // Fetch IoT Device Status for the asset
  const fetchIotDeviceStatus = async (assetId) => {
    if (!assetId) {
      console.log('IoT Device Status: Skipping - no asset ID');
      return;
    }
    
    setIotDeviceStatus(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      console.log('Fetching asset details for IoT device status, assetId:', assetId);
      
      // Step 1: Fetch asset details to get tagDeviceId from dependents
      const assetResponse = await apiGet(`/asset/${assetId}`);
      
      if (!assetResponse.success || !assetResponse.data) {
        throw new Error('Failed to fetch asset details');
      }
      
      const assetData = assetResponse.data;
      
      // Find tagDeviceId from dependents (GPS IOT Device with isTagDevice: true)
      let tagDeviceId = null;
      let hasFuelSensor = false;
      
      if (assetData.dependents && Array.isArray(assetData.dependents)) {
        for (const dependent of assetData.dependents) {
          // Check if this dependent is a tag device (GPS IOT Device)
          if (dependent.assetModel?.isTagDevice === true || 
              dependent.equipmentSubType?.name?.includes('GPS') ||
              dependent.equipmentSubType?.name?.includes('IOT')) {
            tagDeviceId = dependent.tagDeviceId || dependent.altName;
            
            // Check if this GPS device has a fuel sensor dependent
            if (dependent.dependents && Array.isArray(dependent.dependents)) {
              for (const subDependent of dependent.dependents) {
                if (subDependent.assetModel?.isFuelSensorDevice === true ||
                    subDependent.equipmentSubType?.name?.includes('Fuel Sensor')) {
                  hasFuelSensor = true;
                  break;
                }
              }
            }
            break;
          }
        }
      }
      
      // Also check top-level tagDeviceId
      if (!tagDeviceId && assetData.tagDeviceId) {
        tagDeviceId = assetData.tagDeviceId;
      }
      
      if (!tagDeviceId) {
        console.log('No GPS/IoT device found for this asset');
        setIotDeviceStatus({
          hasLiveSensor: false,
          tagDeviceId: null,
          lastDataTimestamp: null,
          lastDataReceiptTime: null,
          isLoading: false,
          error: null,
          fuelSensorConnected: false,
        });
        return;
      }
      
      console.log('Found tagDeviceId:', tagDeviceId, 'Fuel sensor:', hasFuelSensor);
      
      // Step 2: Fetch IoT device data using tagDeviceId
      const iotDataResponse = await apiGet(`/iot-device-data/${tagDeviceId}/${assetId}`);
      
      if (!iotDataResponse.success || !iotDataResponse.data) {
        console.log('No IoT device data available');
        setIotDeviceStatus({
          hasLiveSensor: true,
          tagDeviceId: tagDeviceId,
          lastDataTimestamp: null,
          lastDataReceiptTime: null,
          isLoading: false,
          error: 'No recent data from device',
          fuelSensorConnected: hasFuelSensor,
        });
        return;
      }
      
      const iotData = iotDataResponse.data;
      
      // Extract timestamp (device timestamp - when data was recorded) and incoming_time (server receipt time)
      // Note: tts and incoming_time are both server receipt times, timestamp is the actual device time
      const lastDataTimestamp = iotData.timestamp || null; // Device timestamp
      const lastDataReceiptTime = iotData.incoming_time || iotData.tts || null; // Server receipt time
      
      console.log('IoT Device Data:', {
        tagDeviceId,
        lastDataTimestamp,
        lastDataReceiptTime,
        hasFuelSensor
      });
      
      setIotDeviceStatus({
        hasLiveSensor: true,
        tagDeviceId: tagDeviceId,
        lastDataTimestamp: lastDataTimestamp,
        lastDataReceiptTime: lastDataReceiptTime,
        isLoading: false,
        error: null,
        fuelSensorConnected: hasFuelSensor,
      });
      
    } catch (error) {
      console.error('Error fetching IoT device status:', error);
      setIotDeviceStatus(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch IoT device status',
      }));
    }
  };
  
  // Trigger IoT device status fetch when asset changes
  useEffect(() => {
    if (selectedAssetId) {
      fetchIotDeviceStatus(selectedAssetId);
    } else {
      // Reset IoT status when no asset selected
      setIotDeviceStatus({
        hasLiveSensor: false,
        tagDeviceId: null,
        lastDataTimestamp: null,
        lastDataReceiptTime: null,
        isLoading: false,
        error: null,
        fuelSensorConnected: false,
      });
    }
  }, [selectedAssetId, dataReloadTrigger]);

  // Fetch Alert History for the asset, scoped server-side to the creation-time
  // window ending at the refuel event under analysis (see
  // buildAlertHistoryWindowMs). The window is the query, so it is recorded in
  // state and shown in the UI alongside the results.
  const fetchAlertHistory = async () => {
    if (!mockAlert.assetCode) {
      console.log('Alert History: Skipping - no asset code');
      return;
    }

    const { window: windowMs, error: windowError } = buildAlertHistoryWindowMs(
      mockAlert.refuelTime,
      alertHistoryLookback,
      alertHistoryCustomRange
    );

    // Guard: never fall back to an unscoped fetch. Without a valid window (no
    // refuel time, or an incomplete custom range) there is nothing to query,
    // and an unscoped call would pull the entire closed-alert history and
    // present alerts that are not "past" alerts at all.
    if (!windowMs) {
      setAlertHistory([]);
      setAlertHistoryWindow(null);
      setAlertHistoryIssues({ outOfWindow: 0, missingCreatedAt: 0 });
      setAlertHistoryError(windowError || 'Alert history could not be scoped, so no alerts were loaded.');
      setIsLoadingAlertHistory(false);
      return;
    }

    const { fromMs, toMs } = windowMs;

    setIsLoadingAlertHistory(true);
    setAlertHistoryError(null);

    try {
      console.log(
        `Fetching alert history for asset: ${mockAlert.assetCode} created between`,
        new Date(fromMs).toISOString(), 'and', new Date(toMs).toISOString()
      );

      // Both lifecycle endpoints, same window, issued together. They are
      // independent queries, so one failing must not discard the other's
      // results - but it must also not be hidden, or a partial list would read
      // as the asset's complete history. Each source's outcome is tracked.
      const responses = await Promise.all(
        ALERT_HISTORY_SOURCES.map(source =>
          apiGet(`${source.path}?from=${fromMs}&to=${toMs}`)
            .then(response => ({ source, response }))
            .catch(error => ({ source, response: { success: false, error: error.message } }))
        )
      );

      const failedSources = [];
      const loadedSources = [];
      const collected = [];

      responses.forEach(({ source, response }) => {
        if (!response || !response.success || !Array.isArray(response.data)) {
          failedSources.push({
            label: source.label,
            error: (response && response.error) || 'request failed',
          });
          return;
        }
        loadedSources.push(source.label);
        // The server has already scoped by creation time; what remains is this
        // screen's own scope: this asset, and fuel alert types only.
        response.data.forEach(alert => {
          const asset = alert.asset || alert.entity || {};
          if ((asset.assetCode || '') !== mockAlert.assetCode) return;

          // Fuel-related alerts only (Full Tank Mileage and IoT Based Mileage)
          const alertType = alert.alertType || '';
          if (alertType !== 'FULL_TANK_MILEAGE_ALERT' && alertType !== 'IOT_BASED_MILEAGE_ALERT') return;

          // Stamp the lifecycle from the endpoint that returned the record, and
          // resolve the effective status once here rather than on every render.
          // `currentStatus` is left untouched so the raw API value stays available.
          collected.push({
            ...alert,
            _lifecycle: source.lifecycle,
            _status: getEffectiveAlertStatus(alert),
          });
        });
      });

      // Both endpoints down: there is nothing to show and no honest partial
      // view, so this is an error rather than an empty history.
      if (loadedSources.length === 0) {
        setAlertHistory([]);
        setAlertHistoryWindow(null);
        setAlertHistoryIssues({ outOfWindow: 0, missingCreatedAt: 0, duplicateAcrossSets: 0 });
        setAlertHistoryError(
          `Failed to fetch alert history: ${failedSources.map(f => `${f.label} alerts (${f.error})`).join('; ')}`
        );
        setIsLoadingAlertHistory(false);
        return;
      }

      // Data-quality accounting. Records without a creation timestamp cannot
      // be placed relative to the refuel event, so they are excluded from the
      // history - but the count is surfaced rather than dropped silently.
      // Records outside the requested window (the server should not return
      // any) are kept and counted, so the stated window stays truthful.
      let missingCreatedAt = 0;
      let outOfWindow = 0;
      let duplicateAcrossSets = 0;
      const datedAlerts = [];
      const seenIdentities = new Map();

      collected.forEach(alert => {
        const createdMs = getAlertCreatedMs(alert);
        if (createdMs === null) {
          missingCreatedAt += 1;
          return;
        }
        if (createdMs < fromMs || createdMs >= toMs) {
          outOfWindow += 1;
        }

        // An alert closed between the two requests can come back from both
        // endpoints. Keep the CLOSED copy - it is the later state - and count
        // the collision rather than letting the row appear twice.
        const identity = getAlertIdentity(alert);
        if (identity !== null && seenIdentities.has(identity)) {
          duplicateAcrossSets += 1;
          const existing = seenIdentities.get(identity);
          if (alert._lifecycle === ALERT_LIFECYCLE.CLOSED) {
            existing.alert = alert;
          }
          return;
        }

        const entry = { alert, createdMs };
        if (identity !== null) seenIdentities.set(identity, entry);
        datedAlerts.push(entry);
      });

      // Sort by creation date descending (newest first), using the same
      // timestamp the server filters on.
      datedAlerts.sort((a, b) => b.createdMs - a.createdMs);
      const filteredAlerts = datedAlerts.map(entry => entry.alert);

      console.log(
        `Found ${filteredAlerts.length} fuel alerts for asset ${mockAlert.assetCode} ` +
        `(loaded: ${loadedSources.join(', ')}${failedSources.length ? `; failed: ${failedSources.map(f => f.label).join(', ')}` : ''})`
      );

      setAlertHistory(filteredAlerts);
      setAlertHistoryWindow({
        fromMs,
        toMs,
        label: windowMs.label,
        isCustom: windowMs.isCustom,
        clampedToRefuel: windowMs.clampedToRefuel,
        loadedSources,
        failedSources,
      });
      setAlertHistoryIssues({ outOfWindow, missingCreatedAt, duplicateAcrossSets });
      setAlertHistoryError(null);
      setIsLoadingAlertHistory(false);
    } catch (error) {
      console.error('Error fetching alert history:', error);
      setAlertHistoryError(error.message || 'Failed to fetch alert history');
      setIsLoadingAlertHistory(false);
    }
  };
  
  // Identity of the custom range as a plain string, so the effect below reacts
  // to the dates chosen rather than to the moment objects' identity (a new
  // array on every render would refetch continuously).
  const alertHistoryCustomRangeKey = useMemo(() => {
    if (alertHistoryLookback !== ALERT_HISTORY_CUSTOM_LOOKBACK) return '';
    const [from, to] = Array.isArray(alertHistoryCustomRange) ? alertHistoryCustomRange : [];
    if (!from || !to) return 'incomplete';
    return `${moment(from).format('YYYY-MM-DD')}..${moment(to).format('YYYY-MM-DD')}`;
  }, [alertHistoryLookback, alertHistoryCustomRange]);

  // Load alert history when the queried scope changes: the asset, the refuel
  // event (upper bound of the window), the selected lookback or custom range
  // (lower bound), or an explicit data reload. The key guard keeps this to
  // exactly one request per distinct scope - re-renders and unrelated state
  // changes do not refetch.
  useEffect(() => {
    if (!mockAlert.assetCode) {
      alertHistoryFetchKeyRef.current = null;
      return;
    }

    const fetchKey = [
      mockAlert.assetCode,
      mockAlert.refuelTime || '',
      alertHistoryLookback,
      alertHistoryCustomRangeKey,
      dataReloadTrigger,
    ].join('|');

    if (alertHistoryFetchKeyRef.current === fetchKey) {
      return;
    }
    alertHistoryFetchKeyRef.current = fetchKey;

    fetchAlertHistory();
  }, [mockAlert.assetCode, mockAlert.refuelTime, alertHistoryLookback, alertHistoryCustomRangeKey, dataReloadTrigger]);
  
  // Helper functions for alert history (matching ClosedAlertsReport style)
  const getAlertTypeLabel = (alertType) => {
    const typeMap = {
      "FULL_TANK_MILEAGE_ALERT": "Full Tank to Full Tank Mileage",
      "IOT_BASED_MILEAGE_ALERT": "IoT Based Mileage",
      "DOCUMENT_RENEWAL_ALERT": "Document Renewal",
      "ASSET_AUDIT_DUE_ALERT": "Asset Audit Due",
      "MANPOWER_ENTRY_PENDING_ALERT": "Manpower Entry Pending"
    };
    return typeMap[alertType] || alertType;
  };
  
  const getAlertTypeColor = (alertType) => {
    const colorMap = {
      "FULL_TANK_MILEAGE_ALERT": { bg: "#fff4e6", color: "#d46b08" },
      "IOT_BASED_MILEAGE_ALERT": { bg: "#f6ffed", color: "#389e0d" },
      "DOCUMENT_RENEWAL_ALERT": { bg: "#e9f2ff", color: "#0747a6" },
      "ASSET_AUDIT_DUE_ALERT": { bg: "#fff0f6", color: "#c41d7f" },
      "MANPOWER_ENTRY_PENDING_ALERT": { bg: "#f0f5ff", color: "#2f54eb" }
    };
    return colorMap[alertType] || { bg: "#f4f5f7", color: "#42526e" };
  };
  
  const getSeverityInfo = (severity) => {
    const severityMap = {
      "HIGH": { label: "High", color: "red" },
      "CRITICAL": { label: "Critical", color: "red" },
      "MEDIUM": { label: "Medium", color: "orange" },
      "LOW": { label: "Low", color: "blue" }
    };
    return severityMap[severity] || { label: severity, color: undefined };
  };
  
  const getStatusInfo = (status) => {
    const statusMap = {
      "NEW": { label: "New", color: "blue" },
      "UNDER_ANALYSIS": { label: "Under Analysis", color: "orange" },
      "ACTION_PENDING": { label: "Action Pending", color: "gold" },
      "ACTION_TAKEN": { label: "Action Taken", color: "green" },
      // Neutral rather than "default": antd 3's Tag has no "default" preset, so
      // an unrecognised value makes it an inverse tag (white text) with an
      // invalid background-color - unreadable white-on-grey. Passing no colour
      // gives antd's own neutral tag: dark text on a light background.
      "IGNORED": { label: "Ignored", color: undefined }
    };
    return statusMap[status] || { label: status, color: undefined };
  };
  
  const extractAssetInfo = (alert) => {
    const asset = alert.asset || alert.entity || {};
    const assetModel = asset.assetModel || {};
    const location = asset.location || {};
    const client = asset.client || {};
    const equipmentSubType = asset.equipmentSubType || {};
    
    return {
      assetCode: asset.assetCode || "N/A",
      altName: asset.altName || "",
      ownerCode: asset.ownerCode || "",
      location: location.name || location.code || "N/A",
      subLocation: client.name || client.code || "",
      assetModel: assetModel.name || "",
      assetSubType: equipmentSubType.name || ""
    };
  };
  
  const extractWorkflowInfo = (alert) => {
    const statusHistory = alert.statusHistory || [];
    let analysis = "";
    let actionSuggested = "";
    let actionTaken = "";
    let closedBy = null;
    let closedAt = null;
    
    for (let i = statusHistory.length - 1; i >= 0; i--) {
      const entry = statusHistory[i];
      if (entry.analysisId) {
        if (!analysis && entry.analysisId.analysis) {
          analysis = entry.analysisId.analysis;
        }
        if (!actionSuggested && entry.analysisId.actionSuggested) {
          actionSuggested = entry.analysisId.actionSuggested;
        }
        if (!actionTaken && entry.analysisId.actionTaken) {
          actionTaken = entry.analysisId.actionTaken;
        }
      }
      if (entry.status === "ACTION_TAKEN" && entry.updatedBy) {
        closedBy = entry.updatedBy;
        closedAt = entry.date;
      }
    }
    
    return { analysis, actionSuggested, actionTaken, closedBy, closedAt };
  };
  
  // Loaded-set counts by lifecycle, for the caption and the filter labels.
  const alertHistoryCounts = useMemo(() => {
    let open = 0;
    let closed = 0;
    (alertHistory || []).forEach(alert => {
      if (alert._lifecycle === ALERT_LIFECYCLE.OPEN) open += 1;
      else if (alert._lifecycle === ALERT_LIFECYCLE.CLOSED) closed += 1;
    });
    return { open, closed, total: (alertHistory || []).length };
  }, [alertHistory]);

  // Display list: lifecycle filter first, then the "show last N" cap. Applying
  // the cap last means "Last 7 Alerts" is 7 of whatever is being shown, rather
  // than 7 of the whole set that a filter could then empty out.
  const filteredAlertHistory = useMemo(() => {
    const limit = parseInt(alertHistoryLimit, 10) || 7;
    const byLifecycle = alertHistoryLifecycle === 'ALL'
      ? alertHistory
      : (alertHistory || []).filter(alert => alert._lifecycle === alertHistoryLifecycle);
    return byLifecycle.slice(0, limit);
  }, [alertHistory, alertHistoryLimit, alertHistoryLifecycle]);

  // No mock trend data - use empty array
  const mockTrendData = [];

  // No mock IoT refuel events - use empty array
  const mockIotRefuelEvents = [];

  // No mock IoT data - use fetched data or empty structure
  const mockIotData = {
    refuelEvents: (iotRefuelEvents && iotRefuelEvents.length > 0) ? iotRefuelEvents : [],
    events: [],
    timeseries: [],
    iotMetrics: fetchedIotData?.iotMetrics || {
      fuelAdded: null,
      hoursRun: null,
      kmsRun: null,
      kwhRun: null,
    },
    iotPeriodStats: fetchedIotData?.iotPeriodStats || {
      totalMovingTime: null,
      totalMovingTimeDIN: null,
      totalPauseTime: null,
      totalPauseTimeDIN: null,
      totalIgnitionTime: null,
      totalIgnitionTimeDIN: null,
      totalStopTime: null,
      totalStopTimeDIN: null,
      distanceTravelled: null,
      fuelConsumed: null,
      possibleRefuel: null,
      possibleDrain: null,
      avgFuelConsumedKmPerLtr: null,
      avgFuelConsumedLtrPerHr: null,
      avgFuelConsumedLtrPerHrDIN: null,
      averageSpeed: null,
      kmsRun: null,
      kwhRun: null,
    },
    dayWiseData: fetchedIotData?.dayWiseData || [],
  };

  // Filter trend data based on period
  // Use API data if available, otherwise fall back to mock data
  // Data is sorted newest first (descending order)
  const filteredTrendData = useMemo(() => {
    const count = parseInt(trendPeriod, 10);
    const dataSource = trendData.length > 0 ? trendData : [];
    
    // For KM-based assets: determine if standard is in L/KM or KM/L format
    // actualRate from API is always in KM/L format
    // We need to ensure both are in the same format for deviation calculation
    const isLPerKm = mockAlert.hasKms && (
      mockAlert.standardUnit === 'L_PER_KM' || 
      (mockAlert.standardUnit && mockAlert.standardUnit.includes('L_PER_KM'))
    );
    
    // Take first N items for processing
    const slicedData = dataSource.slice(0, count);
    
    // Step 1: Mark excluded events and calculate which events need recalculation
    // When an event is excluded, the next full tank event (lower index = newer) needs to:
    // - Use meter reading from the previous actual full tank (skip excluded events)
    // - Include excluded event's fuel in its consumption
    const dataWithExclusions = slicedData.map((item, index) => {
      const isExcluded = excludedFullTankEvents[item.refuelTime] === true;
      return {
        ...item,
        isExcluded,
        originalIndex: index,
      };
    });
    
    // Step 2: Recalculate consumption for events affected by exclusions
    // Data is sorted newest first, so we iterate from oldest to newest
    const recalculatedData = dataWithExclusions.map((item, index, arr) => {
      // If this event is excluded, it shows strikethrough but no recalculation needed for itself
      if (item.isExcluded) {
        return {
          ...item,
          recalculatedFuelAdded: null,
          recalculatedRunValue: null,
          recalculatedActualRate: null,
          recalculatedDeviation: null,
        };
      }
      
      // Find the previous actual full tank event (skip excluded ones)
      // Previous = higher index (older) since data is sorted newest first
      let prevActualFullTankIndex = null;
      let accumulatedFuel = item.fuelAdded || 0;
      let accumulatedIntermediateDetails = [...(item.intermediateEntriesDetails || [])];
      
      for (let i = index + 1; i < arr.length; i++) {
        if (!arr[i].isExcluded) {
          prevActualFullTankIndex = i;
          break;
        } else {
          // Add excluded event's fuel to accumulated
          // Use fuelAdded which already includes any intermediate entries from the excluded event
          accumulatedFuel += (arr[i].fuelAdded || arr[i].fullTankFuel || 0);
          // Add excluded event to intermediate details (show only the full tank quantity)
          accumulatedIntermediateDetails.push({
            date: arr[i].refuelTime,
            voucherNo: arr[i].voucherNo || '-',
            quantity: arr[i].fullTankFuel || arr[i].fuelAdded || 0,
            isFullTank: true,
            isExcludedFullTank: true,
          });
          // Also add any intermediate entries from the excluded event to show in breakdown
          if (arr[i].intermediateEntriesDetails) {
            accumulatedIntermediateDetails.push(...arr[i].intermediateEntriesDetails);
          }
        }
      }
      
      // Check if any exclusions affect this event
      let hasExclusionsBefore = false;
      for (let i = index + 1; i < arr.length; i++) {
        if (arr[i].isExcluded) {
          hasExclusionsBefore = true;
          break;
        }
        if (!arr[i].isExcluded) {
          break; // Stop at first non-excluded event
        }
      }
      
      if (!hasExclusionsBefore) {
        // No exclusions before this event, use original values
        return item;
      }
      
      // Recalculate run value (meter reading difference)
      let recalculatedRunValue = null;
      // Runs of every named meter over the widened interval (excluded events are skipped),
      // so the secondary readings and Operating Level stay in step with the primary run.
      let recalculatedMeterRuns = null;
      if (prevActualFullTankIndex !== null) {
        const prevItem = arr[prevActualFullTankIndex];
        recalculatedMeterRuns = getMeterRunsBetween(
          item._apiEntry?.mileage,
          prevItem._apiEntry?.mileage
        );
        // Calculate run value based on asset type
        // Use calculatedReading for accuracy (handles meter resets)
        if (mockAlert.hasKms && item.odo !== null && prevItem.odo !== null) {
          recalculatedRunValue = item.odo - prevItem.odo;
        } else if (mockAlert.hasHours && item.hoursRun !== null) {
          // For hours, use the calculatedReading if available from meterReadingRecord
          const currentHoursMeter = item._apiEntry?.mileage?.find(m => 
            m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
          );
          const prevHoursMeter = prevItem._apiEntry?.mileage?.find(m => 
            m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
          );
          const currentHours = getCalculatedReading(currentHoursMeter);
          const prevHours = getCalculatedReading(prevHoursMeter);
          if (currentHours !== null && prevHours !== null) {
            recalculatedRunValue = currentHours - prevHours;
          }
        } else if (mockAlert.hasKwh && item.kwhRun !== null) {
          const currentKwhMeter = item._apiEntry?.mileage?.find(m => 
            m.name && m.name.toUpperCase().includes('KWH')
          );
          const prevKwhMeter = prevItem._apiEntry?.mileage?.find(m => 
            m.name && m.name.toUpperCase().includes('KWH')
          );
          const currentKwh = getCalculatedReading(currentKwhMeter);
          const prevKwh = getCalculatedReading(prevKwhMeter);
          if (currentKwh !== null && prevKwh !== null) {
            recalculatedRunValue = currentKwh - prevKwh;
          }
        }
      }
      
      // The priority meter's recalculated run wins for its own meter type — same rule as
      // transformToTrendData, so the row's primary reading and Operating Level agree.
      const primaryMeterType = mockAlert.hasKwh ? 'KWH' : (mockAlert.hasKms ? 'KMS' : (mockAlert.hasHours ? 'HOURS' : null));
      if (recalculatedMeterRuns && primaryMeterType && recalculatedRunValue !== null) {
        recalculatedMeterRuns[primaryMeterType] = recalculatedRunValue;
      }

      // Recalculate actual rate
      let recalculatedActualRate = null;
      if (recalculatedRunValue !== null && recalculatedRunValue > 0 && accumulatedFuel > 0) {
        if (mockAlert.hasKms) {
          recalculatedActualRate = recalculatedRunValue / accumulatedFuel;
        } else if (mockAlert.hasHours) {
          recalculatedActualRate = accumulatedFuel / recalculatedRunValue;
        } else if (mockAlert.hasKwh) {
          recalculatedActualRate = recalculatedRunValue / accumulatedFuel;
        }
      }
      
      // Recalculate deviation — use the same standard rate source as the Standard column display
      // (mockAlert.standardRate) so deviation is consistent with what the user sees.
      // Both mockAlert.standardRate and the state standardRate are pre-normalized to display
      // units (KM/L for KM assets, KWH/L for KWH, L/HOURS for HOURS) — do NOT invert here.
      let recalculatedDeviation = null;
      const authoritativeStandardRate = mockAlert.standardRate || standardRate;
      const convertedStandardRate = authoritativeStandardRate;
      if (recalculatedActualRate !== null && convertedStandardRate !== null && convertedStandardRate > 0) {
        if (mockAlert.hasKms || mockAlert.hasKwh) {
          recalculatedDeviation = ((convertedStandardRate - recalculatedActualRate) / convertedStandardRate) * 100;
        } else {
          recalculatedDeviation = ((recalculatedActualRate - convertedStandardRate) / convertedStandardRate) * 100;
        }
      }
      
      // Sort accumulated intermediate details by date
      accumulatedIntermediateDetails.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
      
      return {
        ...item,
        // Store recalculated values
        recalculatedFuelAdded: accumulatedFuel,
        recalculatedRunValue,
        recalculatedMeterRuns,
        recalculatedActualRate,
        recalculatedDeviation,
        recalculatedIntermediateDetails: accumulatedIntermediateDetails,
        hasRecalculation: true,
      };
    });
    
    // Step 3: Update rates and deviations with proper conversions
    const dataWithUpdatedRate = recalculatedData.map(item => {
      // Use recalculated values if available, otherwise use original
      const useRecalculated = item.hasRecalculation && item.recalculatedActualRate !== null;
      
      // Use mockAlert.standardRate as the source of truth — matches what the Standard column
      // displays (see column render ~L4693). Using item.standardRate here caused divergence
      // when handleSelectFullTankEvent and loadTrendData picked different rate entries or
      // when localStorage had a stale value.
      let convertedStandardRate = mockAlert.standardRate || item.standardRate || standardRate;
      let convertedActualRate = useRecalculated ? item.recalculatedActualRate : item.actualRate;
      
      // For KWH assets: recalculate actualRate from kwhRun and fuelAdded if not already recalculated
      if (!useRecalculated && mockAlert.hasKwh && item.kwhRun && item.fuelAdded && item.fuelAdded > 0) {
        convertedActualRate = item.kwhRun / item.fuelAdded;
      }
      
      // mockAlert.standardRate / item.standardRate / state standardRate are already in display
      // units (KM/L) for KM assets — no further inversion needed regardless of isLPerKm.
      
      // Calculate deviation using converted rates
      let calculatedDeviation = useRecalculated ? item.recalculatedDeviation : item.deviation;
      if (!useRecalculated && convertedActualRate !== null && convertedStandardRate !== null && convertedStandardRate > 0) {
        if (mockAlert.hasKms) {
          calculatedDeviation = ((convertedStandardRate - convertedActualRate) / convertedStandardRate) * 100;
        } else if (mockAlert.hasKwh) {
          calculatedDeviation = ((convertedStandardRate - convertedActualRate) / convertedStandardRate) * 100;
        } else {
          calculatedDeviation = ((convertedActualRate - convertedStandardRate) / convertedStandardRate) * 100;
        }
      }
      
      // Meter runs: use the recalculated set when exclusions widened this row's interval.
      // Operating Level is always derived from the same set that the row displays.
      const resolvedMeterRuns = (useRecalculated && item.recalculatedMeterRuns)
        ? item.recalculatedMeterRuns
        : getRecordMeterRuns(item);

      return {
        ...item,
        actualRate: item.isExcluded ? null : convertedActualRate,
        standardRate: standardRate || item.standardRate,
        deviation: item.isExcluded ? null : calculatedDeviation,
        meterRuns: resolvedMeterRuns,
        operatingLevel: computeOperatingLevel(resolvedMeterRuns),
        // Override fuelAdded with recalculated if available
        fuelAdded: useRecalculated ? item.recalculatedFuelAdded : item.fuelAdded,
        // Override run values with recalculated if available
        kmsRun: useRecalculated && mockAlert.hasKms ? item.recalculatedRunValue : item.kmsRun,
        hoursRun: useRecalculated && mockAlert.hasHours ? item.recalculatedRunValue : item.hoursRun,
        kwhRun: useRecalculated && mockAlert.hasKwh ? item.recalculatedRunValue : item.kwhRun,
        // Include recalculated intermediate details if available
        intermediateEntriesDetails: useRecalculated ? item.recalculatedIntermediateDetails : item.intermediateEntriesDetails,
        intermediateFuelCount: useRecalculated 
          ? (item.recalculatedIntermediateDetails?.length || 0) 
          : item.intermediateFuelCount,
      };
    });
    
    return dataWithUpdatedRate;
  }, [trendPeriod, trendData, standardRate, mockAlert.standardRate, mockAlert.hasKms, mockAlert.hasKwh, mockAlert.hasHours, mockAlert.standardUnit, excludedFullTankEvents]);

  // Single source of truth for the standard rate shown to the user, in DISPLAY units
  // (KWH/L for KWH assets, KM/L for KM, L/HOURS for hours-based).
  //
  // mockAlert.standardRate is normalized to display units by handleSelectFullTankEvent — it
  // is the value the Standard column prints. The `standardRate` state can hold a rate that
  // was extracted for a different meter on the same asset (an L/HOURS rate on a DG whose
  // standard is KWH-based), which is why the chart used to draw an L/HOURS standard line
  // against a KWH/L actual series. The table, the chart and the deviation must all read
  // this one value.
  const resolvedStandardRate = useMemo(() => {
    const rate = mockAlert.standardRate || standardRate;
    return (typeof rate === 'number' && isFinite(rate) && rate > 0) ? rate : null;
  }, [mockAlert.standardRate, standardRate]);

  // The one unit the IoT Consumption tab reports in — the same unit resolvedStandardRate is in.
  // Day rows in any other unit (a DG day with no KWH reading falls back to L/Hours) are kept
  // in the table with their own unit but are never averaged or plotted against this one.
  const iotChartUnit = mockAlert.hasKms ? 'KM/L' : (mockAlert.hasKwh ? 'KWH/L' : 'L/Hours');

  // Unit that Operating Level is expressed in for the asset currently loaded — KWH/Hour on a
  // DG, KM/Hour on a vehicle. Read off the data itself, never assumed from the asset type:
  // it is null when no row in the visible range has a derivable Operating Level, and the
  // Maximum Operating Level input is disabled in that case.
  const operatingLevelUnit = useMemo(() => {
    const rowWithUnit = (filteredTrendData || []).find(
      item => item.operatingLevel && item.operatingLevel.unitLabel
    );
    return rowWithUnit ? rowWithUnit.operatingLevel.unitLabel : null;
  }, [filteredTrendData]);

  // A maximum entered for one asset must never carry over to another — a DG's rated KWH/Hour
  // is meaningless against a vehicle's KM/Hour. Clearing on asset change keeps the operator
  // from reading a percentage against the previous asset's ceiling.
  useEffect(() => {
    setMaxOperatingLevel(null);
  }, [mockAlert.assetCode]);


  const handleStartTimeChange = (momentValue, clearEventSelection = true) => {
    if (momentValue) {
      // Ensure we have a proper moment object with time preserved
      // IMPORTANT: Treat all times as IST (GMT+5:30) since that's what the user enters
      let momentWithTime;
      if (moment.isMoment(momentValue)) {
        // DatePicker gives us a moment in browser's local timezone
        // Extract the date/time values and recreate as IST
        momentWithTime = moment(momentValue.format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
      } else if (typeof momentValue === 'string') {
        // If it's a string, parse it with the format and set as IST
        momentWithTime = moment(momentValue, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
      } else {
        momentWithTime = moment(momentValue).utcOffset('+05:30');
      }
      
      // Ensure the moment is valid and has time
      if (!momentWithTime.isValid()) {
        console.error('Invalid moment value:', momentValue);
        return;
      }
      
      const newRange = analysisRange.length === 2 
        ? [momentWithTime, analysisRange[1]]
        : [momentWithTime, moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30')];
      setAnalysisRange(newRange);
      setDataLoaded(false); // Reset data loaded state when range changes
      setFetchedIotData(null);
      if (clearEventSelection) {
        setSelectedStartEventId(null);
      }
      // In real app, trigger reload/calculation here
    }
  };

  const handleEndTimeChange = (momentValue, clearEventSelection = true) => {
    if (momentValue) {
      // Ensure we have a proper moment object with time preserved
      // IMPORTANT: Treat all times as IST (GMT+5:30) since that's what the user enters
      let momentWithTime;
      if (moment.isMoment(momentValue)) {
        // DatePicker gives us a moment in browser's local timezone
        // Extract the date/time values and recreate as IST
        momentWithTime = moment(momentValue.format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
      } else if (typeof momentValue === 'string') {
        // If it's a string, parse it with the format and set as IST
        momentWithTime = moment(momentValue, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
      } else {
        momentWithTime = moment(momentValue).utcOffset('+05:30');
      }
      
      // Ensure the moment is valid and has time
      if (!momentWithTime.isValid()) {
        console.error('Invalid moment value:', momentValue);
        return;
      }
      
      const newRange = analysisRange.length === 2 
        ? [analysisRange[0], momentWithTime]
        : [moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30'), momentWithTime];
      setAnalysisRange(newRange);
      setDataLoaded(false); // Reset data loaded state when range changes
      setFetchedIotData(null);
      if (clearEventSelection) {
        setSelectedEndEventId(null);
      }
      // In real app, trigger reload/calculation here
    }
  };

  const handleSelectStartEvent = (eventId) => {
    const event = mockIotData.refuelEvents.find(e => e.id === eventId);
    if (event) {
      // Create a new moment from the timeText string to ensure time is preserved
      // Parse the timeText which is in format 'YYYY-MM-DD HH:mm:ss'
      const eventMoment = moment(event.timeText, 'YYYY-MM-DD HH:mm:ss');
      
      // Verify the moment has time
      if (!eventMoment.isValid()) {
        console.error('Invalid moment for event:', event);
        return;
      }
      
      setSelectedStartEventId(eventId);
      handleStartTimeChange(eventMoment, false); // Don't clear event selection
      setStartEventsModalVisible(false);
    }
  };

  const handleSelectEndEvent = (eventId) => {
    const event = mockIotData.refuelEvents.find(e => e.id === eventId);
    if (event) {
      // Create a new moment from the timeText string to ensure time is preserved
      // Parse the timeText which is in format 'YYYY-MM-DD HH:mm:ss'
      const eventMoment = moment(event.timeText, 'YYYY-MM-DD HH:mm:ss');
      
      // Verify the moment has time
      if (!eventMoment.isValid()) {
        console.error('Invalid moment for event:', event);
        return;
      }
      
      setSelectedEndEventId(eventId);
      handleEndTimeChange(eventMoment, false); // Don't clear event selection
      setEndEventsModalVisible(false);
    }
  };

  // Helper function to ensure timeMoment is a moment object
  const ensureMoment = (event) => {
    if (event.timeMoment && typeof event.timeMoment.format === 'function') {
      return event.timeMoment;
    }
    // Try to convert from string or use timeText
    return moment(event.timeMoment || event.timeText || event.time);
  };

  // Get refuel events for start period
  const getStartPeriodEvents = () => {
    const lastFullTankDate = moment(mockAlert.lastFullTankTime).startOf('day');
    return mockIotData.refuelEvents.filter(event => {
      const eventMoment = ensureMoment(event);
      const eventDate = eventMoment.startOf('day');
      return (
        eventDate.isSameOrAfter(lastFullTankDate.clone().subtract(1, 'day')) &&
        eventDate.isSameOrBefore(lastFullTankDate.clone().add(1, 'day'))
      );
    });
  };

  // Get refuel events for end period
  const getEndPeriodEvents = () => {
    const currentRefuelDate = moment(mockAlert.refuelTime).startOf('day');
    return mockIotData.refuelEvents.filter(event => {
      const eventMoment = ensureMoment(event);
      const eventDate = eventMoment.startOf('day');
      return (
        eventDate.isSameOrAfter(currentRefuelDate.clone().subtract(1, 'day')) &&
        eventDate.isSameOrBefore(currentRefuelDate.clone().add(1, 'day'))
      );
    });
  };

  // Group events by day
  const groupEventsByDay = (events, targetDate) => {
    const groups = {
      previous: [],
      same: [],
      next: [],
    };
    
    events.forEach(event => {
      const eventMoment = ensureMoment(event);
      const eventDate = eventMoment.startOf('day');
      if (eventDate.isBefore(targetDate, 'day')) {
        groups.previous.push(event);
      } else if (eventDate.isSame(targetDate, 'day')) {
        groups.same.push(event);
      } else {
        groups.next.push(event);
      }
    });
    
    return groups;
  };

  // Render refuel events modal
  const renderRefuelEventsModal = (isStart, visible, onClose, onSelect) => {
    const targetDate = isStart 
      ? moment(mockAlert.lastFullTankTime).startOf('day')
      : moment(mockAlert.refuelTime).startOf('day');
    const events = isStart ? getStartPeriodEvents() : getEndPeriodEvents();
    const groups = groupEventsByDay(events, targetDate);
    const selectedEventId = isStart ? selectedStartEventId : selectedEndEventId;
    
    // Get manual fuel quantities for display
    const currentManualQuantity = mockAlert.fuelAdded || null;
    
    // Find previous event's fuel quantity from trendData (consumption trend - primary source)
    let previousManualQuantity = null;
    if (trendData.length > 0) {
      // Try to find the event that matches lastFullTankTime
      // First, try exact time match (within 1 minute)
      let previousEvent = trendData.find(e => {
        if (!e.refuelTime) return false;
        const eventTime = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
        const lastFullTankTime = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
        return eventTime.isSame(lastFullTankTime, 'minute');
      });
      
      // If not found by time, try matching by date (same day) - take the closest one
      if (!previousEvent) {
        const lastFullTankMoment = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
        const lastFullTankDate = lastFullTankMoment.format('YYYY-MM-DD');
        
        // Find all events on the same date
        const sameDayEvents = trendData.filter(e => {
          if (!e.refuelTime) return false;
          const eventDate = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD');
          return eventDate === lastFullTankDate;
        });
        
        if (sameDayEvents.length > 0) {
          // Find the event closest in time to lastFullTankTime
          previousEvent = sameDayEvents.reduce((closest, current) => {
            const currentTime = moment(current.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
            const closestTime = moment(closest.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
            const currentDiff = Math.abs(currentTime.diff(lastFullTankMoment, 'minutes'));
            const closestDiff = Math.abs(closestTime.diff(lastFullTankMoment, 'minutes'));
            return currentDiff < closestDiff ? current : closest;
          });
        }
      }
      
      if (previousEvent) {
        // Use fullTankFuel (fuel at that event only) for IoT matching, not fuelAdded (total consumed)
        previousManualQuantity = previousEvent.fullTankFuel || previousEvent.fuelAdded || null;
      }
    }
    
    // If not found in trendData, try fuelEntries as fallback
    if (!previousManualQuantity && fuelEntries.length > 0) {
      const previousEntry = fuelEntries.find(e => {
        const entryTime = moment(`${e.date} ${e.time}`, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
        const lastFullTankTime = moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
        return entryTime.isSame(lastFullTankTime, 'minute') && e.isFullTank;
      });
      if (previousEntry && previousEntry.quantity) {
        previousManualQuantity = previousEntry.quantity;
      }
    }
    
    const manualQuantity = isStart ? previousManualQuantity : currentManualQuantity;
    
    const renderEventGroup = (groupEvents, dayType) => {
      if (groupEvents.length === 0) return null;
      
      let dayLabel = '';
      let dayDate = '';
      if (dayType === 'previous') {
        dayLabel = 'Previous Day';
        dayDate = targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
      } else if (dayType === 'same') {
        dayLabel = 'Same Day';
        dayDate = targetDate.format('YYYY-MM-DD');
      } else {
        dayLabel = 'Next Day';
        dayDate = targetDate.clone().add(1, 'day').format('YYYY-MM-DD');
      }
      
      return (
        <div key={dayType} style={{ marginBottom: 16 }}>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 600, 
            marginBottom: 8,
            color: dayType === 'same' ? '#1890ff' : '#8c8c8c',
            paddingBottom: 8,
            borderBottom: '1px solid #e8e8e8'
          }}>
            {dayLabel} ({dayDate})
          </div>
          {groupEvents
            .map(event => {
              // Recalculate confidence and quantity difference based on the correct manual quantity for this modal
              // For "Starting Time" modal, use previousManualQuantity; for "Ending Time", use currentManualQuantity
              const correctManualQuantity = isStart ? previousManualQuantity : currentManualQuantity;
              
              // Recalculate if we have the correct manual quantity and it's different from what was used
              if (correctManualQuantity !== null && correctManualQuantity !== undefined && 
                  typeof correctManualQuantity === 'number' && correctManualQuantity > 0 &&
                  event.quantity > 0) {
                const quantityDifference = Math.abs(event.quantity - correctManualQuantity);
                const quantityDifferencePercent = (quantityDifference / correctManualQuantity) * 100;
                
                // Recalculate confidence
                const percentThreshold = Math.min(5, (10 / correctManualQuantity) * 100);
                const absoluteThreshold = Math.min(10, correctManualQuantity * 0.05);
                
                let confidence = 'medium';
                if (quantityDifferencePercent <= percentThreshold || quantityDifference <= absoluteThreshold) {
                  confidence = 'high';
                } else if (quantityDifferencePercent <= 15 || quantityDifference <= 30) {
                  confidence = 'medium';
                } else {
                  confidence = 'low';
                }
                
                // Return updated event with correct confidence and differences
                return {
                  ...event,
                  manualQuantity: correctManualQuantity,
                  quantityDifference: quantityDifference,
                  quantityDifferencePercent: quantityDifferencePercent,
                  confidence: confidence,
                };
              }
              
              // If no correct manual quantity, return event as-is
              return event;
            })
            .sort((a, b) => {
              // Sort by confidence (high > medium > low), then by quantity difference (lower is better)
              const confidenceOrder = { high: 3, medium: 2, low: 1 };
              const aConf = confidenceOrder[a.confidence] || 0;
              const bConf = confidenceOrder[b.confidence] || 0;
              
              if (aConf !== bConf) {
                return bConf - aConf; // Higher confidence first
              }
              
              // If same confidence, sort by quantity difference (if available)
              if (a.quantityDifference != null && b.quantityDifference != null && 
                  !isNaN(a.quantityDifference) && !isNaN(b.quantityDifference)) {
                return a.quantityDifference - b.quantityDifference; // Lower difference first
              }
              
              return 0;
            })
            .map(event => (
            <div
              key={event.id}
              onClick={() => {
                onSelect(event.id);
                onClose();
              }}
              style={{
                padding: '12px',
                marginBottom: 8,
                cursor: 'pointer',
                borderRadius: 4,
                backgroundColor: selectedEventId === event.id ? '#e6f7ff' : '#fafafa',
                border: selectedEventId === event.id ? '2px solid #1890ff' : '1px solid #e8e8e8',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (selectedEventId !== event.id) {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedEventId !== event.id) {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }
              }}
            >
              <Row type="flex" justify="space-between" align="middle">
                <Col span={16}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                    {event.timeText}
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                    {event.description}
                  </div>
                  {manualQuantity !== null && manualQuantity !== undefined && typeof manualQuantity === 'number' && (
                    <div style={{ fontSize: 11, color: '#595959', marginBottom: 4 }}>
                      Manual Entry: <strong>{manualQuantity.toFixed(2)} L</strong>
                      {event.quantityDifference !== null && event.quantityDifference !== undefined && 
                       event.quantityDifferencePercent !== null && event.quantityDifferencePercent !== undefined && (
                        <span style={{ marginLeft: 8, color: event.quantityDifferencePercent <= 5 ? '#52c41a' : event.quantityDifferencePercent <= 15 ? '#faad14' : '#ff4d4f' }}>
                          (Diff: {event.quantityDifference.toFixed(2)} L / {event.quantityDifferencePercent.toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  )}
                  <div>
                    <Tag 
                      color={
                        event.confidence === 'high' ? 'green' : 
                        event.confidence === 'medium' ? 'orange' : 'red'
                      } 
                      size="small"
                    >
                      {event.confidence} confidence
                    </Tag>
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1890ff' }}>
                    {event.quantity != null && !isNaN(event.quantity) ? event.quantity.toFixed(2) : 'N/A'} L
                  </div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    IoT Detected
                  </div>
                </Col>
              </Row>
            </div>
          ))}
        </div>
      );
    };

    return (
      <Modal
        visible={visible}
        title={
          <span>
            <Icon type="environment" style={{ marginRight: 8 }} />
            Detected Refuel Events - {isStart ? 'Starting Time' : 'Ending Time'}
          </span>
        }
        onCancel={onClose}
        footer={[
          <Button 
            key="refresh" 
            icon="reload" 
            loading={isLoadingIotEvents}
            onClick={async () => {
              await loadIotRefuelEvents(true);
            }}
          >
            Refresh
          </Button>,
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginBottom: 12 }}>
          <Alert
            type="info"
            message={
              <div>
                <div style={{ marginBottom: 4 }}>
                  <strong>Target Date:</strong> {targetDate.format('YYYY-MM-DD')}
                </div>
                <div style={{ marginBottom: 4, fontSize: 12 }}>
                  <strong>Manual Entry Quantity:</strong> {manualQuantity !== null && manualQuantity !== undefined && typeof manualQuantity === 'number' ? `${manualQuantity.toFixed(2)} L` : 'Not available'}
                  {isStart && previousManualQuantity !== null && previousManualQuantity !== undefined && typeof previousManualQuantity === 'number' && (
                    <span> (Previous: {previousManualQuantity.toFixed(2)} L)</span>
                  )}
                  {!isStart && currentManualQuantity !== null && currentManualQuantity !== undefined && typeof currentManualQuantity === 'number' && (
                    <span> (Current: {currentManualQuantity.toFixed(2)} L)</span>
                  )}
                </div>
                <div style={{ fontSize: 12 }}>
                  Showing refuel events from previous day, same day, and next day.
                  Confidence is based on how close the IoT-detected quantity matches the manual entry.
                  Click on an event to select it.
                </div>
              </div>
            }
            showIcon
          />
        </div>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {events.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
              <Icon type="info-circle" style={{ fontSize: 32, marginBottom: 8 }} />
              <div>No refuel events detected around this date</div>
            </div>
          ) : (
            <>
              {renderEventGroup(groups.previous, 'previous')}
              {renderEventGroup(groups.same, 'same')}
              {renderEventGroup(groups.next, 'next')}
            </>
          )}
        </div>
      </Modal>
    );
  };

  const handleChartEventClick = event => {
    console.log('chart event clicked', event);
    // Could set analysis range based on event
  };

  const handleFetchData = async () => {
    if (analysisRange.length !== 2 || !analysisRange[0] || !analysisRange[1]) {
      Modal.error({
        title: 'Invalid Date Range',
        content: 'Please select both start and end times for the analysis period.',
      });
      return;
    }

    setIsLoadingData(true);
    setDataLoaded(false);

    try {
      const startMoment = analysisRange[0];
      const endMoment = analysisRange[1];
      
      console.log('Fetching IoT data for period:', {
        start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
        end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
      });

      // Fetch combined IoT data using both daily summary and period APIs
      const result = await fetchCombinedIotData(
        mockAlert.assetCode, // '20E-290'
        startMoment,
        endMoment
      );

      if (!result.success) {
        Modal.error({
          title: 'Failed to Fetch IoT Data',
          content: result.error || 'An error occurred while fetching IoT data. Please try again.',
        });
        setDataLoaded(false);
        setIsLoadingData(false);
        return;
      }

      // Transform the data to match UI format
      const transformedData = transformIotDataForUI(result.data, startMoment, endMoment);
      
      if (!transformedData) {
        Modal.error({
          title: 'Data Transformation Error',
          content: 'Failed to transform IoT data. Please try again.',
        });
        setDataLoaded(false);
        setIsLoadingData(false);
        return;
      }

      // Store the fetched data
      setFetchedIotData(transformedData);
      setDataLoaded(true);
      
      // Save to localStorage immediately with new data
      saveApiDataToStorage({ fetchedIotData: transformedData });
      
      Modal.success({
        title: 'Data Loaded Successfully',
        content: `IoT data loaded for period: ${startMoment.format('YYYY-MM-DD HH:mm:ss')} to ${endMoment.format('YYYY-MM-DD HH:mm:ss')}`,
        duration: 2,
      });
      
    } catch (error) {
      console.error('Error fetching IoT data:', error);
      Modal.error({
        title: 'Error',
        content: error.message || 'An unexpected error occurred while fetching IoT data.',
      });
      setDataLoaded(false);
    } finally {
      setIsLoadingData(false);
    }
  };


  // Helper function to convert time string (HH:MM:SS) to hours
  const timeStringToHours = timeStr => {
    if (!timeStr) return null;
    const parts = timeStr.split(':');
    if (parts.length !== 3) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);
    return hours + minutes / 60 + seconds / 3600;
  };

  // Generate day-wise breakdown from analysis range
  const dayWiseBreakdown = useMemo(() => {
    if (analysisRange.length !== 2) return [];
    
    const start = analysisRange[0];
    const end = analysisRange[1];
    const days = [];
    let current = start.clone().startOf('day');
    const endDay = end.clone().startOf('day');

    while (current.isSameOrBefore(endDay, 'day')) {
      const dayStart = current.isSame(start, 'day') 
        ? start 
        : current.clone().startOf('day');
      const dayEnd = current.isSame(end, 'day')
        ? end
        : current.clone().endOf('day');
      
      const dayStartOfDay = current.clone().startOf('day');
      const dayEndOfDay = current.clone().endOf('day');
      const isFullDay = dayStart.isSame(dayStartOfDay, 'minute') && 
                       dayEnd.isSame(dayEndOfDay, 'minute');
      
      // Find matching day data from mockIotData.dayWiseData
      const dayData = (mockIotData.dayWiseData || []).find(
        d => moment(d.date).isSame(current, 'day')
      ) || {
        date: current.format('YYYY-MM-DD'),
        isFullDay,
        dataSource: isFullDay ? 'day_summary' : 'iot_table',
        movingTime: '00:00:00',
        pauseTime: '00:00:00',
        ignitionTime: '00:00:00',
        stopTime: '00:00:00',
        distanceTravelled: 0,
        fuelConsumed: 0,
        possibleRefuel: 0,
        possibleDrain: 0,
        avgFuelLtrPerHr: 0,
        averageSpeed: null,
      };

      // Calculate consumption rates in all formats when data is available
      let avgFuelKmPerL = null;
      let avgFuelLPerKm = null;
      let avgFuelLPerHour = null;
      let avgFuelHourPerL = null;

      // Calculate KM/L and L/KM if distance and fuel data are available
      if (dayData.distanceTravelled && dayData.fuelConsumed) {
        const distance = typeof dayData.distanceTravelled === 'number' ? dayData.distanceTravelled : parseFloat(dayData.distanceTravelled);
        const fuel = typeof dayData.fuelConsumed === 'number' ? dayData.fuelConsumed : parseFloat(dayData.fuelConsumed);
        if (!isNaN(distance) && !isNaN(fuel) && distance > 0 && fuel > 0) {
          avgFuelKmPerL = distance / fuel;
          avgFuelLPerKm = fuel / distance;
        }
      }

      // Calculate L/Hours and Hours/L if time and fuel data are available
      if (dayData.avgFuelLtrPerHr) {
        const lPerHr = typeof dayData.avgFuelLtrPerHr === 'number' ? dayData.avgFuelLtrPerHr : parseFloat(dayData.avgFuelLtrPerHr);
        if (!isNaN(lPerHr) && lPerHr > 0) {
          avgFuelLPerHour = lPerHr;
          avgFuelHourPerL = 1 / lPerHr;
        }
      }
      
      // Also calculate from ignition time if available (for hours-based calculation)
      if (!avgFuelLPerHour && dayData.ignitionTime && dayData.fuelConsumed) {
        const ignitionHours = timeStringToHours(dayData.ignitionTime);
        const fuel = typeof dayData.fuelConsumed === 'number' ? dayData.fuelConsumed : parseFloat(dayData.fuelConsumed);
        if (ignitionHours && !isNaN(fuel) && ignitionHours > 0 && fuel > 0) {
          avgFuelLPerHour = fuel / ignitionHours;
          avgFuelHourPerL = ignitionHours / fuel;
        }
      }

      days.push({
        ...dayData,
        dayStart: dayStart.format('YYYY-MM-DD HH:mm:ss'),
        dayEnd: dayEnd.format('YYYY-MM-DD HH:mm:ss'),
        // Add calculated consumption rates
        avgFuelKmPerL,
        avgFuelLPerKm,
        avgFuelLPerHour,
        avgFuelHourPerL,
      });

      current.add(1, 'day');
    }

    return days;
  }, [analysisRange, mockIotData.dayWiseData, mockAlert.hasKms, mockAlert.hasHours]);

  const handleViewDayCharts = (date, dayStart, dayEnd) => {
    // Open Stable Fuel Consumption Analysis in a new window with pre-filled parameters
    if (!mockAlert.assetCode) {
      message.warning('Asset code not available');
      return;
    }
    
    // Build URL with parameters for Stable Fuel Consumption Analysis
    // Format: #/stable-fuel-analysis?asset=XXXX&start=YYYY-MM-DD%20HH:mm:ss&end=YYYY-MM-DD%20HH:mm:ss
    const params = new URLSearchParams();
    params.set('asset', mockAlert.assetCode);
    if (dayStart) params.set('start', dayStart);
    if (dayEnd) params.set('end', dayEnd);
    
    const url = `${window.location.origin}${window.location.pathname}#/stable-fuel-analysis?${params.toString()}`;
    
    console.log('Opening Stable Fuel Consumption Analysis:', {
      assetCode: mockAlert.assetCode,
      start: dayStart,
      end: dayEnd,
      url: url
    });
    
    // Open in new window/tab
    window.open(url, '_blank');
  };

  // Rows for the Meter Run column: the priority meter first (it is the one the standard and
  // actual rate are calculated on), then every lower-priority meter recorded on the same
  // entry. Nothing is inferred — a meter only appears when its run was actually computed
  // from readings present on both this full tank and the previous one.
  const getMeterRunRows = (record) => {
    const runs = getRecordMeterRuns(record);
    const primaryType = record.primaryMeterType
      || (mockAlert.hasKwh ? 'KWH' : (mockAlert.hasKms ? 'KMS' : (mockAlert.hasHours ? 'HOURS' : null)));
    const availableTypes = METER_PRIORITY.filter(
      type => typeof runs[type] === 'number' && Number.isFinite(runs[type])
    );
    const orderedTypes = [
      ...availableTypes.filter(type => type === primaryType),
      ...availableTypes.filter(type => type !== primaryType),
    ];
    return orderedTypes.map(type => ({
      type,
      value: runs[type],
      isPrimary: type === primaryType,
    }));
  };

  const trendColumns = [
    {
      title: 'Refueling Date',
      dataIndex: 'refuelTime',
      key: 'refuelTime',
      render: time => moment(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Voucher No',
      dataIndex: 'voucherNo',
      key: 'voucherNo',
    },
    {
      // Shows EVERY meter recorded between this full tank and the previous one, not just the
      // one the rate is calculated on. Bold first line = priority meter (KWH > KMs > Hours),
      // which is what Actual/Standard/Deviation on this row are based on. The lower-priority
      // readings below it are reference values only and never feed the rate.
      title: 'Meter Run',
      dataIndex: 'meterRuns',
      key: 'meterRuns',
      align: 'right',
      render: (_, record) => {
        const meterRunRows = getMeterRunRows(record);

        if (meterRunRows.length === 0) {
          return <span style={{ color: '#bfbfbf' }}>-</span>;
        }

        return (
          <div>
            {meterRunRows.map(runRow => (
              <div
                key={runRow.type}
                style={runRow.isPrimary
                  ? { fontWeight: 'bold' }
                  : { fontSize: 11, color: '#8c8c8c' }}
              >
                {runRow.value.toFixed(2)} {METER_LABELS[runRow.type].unit}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      // Operating Level = run on the higher-priority meter over the run on the lower-priority
      // meter for the SAME interval: KWH/Hour on a DG (power load), KM/Hour on a vehicle
      // (average speed). Indicator only — it never affects the standard rate, actual rate,
      // deviation or the alert status. Blank with a reason when it cannot be derived.
      title: maxOperatingLevel > 0 && operatingLevelUnit
        ? `Operating Level % (max ${maxOperatingLevel} ${operatingLevelUnit})`
        : 'Operating Level',
      dataIndex: 'operatingLevel',
      key: 'operatingLevel',
      align: 'right',
      render: (_, record) => {
        const level = record.operatingLevel || computeOperatingLevel(getRecordMeterRuns(record));

        if (!level || level.value === null || level.value === undefined) {
          return (
            <div>
              <div style={{ color: '#bfbfbf' }}>-</div>
              <div style={{ fontSize: 10, color: '#bfbfbf' }}>
                {level && level.reason ? level.reason : 'Not available'}
              </div>
            </div>
          );
        }

        // Percentage of the stated Maximum Operating Level. Null — and therefore not
        // rendered at all — until a maximum in this row's own unit has been entered.
        const percentOfMax = operatingLevelPercentOfMax(level, maxOperatingLevel, operatingLevelUnit);
        // OVER_MAX above 100% (red), LOW below 50% (orange, running lightly loaded),
        // NORMAL in between. Colour and icon only — the figure itself is untouched.
        const band = operatingLevelBand(percentOfMax);
        const bandStyle = {
          OVER_MAX: { color: '#f5222d', icon: 'warning' },
          LOW: { color: '#fa8c16', icon: 'arrow-down' },
          NORMAL: { color: '#262626', icon: null },
        }[band] || { color: '#262626', icon: null };

        // Once a maximum is known, utilisation against it is the number the operator reads,
        // so the percentage becomes the headline and the raw rate drops to a supporting line.
        // With no maximum set there is nothing to be a percentage of, and the raw rate leads.
        if (percentOfMax !== null) {
          return (
            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  lineHeight: '22px',
                  color: bandStyle.color,
                }}
              >
                {percentOfMax.toFixed(1)}%
                {bandStyle.icon && (
                  <Icon type={bandStyle.icon} style={{ fontSize: 13, marginLeft: 6 }} />
                )}
              </div>
              <div style={{ fontSize: 12, color: '#595959' }}>
                {level.value.toFixed(2)} {level.unitLabel}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                of {maxOperatingLevel} {operatingLevelUnit} max
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {level.numerator.toFixed(2)} {METER_LABELS[level.numeratorType].unit} / {level.denominator.toFixed(2)} {METER_LABELS[level.denominatorType].unit}
              </div>
            </div>
          );
        }

        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {level.value.toFixed(2)} {level.unitLabel}
            </div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {level.numerator.toFixed(2)} {METER_LABELS[level.numeratorType].unit} / {level.denominator.toFixed(2)} {METER_LABELS[level.denominatorType].unit}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Fuel Consumed (L)',
      dataIndex: 'fuelAdded',
      key: 'fuelAdded',
      align: 'right',
      render: (val, record) => {
        const fullTankFuel = record.fullTankFuel || 0;
        const totalConsumed = record.fuelAdded || 0;
        const hasIntermediate = record.intermediateFuelCount > 0;
        
        // Build breakdown content for popover
        const breakdownContent = hasIntermediate ? (
          <div style={{ maxWidth: 350 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
              Fuel Consumption Breakdown
            </div>
            <table style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Voucher</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Qty (L)</th>
                  <th style={{ textAlign: 'center', padding: '4px 8px' }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {/* Intermediate entries (non-full tank and excluded full tank) */}
                {record.intermediateEntriesDetails && record.intermediateEntriesDetails.map((entry, idx) => (
                  <tr key={idx} style={{ backgroundColor: entry.isExcludedFullTank ? '#fff1f0' : '#fff7e6' }}>
                    <td style={{ padding: '4px 8px', fontSize: 11 }}>{moment(entry.date).format('DD-MMM HH:mm')}</td>
                    <td style={{ padding: '4px 8px', fontSize: 11 }}>{entry.voucherNo}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{entry.quantity.toFixed(2)}</td>
                    <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                      {entry.isExcludedFullTank ? (
                        <Tag color="red" style={{ fontSize: 10, margin: 0 }}>Excl. FT</Tag>
                      ) : (
                        <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>Partial</Tag>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Current full tank entry */}
                <tr style={{ backgroundColor: '#f6ffed' }}>
                  <td style={{ padding: '4px 8px', fontSize: 11 }}>{moment(record.refuelTime).format('DD-MMM HH:mm')}</td>
                  <td style={{ padding: '4px 8px', fontSize: 11 }}>{record.voucherNo || '-'}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{fullTankFuel.toFixed(2)}</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                    <Tag color="green" style={{ fontSize: 10, margin: 0 }}>Full Tank</Tag>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '1px solid #d9d9d9', fontWeight: 'bold' }}>
                  <td colSpan={2} style={{ padding: '4px 8px' }}>Total Consumed</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{totalConsumed.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null;
        
        return (
          <div>
            {/* Fuel Consumed - prominent at top (used for rate calculation) */}
            {hasIntermediate ? (
              <Popover content={breakdownContent} title={null} trigger="click" placement="left">
                <div style={{ fontWeight: 'bold', color: '#1890ff', cursor: 'pointer' }}>
                  {totalConsumed.toFixed(2)}
                  <Icon type="info-circle" style={{ fontSize: 10, marginLeft: 4 }} />
                </div>
              </Popover>
            ) : (
              <div style={{ fontWeight: 'bold' }}>
                {totalConsumed.toFixed(2)}
              </div>
            )}
            {/* Fuel Added at this entry - secondary info below */}
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              <span style={{ color: '#52c41a' }}>{fullTankFuel.toFixed(2)}</span>
              <span style={{ marginLeft: 4 }}>Added</span>
              {hasIntermediate && (
                <span style={{ color: '#fa8c16', marginLeft: 4 }}>
                  (+{record.intermediateFuelCount} partial)
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: mockAlert.hasKms ? 'Actual (KM/L | L/KM)' : mockAlert.hasKwh ? 'Actual (KWH/L)' : 'Actual (L/Hours | Hours/L)',
      dataIndex: 'actualRate',
      key: 'actualRate',
      align: 'right',
      render: (val, record) => {
        // For KM-based assets: show both KM/L and L/KM
        // Match the format order based on standardUnit to ensure proper comparison
        if (mockAlert.hasKms && val !== null && val !== undefined && val > 0 && !isNaN(val)) {
          // actualRate from API is always in KM/L format (kmsRun / fuelAdded)
          // Check standardUnit to determine which format to show first
          const isLPerKm = mockAlert.standardUnit === 'L_PER_KM' || 
            (mockAlert.standardUnit && mockAlert.standardUnit.includes('L_PER_KM'));
          
          let kmPerL, lPerKm;
          if (isLPerKm) {
            // Standard is in L/KM format, so show L/KM first, then KM/L
            lPerKm = val > 0 ? 1 / val : null; // Convert from KM/L to L/KM
            kmPerL = val; // Keep original KM/L
          } else {
            // Standard is in KM/L format, so show KM/L first, then L/KM
            kmPerL = val; // Keep original KM/L
            lPerKm = val > 0 ? 1 / val : null; // Convert from KM/L to L/KM
          }
          
          // Ensure values are valid before calling toFixed
          if (kmPerL === null || lPerKm === null || isNaN(kmPerL) || isNaN(lPerKm)) {
            return <span>-</span>;
          }
          
          // Always show KM/L above and L/KM below (consistent with Standard column)
            return (
              <div style={{ fontWeight: record.isAlert ? 'bold' : 'normal' }}>
                <div>{kmPerL.toFixed(2)} KM/L</div>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                  {lPerKm.toFixed(2)} L/KM
                </div>
              </div>
            );
        }
        // For hours-based assets: show both L/Hours and Hours/L
        if (mockAlert.hasHours && val !== null && val !== undefined && val > 0 && !isNaN(val)) {
          // actualRate from API for hours-based is in L/Hours format (fuelAdded / hoursRun)
          // So we always convert from L/Hours to Hours/L
          const lPerHour = val;
          const hourPerL = val > 0 ? 1 / val : null;
          
          if (hourPerL === null || isNaN(hourPerL)) {
            return <span>-</span>;
          }
          
          return (
            <div style={{ fontWeight: record.isAlert ? 'bold' : 'normal' }}>
              <div>{lPerHour.toFixed(2)} L/Hours</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {hourPerL.toFixed(2)} Hours/L
              </div>
            </div>
          );
        }
        // For KWH-based assets: show as KWH/L
        if (mockAlert.hasKwh && val !== null && val !== undefined && val > 0 && !isNaN(val)) {
          return (
            <span style={{ fontWeight: record.isAlert ? 'bold' : 'normal' }}>
              {val.toFixed(2)} KWH/L
            </span>
          );
        }
        return (
        <span style={{ fontWeight: record.isAlert ? 'bold' : 'normal' }}>
            {val !== null && val !== undefined && !isNaN(val) ? val.toFixed(2) : '-'}
        </span>
        );
      },
    },
    {
      title: mockAlert.hasKms ? 'Standard (KM/L | L/KM)' : mockAlert.hasKwh ? 'Standard (KWH/L)' : 'Standard (L/Hours | Hours/L)',
      dataIndex: 'standardRate',
      key: 'standardRate',
      align: 'right',
      render: (val, record) => {
        // Read the one resolved rate (see resolvedStandardRate) so this column, the trend
        // chart and the deviation can never print three different standards for one asset.
        // Falls back to the row's own stored rate only when nothing has been resolved yet.
        const displayStandardRate = resolvedStandardRate !== null ? resolvedStandardRate : val;
        
        // Debug log to trace standard rate source
        // console.log('Standard column render:', { hasKwh: mockAlert.hasKwh, mockAlertStdRate: mockAlert.standardRate, stateStdRate: standardRate, val, displayStandardRate });
        // For KM-based assets: show both KM/L and L/KM
        if (mockAlert.hasKms && displayStandardRate !== null && displayStandardRate !== undefined && displayStandardRate > 0 && !isNaN(displayStandardRate)) {
          // displayStandardRate is already in KM/L. standardUnit 'L_PER_KM' records how the
          // asset model *stores* the rate; every source (handleSelectFullTankEvent,
          // normalizeStandardRateForDisplay, the trend rows) inverts it to KM/L before it
          // reaches here, and the deviation is computed against that KM/L value. Inverting
          // again on standardUnit printed 11 KM/L as "0.09 KM/L | 11.00 L/KM".
          const kmPerL = displayStandardRate;
          const lPerKm = 1 / displayStandardRate;
          
          if (kmPerL === null || lPerKm === null || isNaN(kmPerL) || isNaN(lPerKm)) {
            return <span>-</span>;
          }
          
          return (
            <div>
              <div>{kmPerL.toFixed(2)} KM/L</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {lPerKm.toFixed(2)} L/KM
              </div>
            </div>
          );
        }
        // For hours-based assets: show both L/Hours and Hours/L
        if (mockAlert.hasHours && displayStandardRate !== null && displayStandardRate !== undefined && displayStandardRate > 0 && !isNaN(displayStandardRate)) {
          // Standard rate for hours-based is in L/Hours format
          // So we always convert from L/Hours to Hours/L
          const lPerHour = displayStandardRate;
          const hourPerL = displayStandardRate > 0 ? 1 / displayStandardRate : null;
          
          if (hourPerL === null || isNaN(hourPerL)) {
            return <span>-</span>;
          }
          
          return (
            <div>
              <div>{lPerHour.toFixed(2)} L/Hours</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                {hourPerL.toFixed(2)} Hours/L
              </div>
            </div>
          );
        }
        // For KWH-based assets: show as KWH/L
        if (mockAlert.hasKwh && displayStandardRate !== null && displayStandardRate !== undefined && displayStandardRate > 0 && !isNaN(displayStandardRate)) {
          return `${displayStandardRate.toFixed(2)} KWH/L`;
        }
        return displayStandardRate !== null && displayStandardRate !== undefined ? displayStandardRate.toFixed(2) : '-';
      },
    },
    {
      title: 'Deviation',
      dataIndex: 'deviation',
      key: 'deviation',
      align: 'right',
      render: (value, record) => {
        if (value === null || value === undefined) {
          return <span>-</span>;
        }
        
        // For KM/L: Deviation is inverted (negative when actual > standard, positive when actual < standard)
        //   - Negative deviation (actual KM/L > standard KM/L, lower fuel consumption) = green
        //   - Positive deviation (actual KM/L < standard KM/L, higher fuel consumption) = red
        // For L/Hour: Normal deviation calculation
        //   - Negative deviation (actual L/Hour < standard L/Hour, lower fuel consumption) = green
        //   - Positive deviation (actual L/Hour > standard L/Hour, higher fuel consumption) = red
        const isKmBased = mockAlert.hasKms;
        const absValue = Math.abs(value);
        let color;
        
        if (isKmBased) {
          // KM/L logic: negative = good (green), positive = bad (red)
          if (value < 0) {
            // Negative deviation: actual KM/L > standard KM/L (lower fuel consumption, good)
            color = absValue > 10 ? '#52c41a' : absValue > 5 ? '#52c41a' : '#52c41a'; // Green for negative
          } else {
            // Positive deviation: actual KM/L < standard KM/L (higher fuel consumption, bad)
            color = absValue > 10 ? '#f5222d' : absValue > 5 ? '#faad14' : '#f5222d'; // Red/orange for positive
          }
        } else {
          // L/Hour logic: negative = good (green), positive = bad (red)
          if (value < 0) {
            // Negative deviation: actual L/Hour < standard L/Hour (lower fuel consumption, good)
            color = absValue > 10 ? '#52c41a' : absValue > 5 ? '#52c41a' : '#52c41a'; // Green for negative
          } else {
            // Positive deviation: actual L/Hour > standard L/Hour (higher fuel consumption, bad)
            color = absValue > 10 ? '#f5222d' : absValue > 5 ? '#faad14' : '#f5222d'; // Red/orange for positive
          }
        }
        
        return (
          <span style={{ color, fontWeight: record.isAlert ? 'bold' : 'normal' }}>
            {value > 0 ? '+' : ''}
            {value.toFixed(2)}%
          </span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.isAlert) {
          return <Tag color="red">Alert</Tag>;
        }
        
        const deviation = record.deviation;
        if (deviation === null || deviation === undefined) {
          return <Tag color="default">-</Tag>;
        }
        
        // Use variance threshold if available, otherwise fall back to default thresholds
        const threshold = varianceThreshold || 10;
        const absDev = Math.abs(deviation);
        
        // For KM/L: negative deviation is good (actual > standard), positive is bad (actual < standard)
        // For L/Hour: negative deviation is good (actual < standard), positive is bad (actual > standard)
        // Status logic:
        // - Low: deviation is negative beyond threshold (good performance) = green
        // - High: deviation is positive beyond threshold (bad performance) = red
        // - OK: deviation is within ±threshold range = yellow
        
        if (deviation < -threshold) {
          // Deviation is negative beyond threshold (good performance)
          return <Tag color="green">Low</Tag>;
        } else if (deviation > threshold) {
          // Deviation is positive beyond threshold (bad performance)
          return <Tag color="red">High</Tag>;
        } else {
          // Deviation is within ±threshold range
          return <Tag color="gold">OK</Tag>;
        }
      },
    },
    {
      title: (
        <Tooltip title="Temporarily mark full tank events as non-full-tank for analysis. This recalculates consumption rates without modifying actual data.">
          <span>
            Exclude
            <Icon type="question-circle" style={{ marginLeft: 4, fontSize: 12 }} />
          </span>
        </Tooltip>
      ),
      key: 'exclude',
      width: 80,
      align: 'center',
      render: (_, record, index) => {
        const isExcluded = excludedFullTankEvents[record.refuelTime] === true;
        // Don't allow excluding the first (newest) or last (oldest) event in the visible list
        const isFirstOrLast = index === 0 || index === filteredTrendData.length - 1;
        
        return (
          <Tooltip 
            title={isFirstOrLast 
              ? "Cannot exclude first or last event" 
              : isExcluded 
                ? "Click to restore as full tank" 
                : "Click to temporarily mark as non-full-tank for analysis"
            }
          >
            <Checkbox
              checked={isExcluded}
              disabled={isFirstOrLast}
              onChange={(e) => {
                setExcludedFullTankEvents(prev => ({
                  ...prev,
                  [record.refuelTime]: e.target.checked
                }));
              }}
            />
          </Tooltip>
        );
      },
    },
  ];
  
  // Function to clear all exclusions
  const clearAllExclusions = () => {
    setExcludedFullTankEvents({});
  };
  
  // Check if any exclusions are active
  const hasActiveExclusions = Object.values(excludedFullTankEvents).some(v => v === true);

  const renderAlignModal = () => {
    const refuelEvents = mockIotData.refuelEvents || [];
    
    // Get dates for filtering
    const lastFullTankDate = moment(mockAlert.lastFullTankTime).startOf('day');
    const currentRefuelDate = moment(mockAlert.refuelTime).startOf('day');
    
    // Filter events for start period (last full tank date ± 1 day)
    const startEvents = refuelEvents.filter(event => {
      const eventMoment = ensureMoment(event);
      const eventDate = eventMoment.startOf('day');
      return (
        eventDate.isSameOrAfter(lastFullTankDate.clone().subtract(1, 'day')) &&
        eventDate.isSameOrBefore(lastFullTankDate.clone().add(1, 'day'))
      );
    });
    
    // Filter events for end period (current refuel date ± 1 day)
    const endEvents = refuelEvents.filter(event => {
      const eventMoment = ensureMoment(event);
      const eventDate = eventMoment.startOf('day');
      return (
        eventDate.isSameOrAfter(currentRefuelDate.clone().subtract(1, 'day')) &&
        eventDate.isSameOrBefore(currentRefuelDate.clone().add(1, 'day'))
      );
    });
    
    // Group events by day relative to target date
    const groupEventsByDay = (events, targetDate) => {
      const groups = {
        previous: [],
        same: [],
        next: [],
      };
      
      events.forEach(event => {
        const eventMoment = ensureMoment(event);
        const eventDate = eventMoment.startOf('day');
        if (eventDate.isBefore(targetDate, 'day')) {
          groups.previous.push(event);
        } else if (eventDate.isSame(targetDate, 'day')) {
          groups.same.push(event);
        } else {
          groups.next.push(event);
        }
      });
      
      return groups;
    };
    
    const startEventGroups = groupEventsByDay(startEvents, lastFullTankDate);
    const endEventGroups = groupEventsByDay(endEvents, currentRefuelDate);
    
    const renderEventGroup = (events, targetDate, dayType) => {
      if (events.length === 0) return null;
      
      let dayLabel = '';
      let dayDate = '';
      if (dayType === 'previous') {
        dayLabel = 'Previous Day';
        dayDate = targetDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
      } else if (dayType === 'same') {
        dayLabel = 'Same Day';
        dayDate = targetDate.format('YYYY-MM-DD');
      } else {
        dayLabel = 'Next Day';
        dayDate = targetDate.clone().add(1, 'day').format('YYYY-MM-DD');
      }
      
      return (
        <div key={dayType} style={{ marginBottom: 16 }}>
          <div style={{ 
            fontSize: 13, 
            fontWeight: 600, 
            marginBottom: 8,
            color: dayType === 'same' ? '#1890ff' : '#8c8c8c'
          }}>
            {dayLabel} ({dayDate})
          </div>
          {events.map(event => (
            <Radio key={event.id} value={event.id} style={{ display: 'block', marginBottom: 8, marginLeft: 16 }}>
              <div>
                <div style={{ fontWeight: 500 }}>{event.timeText}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {event.description}
                </div>
                <div style={{ fontSize: 11, marginTop: 2 }}>
                  <Tag 
                    color={
                      event.confidence === 'high' ? 'green' : 
                      event.confidence === 'medium' ? 'orange' : 'red'
                    } 
                    size="small"
                  >
                    {event.confidence} confidence
                  </Tag>
                </div>
              </div>
            </Radio>
          ))}
        </div>
      );
    };

    return (
      <Modal
        visible={alignModalVisible}
        title="Align Refueling Times Using IoT Events"
        onCancel={() => setAlignModalVisible(false)}
        onOk={applyAlignmentFromModal}
        okText="Apply"
        width={700}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Start Period Selection */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Select event for Last Full Tank (Start)</strong>
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
              Last Full Tank Date: {lastFullTankDate.format('YYYY-MM-DD')}
            </div>
            {startEvents.length === 0 ? (
              <Alert
                type="info"
                message="No IoT refuel events detected around the last full tank date."
                showIcon
                style={{ marginTop: 8 }}
              />
            ) : (
              <RadioGroup
                onChange={e => setSelectedStartRefuelId(e.target.value)}
                value={selectedStartRefuelId}
                style={{ display: 'block' }}
              >
                {renderEventGroup(startEventGroups.previous, lastFullTankDate, 'previous')}
                {renderEventGroup(startEventGroups.same, lastFullTankDate, 'same')}
                {renderEventGroup(startEventGroups.next, lastFullTankDate, 'next')}
              </RadioGroup>
            )}
          </div>

          <div style={{ borderTop: '1px solid #e8e8e8', margin: '16px 0' }} />

          {/* End Period Selection */}
          <div>
            <div style={{ marginBottom: 8 }}>
              <strong>Select event for Current Full Tank (End)</strong>
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
              Current Refuel Date: {currentRefuelDate.format('YYYY-MM-DD')}
            </div>
            {endEvents.length === 0 ? (
              <Alert
                type="info"
                message="No IoT refuel events detected around the current refuel date."
                showIcon
                style={{ marginTop: 8 }}
              />
            ) : (
              <RadioGroup
                onChange={e => setSelectedEndRefuelId(e.target.value)}
                value={selectedEndRefuelId}
                style={{ display: 'block' }}
              >
                {renderEventGroup(endEventGroups.previous, currentRefuelDate, 'previous')}
                {renderEventGroup(endEventGroups.same, currentRefuelDate, 'same')}
                {renderEventGroup(endEventGroups.next, currentRefuelDate, 'next')}
              </RadioGroup>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  const isUsingAdjustedTimes =
    analysisRange.length === 2 &&
    (analysisRange[0].format('YYYY-MM-DD HH:mm:ss') !==
      moment(mockAlert.lastFullTankTime).format('YYYY-MM-DD HH:mm:ss') ||
      analysisRange[1].format('YYYY-MM-DD HH:mm:ss') !==
        moment(mockAlert.refuelTime).format('YYYY-MM-DD HH:mm:ss'));

  return (
    <Layout style={{ background: '#f0f2f5', minHeight: '100vh' }}>
      <Content style={{ padding: 16 }}>
        {/* AI Analysis Button - if saved locally show it; otherwise ask for API key */}
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button
            type="primary"
            icon="robot"
            onClick={() => {
              // If we have cached analysis for this alert, show it without asking for API key
              if (mockAlert.assetCode && mockAlert.refuelTime) {
                const cached = loadAiAnalysisFromCache();
                if (cached && cached.result) {
                  setAiAnalysisResult(cached.result);
                  setAiAnalysisFromCache(true);
                  setAiAnalysisGeneratedAt(cached.generatedAt || null);
                  setAiAnalysisError(null);
                  setAiAnalysisModalVisible(true);
                  return;
                }
              }
              // No cache: ask for API key
              setPendingAiAction('analyze');
              setApiKeyModalInput('');
              setApiKeyModalVisible(true);
            }}
            loading={aiAnalysisLoading}
            disabled={!mockAlert.assetCode}
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            }}
          >
            AI Analysis
          </Button>
        </div>

        {/* HEADER */}
        <Card style={{ marginBottom: 16 }}>
          <Row type="flex" justify="space-between" align="top" gutter={16}>
            <Col span={14}>
              <Row type="flex" justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                <Col>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
                Asset Code - {formatValue(mockAlert.assetCode)}{mockAlert.altName ? ` / ${formatValue(mockAlert.altName)}` : ''}{mockAlert.ownerCode ? ` / ${formatValue(mockAlert.ownerCode)}` : ''}
              </div>
                </Col>
                <Col>
                  <Button
                    type="default"
                    icon="edit"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Opening asset selection modal, current state:', assetSelectionModalVisible);
                      setAssetSelectionModalVisible(true);
                      console.log('Modal state set to true');
                    }}
                    size="small"
                  >
                    Select Asset & Load Data
                  </Button>
                </Col>
              </Row>
              <div style={{ marginBottom: 8 }}>
              <span style={{ color: '#8c8c8c' }}>
                Alert Time: {formatValue(mockAlert.alertTime, (val) => moment(val).format('YYYY-MM-DD HH:mm:ss'))}
                </span>
              </div>
              <div style={{ color: '#8c8c8c', marginBottom: 8 }}>
                Refueling completed for {formatValue(mockAlert.assetCode)} /{' '}
                {formatValue(mockAlert.altName)} / {formatValue(mockAlert.ownerCode)}
              </div>
              {(mockAlert.location || mockAlert.subLocation) && (
              <div style={{ color: '#8c8c8c', marginBottom: 8 }}>
                  Location: {formatValue(mockAlert.location) || '-'}
                  {mockAlert.subLocation && ` | Sub Location: ${formatValue(mockAlert.subLocation)}`}
                  </div>
                )}
              
            </Col>
            <Col span={10}>
              <Row gutter={8}>
                <Col span={8}>
                  <Card bordered={false} bodyStyle={{ padding: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Actual</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      {(() => {
                        // For KM-based assets: show both KM/L and L/KM
                        // Note: actualRate is always in KM/L format (converted from L/KM if needed)
                        if (mockAlert.hasKms) {
                          let kmPerL = null;
                          let lPerKm = null;
                          
                          // Prefer mockAlert.actualRate — it's computed from the full cycle's
                          // totalFuelConsumed (full tank + intermediate partial entries) and matches
                          // the trend table. Manual calc using mockAlert.fuelAdded would only count
                          // the current full tank quantity and produce an inflated rate.
                          if (mockAlert.actualRate && mockAlert.actualRate > 0) {
                            kmPerL = mockAlert.actualRate;
                            lPerKm = 1 / mockAlert.actualRate;
                          } else if (mockAlert.kmsRun && mockAlert.totalFuelConsumed && mockAlert.totalFuelConsumed > 0) {
                            kmPerL = mockAlert.kmsRun / mockAlert.totalFuelConsumed;
                            lPerKm = mockAlert.totalFuelConsumed / mockAlert.kmsRun;
                          }
                          
                          if (kmPerL && lPerKm) {
                            return (
                              <div>
                                <div>{kmPerL.toFixed(2)} KM/L</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                                  ({lPerKm.toFixed(2)} L/KM)
                                </div>
                              </div>
                            );
                          }
                          return '-';
                        }
                        // For hours-based assets: show both L/Hours and Hours/L
                        if (mockAlert.hasHours) {
                          let lPerHour = null;
                          let hourPerL = null;
                          
                          if (mockAlert.actualRate) {
                            const isLPerHour = mockAlert.standardUnit && (
                              mockAlert.standardUnit.includes('HOUR') || 
                              mockAlert.standardUnit === 'L_PER_HOUR' ||
                              mockAlert.standardUnit === 'LTR_PER_HOUR'
                            );
                            const isHourPerL = mockAlert.standardUnit && (
                              mockAlert.standardUnit === 'HOUR_PER_L' ||
                              mockAlert.standardUnit === 'HOURS_PER_L'
                            );
                            
                            if (isLPerHour) {
                              lPerHour = mockAlert.actualRate;
                              hourPerL = 1 / mockAlert.actualRate;
                            } else if (isHourPerL) {
                              hourPerL = mockAlert.actualRate;
                              lPerHour = 1 / mockAlert.actualRate;
                            } else {
                              // Default: assume L/Hours
                              lPerHour = mockAlert.actualRate;
                              hourPerL = 1 / mockAlert.actualRate;
                            }
                          } else if (mockAlert.hoursRun && mockAlert.fuelAdded && mockAlert.hoursRun > 0) {
                            lPerHour = mockAlert.fuelAdded / mockAlert.hoursRun;
                            hourPerL = mockAlert.hoursRun / mockAlert.fuelAdded;
                          }
                          
                          if (lPerHour && hourPerL) {
                            return (
                              <div>
                                <div>{lPerHour.toFixed(2)} L/Hours</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                                  ({hourPerL.toFixed(2)} Hours/L)
                                </div>
                              </div>
                            );
                          }
                          return '-';
                        }
                        // For KWH-based assets: show KWH/L
                        if (mockAlert.hasKwh) {
                          if (mockAlert.actualRate) {
                            return `${mockAlert.actualRate.toFixed(2)} KWH/L`;
                          }
                          return '-';
                        }
                        return '-';
                      })()}
                    </div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card bordered={false} bodyStyle={{ padding: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Standard</div>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>
                      {(() => {
                        // For KM-based assets: show both KM/L and L/KM
                        // Note: standardRate is always in KM/L format (converted from L/KM if needed)
                        if (mockAlert.hasKms) {
                          if (mockAlert.standardRate && mockAlert.standardRate > 0) {
                            // standardRate is always in KM/L format (already converted)
                            const kmPerL = mockAlert.standardRate;
                            const lPerKm = 1 / mockAlert.standardRate;
                            return (
                              <div>
                                <div>{kmPerL.toFixed(2)} KM/L</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                                  ({lPerKm.toFixed(2)} L/KM)
                                </div>
                              </div>
                            );
                          }
                          return '-';
                        }
                        // For hours-based assets: show both L/Hours and Hours/L
                        if (mockAlert.hasHours) {
                          if (mockAlert.standardRate && mockAlert.standardRate > 0) {
                            const isLPerHour = mockAlert.standardUnit && (
                              mockAlert.standardUnit.includes('HOUR') || 
                              mockAlert.standardUnit === 'L_PER_HOUR' ||
                              mockAlert.standardUnit === 'LTR_PER_HOUR'
                            );
                            const isHourPerL = mockAlert.standardUnit && (
                              mockAlert.standardUnit === 'HOUR_PER_L' ||
                              mockAlert.standardUnit === 'HOURS_PER_L'
                            );
                            
                            let lPerHour, hourPerL;
                            if (isLPerHour) {
                              lPerHour = mockAlert.standardRate;
                              hourPerL = 1 / mockAlert.standardRate;
                            } else if (isHourPerL) {
                              hourPerL = mockAlert.standardRate;
                              lPerHour = 1 / mockAlert.standardRate;
                            } else {
                              // Default: assume L/Hours
                              lPerHour = mockAlert.standardRate;
                              hourPerL = 1 / mockAlert.standardRate;
                            }
                            return (
                              <div>
                                <div>{lPerHour.toFixed(2)} L/Hours</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                                  ({hourPerL.toFixed(2)} Hours/L)
                                </div>
                              </div>
                            );
                          }
                          return '-';
                        }
                        // For KWH-based assets: show KWH/L
                        if (mockAlert.hasKwh) {
                          if (mockAlert.standardRate) {
                            return `${mockAlert.standardRate.toFixed(2)} KWH/L`;
                          }
                          return '-';
                        }
                        return '-';
                      })()}
                    </div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card bordered={false} bodyStyle={{ padding: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Deviation</div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        // Green if negative (good performance), Red if positive (bad performance)
                        color: mockAlert.deviation !== null && mockAlert.deviation !== undefined
                          ? (mockAlert.deviation < 0 ? '#52c41a' : '#f5222d')
                          : '#8c8c8c',
                      }}
                    >
                      {mockAlert.deviation !== null && mockAlert.deviation !== undefined ? (
                        <>
                          {mockAlert.deviation > 0 ? '+' : ''}
                          {mockAlert.deviation.toFixed(2)}%
                        </>
                      ) : '-'}
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
          {/* IoT Device Status */}
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
            {iotDeviceStatus.isLoading ? (
              <span style={{ color: '#8c8c8c' }}>
                <Icon type="loading" style={{ marginRight: 4 }} />
                Loading IoT device status...
              </span>
            ) : iotDeviceStatus.hasLiveSensor ? (
              (() => {
                const delaySec = iotDeviceStatus.lastDataTimestamp && iotDeviceStatus.lastDataReceiptTime
                  ? Math.round((iotDeviceStatus.lastDataReceiptTime - iotDeviceStatus.lastDataTimestamp) / 1000)
                  : 0;
                const isOldData = delaySec > 300;
                const boxStyle = {
                  background: isOldData ? '#fff7e6' : '#f6ffed',
                  border: isOldData ? '1px solid #ffd591' : '1px solid #b7eb8f',
                  borderRadius: 4,
                  padding: '8px 12px',
                  fontSize: 12,
                  width: '100%'
                };
                return (
                  <div style={boxStyle}>
                    <div style={{ marginBottom: 4 }}>
                      <Tag color={isOldData ? 'orange' : 'green'} style={{ marginRight: 8 }}>
                        <Icon type="api" style={{ marginRight: 4 }} />
                        Live Sensor Connected
                        {isOldData && ' (Old data syncing)'}
                      </Tag>
                      {iotDeviceStatus.fuelSensorConnected && (
                        <Tag color="blue">
                          <Icon type="experiment" style={{ marginRight: 4 }} />
                          Fuel Sensor
                        </Tag>
                      )}
                    </div>
                    <div style={{ color: '#595959' }}>
                      <span style={{ marginRight: 16 }}>
                        <strong>Device ID:</strong> {iotDeviceStatus.tagDeviceId || '-'}
                      </span>
                    </div>
                    {iotDeviceStatus.lastDataTimestamp && (
                      <div style={{ color: '#595959', marginTop: 4 }}>
                        <span style={{ marginRight: 16 }}>
                          <strong>Last Data Timestamp (IST):</strong>{' '}
                          {moment(iotDeviceStatus.lastDataTimestamp).utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss')}
                        </span>
                      </div>
                    )}
                    {iotDeviceStatus.lastDataReceiptTime && (
                      <div style={{ color: '#595959', marginTop: 4 }}>
                        <span>
                          <strong>Last Data Receipt Time (IST):</strong>{' '}
                          {moment(iotDeviceStatus.lastDataReceiptTime).utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss')}
                        </span>
                        {iotDeviceStatus.lastDataTimestamp && iotDeviceStatus.lastDataReceiptTime && (
                          <span style={{ marginLeft: 8, color: '#8c8c8c' }}>
                            (Delay: {formatDelay(delaySec)})
                          </span>
                        )}
                      </div>
                    )}
                    {iotDeviceStatus.error && (
                      <div style={{ color: '#faad14', marginTop: 4 }}>
                        <Icon type="warning" style={{ marginRight: 4 }} />
                        {iotDeviceStatus.error}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div
                style={{
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: 4,
                  padding: '8px 12px',
                  fontSize: 12,
                  width: '100%'
                }}
              >
                <Tag color="orange">
                  <Icon type="disconnect" style={{ marginRight: 4 }} />
                  No Live Sensor
                </Tag>
                <span style={{ color: '#8c8c8c', marginLeft: 8 }}>
                  IoT device not connected to this asset
                </span>
              </div>
            )}
            </Col>
          </Row>
        </Card>

        {/* API Key prompt modal - shown when no cached analysis (or when Regenerate is clicked) */}
        <Modal
          title={
            <span>
              <Icon type="lock" style={{ marginRight: 8 }} />
              OpenAI API Key Required
            </span>
          }
          visible={apiKeyModalVisible}
          onCancel={() => {
            setApiKeyModalVisible(false);
            setPendingAiAction(null);
            setApiKeyModalInput('');
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setApiKeyModalVisible(false);
                setPendingAiAction(null);
                setApiKeyModalInput('');
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              icon="check"
              onClick={() => {
                const key = apiKeyModalInput.trim();
                if (!key) {
                  message.warning('Please enter your OpenAI API key');
                  return;
                }
                const isRegenerate = pendingAiAction === 'regenerate';
                setApiKeyModalVisible(false);
                setApiKeyModalInput('');
                setPendingAiAction(null);
                performAIAnalysis(isRegenerate, key);
              }}
            >
              Continue
            </Button>,
          ]}
          destroyOnClose
        >
          <p style={{ marginBottom: 12, color: '#666' }}>
            Only users with an OpenAI API key can run AI analysis. Your key is <strong>not stored</strong> and is required each time you use this feature.
          </p>
          <Input.Password
            placeholder="sk-..."
            value={apiKeyModalInput}
            onChange={(e) => setApiKeyModalInput(e.target.value)}
            onPressEnter={() => {
              const key = apiKeyModalInput.trim();
              if (key) {
                const isRegenerate = pendingAiAction === 'regenerate';
                setApiKeyModalVisible(false);
                setApiKeyModalInput('');
                setPendingAiAction(null);
                performAIAnalysis(isRegenerate, key);
              }
            }}
            style={{ width: '100%' }}
            addonBefore="API Key"
          />
        </Modal>

        {/* AI Analysis Modal */}
        <Modal
          title={
            <span>
              <Icon type="robot" style={{ marginRight: 8, color: '#667eea' }} />
              AI Fuel Alert Analysis
              {aiAnalysisFromCache && (
                <Tag color="blue" style={{ marginLeft: 8 }}>Saved locally</Tag>
              )}
              {aiAnalysisGeneratedAt && !aiAnalysisFromCache && (
                <Tag color="green" style={{ marginLeft: 8 }}>Just generated</Tag>
              )}
            </span>
          }
          visible={aiAnalysisModalVisible}
          onCancel={() => {
            setAiAnalysisModalVisible(false);
            setAiAnalysisResult(null);
            setAiAnalysisError(null);
            setAiAnalysisFromCache(false);
            setAiAnalysisGeneratedAt(null);
          }}
          footer={[
            aiAnalysisResult && (
              <Button
                key="regenerate"
                icon="reload"
                onClick={() => {
                  // Close analysis modal first so user can see the API key entry window
                  setAiAnalysisModalVisible(false);
                  setAiAnalysisResult(null);
                  setAiAnalysisError(null);
                  setAiAnalysisFromCache(false);
                  setAiAnalysisGeneratedAt(null);
                  setPendingAiAction('regenerate');
                  setApiKeyModalInput('');
                  setApiKeyModalVisible(true);
                }}
                loading={aiAnalysisLoading}
              >
                Regenerate AI Analysis
              </Button>
            ),
            <Button
              key="copy"
              icon="copy"
              onClick={() => {
                if (aiAnalysisResult) {
                  navigator.clipboard.writeText(aiAnalysisResult);
                  message.success('Analysis copied to clipboard');
                }
              }}
              disabled={!aiAnalysisResult}
            >
              Copy to Clipboard
            </Button>,
            <Button
              key="apply"
              type="primary"
              onClick={() => {
                if (aiAnalysisResult) {
                  // Extract analysis and suggested action from AI response
                  const analysisMatch = aiAnalysisResult.match(/## Analysis Summary\n([\s\S]*?)(?=\n##|$)/);
                  const actionsMatch = aiAnalysisResult.match(/## Recommended Actions\n([\s\S]*?)(?=\n##|$)/);
                  
                  if (analysisMatch) {
                    setAnalysis(analysisMatch[1].trim());
                  }
                  if (actionsMatch) {
                    setSuggestedAction(actionsMatch[1].trim());
                  }
                  
                  message.success('AI analysis applied to analysis fields');
                  setAiAnalysisModalVisible(false);
                }
              }}
              disabled={!aiAnalysisResult}
            >
              Apply to Analysis
            </Button>,
            <Button
              key="close"
              onClick={() => {
                setAiAnalysisModalVisible(false);
                setAiAnalysisResult(null);
                setAiAnalysisError(null);
                setAiAnalysisFromCache(false);
                setAiAnalysisGeneratedAt(null);
              }}
            >
              Close
            </Button>,
          ].filter(Boolean)}
          width={800}
          style={{ top: 20 }}
        >
          {aiAnalysisLoading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#666' }}>
                Analyzing fuel data with AI...
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                This may take a few seconds
              </div>
            </div>
          )}
          
          {aiAnalysisError && (
            <div style={{ marginBottom: 16 }}>
              {aiAnalysisError === 'OPENAI_KEY_REQUIRED' ? (
                <Alert
                  message="API key required"
                  description="Please close this modal and click AI Analysis again to enter your OpenAI API key. Your key is not stored."
                  type="warning"
                  showIcon
                />
              ) : (
                <Alert
                  message="Analysis Failed"
                  description={aiAnalysisError}
                  type="error"
                  showIcon
                />
              )}
            </div>
          )}
          
          {aiAnalysisResult && (
            <div>
              {(aiAnalysisFromCache || aiAnalysisGeneratedAt) && (
                <div style={{ marginBottom: 12, fontSize: 12, color: '#8c8c8c' }}>
                  {aiAnalysisFromCache && aiAnalysisGeneratedAt && (
                    <span>
                      <Icon type="save" style={{ marginRight: 4 }} />
                      Saved locally · Generated {moment(aiAnalysisGeneratedAt).format('DD MMM YYYY, HH:mm')}
                    </span>
                  )}
                  {aiAnalysisGeneratedAt && !aiAnalysisFromCache && (
                    <span>
                      <Icon type="check-circle" style={{ marginRight: 4 }} />
                      Generated {moment(aiAnalysisGeneratedAt).format('DD MMM YYYY, HH:mm')} · Saved to this device
                    </span>
                  )}
                </div>
              )}
              <div
                style={{
                  maxHeight: '60vh',
                  overflow: 'auto',
                  padding: 16,
                  background: '#fafafa',
                  borderRadius: 8,
                  fontSize: 14,
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap',
                }}
              >
              {/* Render markdown-style formatting */}
              {aiAnalysisResult.split('\n').map((line, index) => {
                if (line.startsWith('## ')) {
                  return (
                    <h3 key={index} style={{ color: '#667eea', marginTop: index > 0 ? 16 : 0, marginBottom: 8 }}>
                      {line.replace('## ', '')}
                    </h3>
                  );
                }
                if (line.startsWith('### ')) {
                  return (
                    <h4 key={index} style={{ color: '#764ba2', marginTop: 12, marginBottom: 4 }}>
                      {line.replace('### ', '')}
                    </h4>
                  );
                }
                if (line.startsWith('- ') || line.startsWith('* ')) {
                  return (
                    <div key={index} style={{ paddingLeft: 16, marginBottom: 4 }}>
                      <span style={{ marginRight: 8 }}>•</span>
                      {line.substring(2)}
                    </div>
                  );
                }
                if (line.match(/^\d+\. /)) {
                  return (
                    <div key={index} style={{ paddingLeft: 16, marginBottom: 4 }}>
                      {line}
                    </div>
                  );
                }
                if (line.trim() === '') {
                  return <div key={index} style={{ height: 8 }} />;
                }
                return <div key={index}>{line}</div>;
              })}
              </div>
            </div>
          )}
        </Modal>

        {/* Refuel Details Bar */}
        <Card
          style={{ marginBottom: 16 }}
          bodyStyle={{ padding: 0 }}
        >
          <div
            style={{
              padding: '8px 16px',
              background: refuelDetailsExpanded ? '#f0f2f5' : '#fff',
              borderBottom: refuelDetailsExpanded ? '1px solid #e8e8e8' : 'none',
            }}
          >
            <Row type="flex" justify="space-between" align="middle" gutter={16} style={{ flexWrap: 'nowrap' }}>
              <Col flex="1" style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#262626', marginBottom: 6 }}>
                  Fuel Entry Details
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Asset:</span>{' '}
                    <span style={{ fontSize: 13 }}>
                      {formatValue(mockAlert.assetCode)} / {formatValue(mockAlert.altName)} / {formatValue(mockAlert.ownerCode)}
                    </span>
                    {' | '}
                    <span style={{ fontSize: 13, color: '#8c8c8c' }}>{formatValue(mockAlert.assetModel)}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Current Full Tank:</span>{' '}
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {formatValue(mockAlert.refuelTime, (val) => moment(val, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss'))}
                    </span>
                    {' | '}
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Odometer:</span>{' '}
                    <span style={{ fontSize: 13 }}>
                      {(() => {
                        const readings = [];
                        if (mockAlert.currentHoursOdo !== null && mockAlert.currentHoursOdo !== undefined) {
                          readings.push(`${mockAlert.currentHoursOdo} HOURS`);
                        }
                        if (mockAlert.currentKmsOdo !== null && mockAlert.currentKmsOdo !== undefined) {
                          readings.push(`${mockAlert.currentKmsOdo} KMs`);
                        }
                        if (mockAlert.currentKwhOdo !== null && mockAlert.currentKwhOdo !== undefined) {
                          readings.push(`${mockAlert.currentKwhOdo} KWH`);
                        }
                        if (readings.length === 0 && mockAlert.currentOdo !== null && mockAlert.currentOdo !== undefined) {
                          readings.push(`${mockAlert.currentOdo} ${mockAlert.hasKwh ? 'KWH' : mockAlert.hasKms ? 'KMs' : 'HOURS'}`);
                        }
                        return readings.length > 0 ? readings.join(' | ') : '-';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Last Full Tank:</span>{' '}
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {mockAlert.lastFullTankTime ? moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                    </span>
                    {' | '}
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Meter:</span>{' '}
                    <span style={{ fontSize: 13 }}>
                      {(() => {
                        const readings = [];
                        if (mockAlert.lastFullTankHoursOdo !== null && mockAlert.lastFullTankHoursOdo !== undefined) {
                          readings.push(`${mockAlert.lastFullTankHoursOdo} HOURS`);
                        }
                        if (mockAlert.lastFullTankKmsOdo !== null && mockAlert.lastFullTankKmsOdo !== undefined) {
                          readings.push(`${mockAlert.lastFullTankKmsOdo} KMs`);
                        }
                        if (mockAlert.lastFullTankKwhOdo !== null && mockAlert.lastFullTankKwhOdo !== undefined) {
                          readings.push(`${mockAlert.lastFullTankKwhOdo} KWH`);
                        }
                        if (readings.length === 0 && mockAlert.lastFullTankOdo !== null && mockAlert.lastFullTankOdo !== undefined) {
                          readings.push(`${mockAlert.lastFullTankOdo} ${mockAlert.hasKwh ? 'KWH' : mockAlert.hasKms ? 'KMs' : 'HOURS'}`);
                        }
                        return readings.length > 0 ? readings.join(' | ') : '-';
                      })()}
                    </span>
                  </div>
                  {(() => {
                    const runs = [];
                    if (mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && !isNaN(mockAlert.hoursRun)) {
                      runs.push({ label: 'Hours Run', value: Number(mockAlert.hoursRun).toFixed(2), unit: 'HOURS' });
                    }
                    if (mockAlert.kmsRun !== null && mockAlert.kmsRun !== undefined && !isNaN(mockAlert.kmsRun)) {
                      runs.push({ label: 'KMs Run', value: Number(mockAlert.kmsRun).toFixed(2), unit: 'KMs' });
                    }
                    if (mockAlert.kwhRun !== null && mockAlert.kwhRun !== undefined && !isNaN(mockAlert.kwhRun)) {
                      runs.push({ label: 'KWH Consumed', value: Number(mockAlert.kwhRun).toFixed(2), unit: 'KWH' });
                    }
                    if (runs.length === 0) {
                      return (
                        <div>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Run:</span>{' '}
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1890ff' }}>{formatValue(null)}</span>
                        </div>
                      );
                    }
                    return runs.map(({ label, value, unit }) => (
                      <div key={label}>
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{label}:</span>{' '}
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1890ff' }}>
                          {value} {unit}
                        </span>
                      </div>
                    ));
                  })()}
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Total Fuel Consumed:</span>{' '}
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#1890ff' }}>
                      {formatValue(mockAlert.totalFuelConsumed)} {mockAlert.totalFuelConsumed ? 'L' : ''}
                    </span>
                    {' '}
                    <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                      ({fuelEntries.length > 0 ? fuelEntries.length : 0} entries)
                    </span>
                  </div>
                </div>
              </Col>
              <Col style={{ flexShrink: 0, marginLeft: 'auto', alignSelf: 'flex-start', paddingTop: 4 }}>
                <Button
                  type="link"
                  icon={refuelDetailsExpanded ? 'up' : 'down'}
                  onClick={() => setRefuelDetailsExpanded(!refuelDetailsExpanded)}
                  size="small"
                  style={{ 
                    padding: '0 8px',
                    color: '#1890ff',
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {refuelDetailsExpanded ? 'Collapse' : 'Expand'}
                </Button>
              </Col>
            </Row>
          </div>

          {refuelDetailsExpanded && (
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                All Fuel Entries ({fuelEntries.length > 0 ? fuelEntries.length : 0})
              </div>
              {fuelEntriesError && (
                <Alert
                  message="Failed to Load Fuel Entries"
                  description={fuelEntriesError}
                  type="error"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                />
              )}
              {!fuelEntriesError && fuelEntries.length > 0 && (
                <Alert
                  message="Data Loaded from API"
                  description={`Loaded ${fuelEntries.length} fuel entries from API`}
                  type="success"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                />
              )}
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button
                  type="default"
                  icon="download"
                  onClick={() => {
                    const columns = [
                      { title: 'Date', dataIndex: 'date', key: 'date', render: (date) => moment(date).format('YYYY-MM-DD') },
                      { title: 'Time', dataIndex: 'time', key: 'time' },
                      { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo' },
                      { 
                        title: 'Meter Reading', 
                        dataIndex: 'meterReading', 
                        key: 'meterReading', 
                        render: (reading, record) => {
                          // Show both hours and KWH readings when available
                          const readings = [];
                          
                          if (record.hoursReading !== null && record.hoursReading !== undefined) {
                            readings.push(`${record.hoursReading} HOURS`);
                          }
                          if (record.kwhReading !== null && record.kwhReading !== undefined) {
                            readings.push(`${record.kwhReading} KWH`);
                          }
                          if (record.kmReading !== null && record.kmReading !== undefined) {
                            readings.push(`${record.kmReading} KMs`);
                          }
                          
                          // Fallback to primary meter reading if no separate readings available
                          if (readings.length === 0 && reading !== null && reading !== undefined) {
                            if (mockAlert.hasKwh) {
                              readings.push(`${reading} KWH`);
                            } else if (mockAlert.hasHours) {
                              readings.push(`${reading} HOURS`);
                            } else if (mockAlert.hasKms) {
                              readings.push(`${reading} KMs`);
                            } else {
                              readings.push(`${reading} HOURS`); // Default fallback
                            }
                          }
                          
                          return readings.length > 0 ? readings.join(' | ') : '-';
                        }
                      },
                      { title: 'Quantity (L)', dataIndex: 'quantity', key: 'quantity' },
                      { title: 'Type', key: 'type', render: (_, record) => record.isFullTank ? 'Full Tank' : 'Partial' },
                      { title: 'Photos Count', key: 'photosCount', render: (_, record) => record.photos ? record.photos.length : 0 },
                    ];
                    convertTableToCSV(columns, fuelEntries.length > 0 ? fuelEntries : [], 'all_fuel_entries');
                  }}
                >
                  Download CSV
                </Button>
              </div>
              <Table
                dataSource={fuelEntries.length > 0 ? fuelEntries : []}
                loading={isLoadingFuelEntries}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    width: 100,
                    render: (date) => moment(date).format('YYYY-MM-DD'),
                  },
                  {
                    title: 'Time',
                    dataIndex: 'time',
                    key: 'time',
                    width: 80,
                  },
                  {
                    title: 'Voucher No',
                    dataIndex: 'voucherNo',
                    key: 'voucherNo',
                    width: 120,
                    render: (voucherNo, record) => (
                      <a
                        href={`#/vouchers/${voucherNo}/edit`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // In real app, navigate to voucher edit view
                          console.log('Open voucher:', voucherNo);
                        }}
                        style={{ color: '#1890ff' }}
                      >
                        {voucherNo}
                      </a>
                    ),
                  },
                  {
                    title: 'Meter Reading',
                    dataIndex: 'meterReading',
                    key: 'meterReading',
                    align: 'right',
                    width: 180,
                    render: (reading, record) => {
                      // Show both hours and KWH readings when available
                      const readings = [];
                      
                      if (record.hoursReading !== null && record.hoursReading !== undefined) {
                        readings.push(`${record.hoursReading} HOURS`);
                      }
                      if (record.kwhReading !== null && record.kwhReading !== undefined) {
                        readings.push(`${record.kwhReading} KWH`);
                      }
                      if (record.kmReading !== null && record.kmReading !== undefined) {
                        readings.push(`${record.kmReading} KMs`);
                      }
                      
                      // Fallback to primary meter reading if no separate readings available
                      if (readings.length === 0 && reading !== null && reading !== undefined) {
                        if (mockAlert.hasKwh) {
                          readings.push(`${reading} KWH`);
                        } else if (mockAlert.hasHours) {
                          readings.push(`${reading} HOURS`);
                        } else if (mockAlert.hasKms) {
                          readings.push(`${reading} KMs`);
                        } else {
                          readings.push(`${reading} HOURS`); // Default fallback
                        }
                      }
                      
                      return readings.length > 0 ? readings.join(' | ') : '-';
                    },
                  },
                  {
                    title: 'Quantity (L)',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    align: 'right',
                    width: 100,
                    render: (qty, record) => (
                      <span style={{ fontWeight: record.isFullTank ? 600 : 'normal' }}>
                        {qty}
                      </span>
                    ),
                  },
                  {
                    title: 'Type',
                    key: 'type',
                    width: 100,
                    render: (_, record) => (
                      <Tag color={record.isFullTank ? 'blue' : 'orange'}>
                        {record.isFullTank ? 'Full Tank' : 'Partial'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Photos',
                    key: 'photos',
                    width: 120,
                    render: (_, record) => {
                      if (!record.photos || record.photos.length === 0) {
                        return <span style={{ color: '#bfbfbf' }}>No photos</span>;
                      }
                      return (
                        <div>
                          <Button
                            type="link"
                            size="small"
                            icon="picture"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVoucherPhotos(record.photos);
                              setSelectedVoucherNo(record.voucherNo);
                              setPhotoViewerVisible(true);
                            }}
                          >
                            {record.photos.length} photo{record.photos.length > 1 ? 's' : ''}
                          </Button>
                        </div>
                      );
                    },
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="id"
              />
            </div>
          )}
        </Card>

        {isUsingAdjustedTimes && (
          <Alert
            message="Using sensor-adjusted refueling times. Original manual timestamps were adjusted."
            type="info"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setAnalysisRange([
                    moment(mockAlert.lastFullTankTime),
                    moment(mockAlert.refuelTime),
                  ]);
                }}
              >
                Reset to Manual
              </Button>
            }
          />
        )}

        {/* BODY */}
        <Row gutter={16}>
          {/* MAIN CONTENT */}
          <Col span={24}>
            <Card>
              <Tabs defaultActiveKey="trend">
                {/* TAB 1: TREND */}
                <TabPane 
                  tab={
                    <span>
                      F2F Consumption Data
                      {filteredTrendData && filteredTrendData.length > 0 && (() => {
                        const validRates = filteredTrendData
                          .filter(item => item.actualRate !== null && item.actualRate !== undefined)
                          .map(item => item.actualRate);
                        if (validRates.length > 0) {
                          const average = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
                          // Determine unit based on asset type
                          // Priority: KWH > KMS > HOURS (matching standard unit priority)
                          // Note: Data is always displayed in the converted format (KWH/L, KM/L, or L/Hours)
                          // regardless of how the standard is defined in the API
                          let unit = '';
                          if (mockAlert.hasKwh) {
                            // Always show KWH/L (data is converted from L/KWH if needed)
                            unit = 'KWH/L';
                          } else if (mockAlert.hasKms) {
                            // Always show KM/L (data is converted from L/KM if needed)
                            unit = 'KM/L';
                          } else if (mockAlert.hasHours) {
                            // Always show L/Hours (data is in L/Hours format)
                            unit = 'L/Hours';
                          }
                          return (
                            <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px', fontWeight: 'normal' }}>
                              (Avg: {average.toFixed(2)} {unit})
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </span>
                  }
                  key="trend"
                >
                  {/* Loading and Error States */}
                  {isLoadingTrendData && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 8, color: '#666' }}>
                        Loading trend data from API...
                      </div>
                    </div>
                  )}

                  {!isLoadingTrendData && trendDataError && (
                    <Alert
                      message="Failed to Load Trend Data"
                      description={trendDataError}
                      type="error"
                      showIcon
                      closable
                      style={{ marginBottom: 16 }}
                      action={
                        <Button
                          size="small"
                          onClick={() => {
                            // Retry loading
                            const loadTrendData = async () => {
                              setIsLoadingTrendData(true);
                              setTrendDataError(null);
                              try {
                                // Fetch past N events (based on trendPeriod)
                                const count = parseInt(trendPeriod, 10) || 7;
                                const pastEventsResult = await fetchLastFullTankEvents(
                                  mockAlert.assetCode,
                                  mockAlert.refuelTime,
                                  count
                                );
                                
                                if (!pastEventsResult.success) {
                                  setTrendDataError(pastEventsResult.error || 'Failed to fetch trend data');
                                  setTrendData([]);
                                } else {
                                  // Fetch current refuel event
                                  let currentRefuelEvent = null;
                                  const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');
                                  const currentRefuelMomentValue = currentRefuelMoment.valueOf();
                                  
                                  // First, check if current refuel event is already in past events
                                  const currentInPastEvents = pastEventsResult.events.find(event => {
                                    const eventMoment = moment(event.date);
                                    const timeDiff = Math.abs(eventMoment.valueOf() - currentRefuelMomentValue);
                                    return timeDiff < 300000; // 5 minutes
                                  });
                                  
                                  if (currentInPastEvents) {
                                    currentRefuelEvent = currentInPastEvents;
                                  } else {
                                    try {
                                      const assetResult = await findAssetIdByCode(mockAlert.assetCode);
                                      if (assetResult.success) {
                                        const assetId = assetResult.assetId;
                                        
                                        // Update assetCodeMap with asset info
                                        if (!assetCodeMap[mockAlert.assetCode]) {
                                          assetCodeMap[mockAlert.assetCode] = {};
                                        }
                                        assetCodeMap[mockAlert.assetCode] = {
                                          ...assetCodeMap[mockAlert.assetCode],
                                          assetId: assetId,
                                          altName: assetResult.altName,
                                          ownerCode: assetResult.ownerCode,
                                          assetModel: assetResult.assetModel,
                                          location: assetResult.location,
                                          subLocation: assetResult.subLocation
                                        };
                                        setAssetCodeMap({ ...assetCodeMap });
                                        
                                        // Update mockAlert with asset info
                                        // Always update from API data, even if null (to clear old localStorage values)
                                        setMockAlert(prev => ({
                                          ...prev,
                                          altName: assetResult.altName !== undefined ? assetResult.altName : prev.altName,
                                          ownerCode: assetResult.ownerCode !== undefined ? assetResult.ownerCode : prev.ownerCode
                                        }));
                                        const startMoment = currentRefuelMoment.clone().subtract(2, 'days');
                                        const endMoment = currentRefuelMoment.clone().add(1, 'day');
                                        
                                        const currentRefuelResult = await fetchFuelEntries(
                                          assetId,
                                          startMoment.toISOString(),
                                          endMoment.toISOString()
                                        );
                                        
                                        if (currentRefuelResult.success && currentRefuelResult.data) {
                                          currentRefuelEvent = currentRefuelResult.data.find(entry => {
                                            if (!entry.isFullTank) return false;
                                            
                                            const entryMoment = moment(entry.date);
                                            const timeDiff = Math.abs(entryMoment.valueOf() - currentRefuelMomentValue);
                                            
                                            // Match by voucher number (most reliable)
                                            if (mockAlert.voucherNo && entry.voucherNo === mockAlert.voucherNo) {
                                              return true;
                                            }
                                            
                                            // Match by time (within 5 minutes)
                                            if (timeDiff < 300000) {
                                              return true;
                                            }
                                            
                                            return false;
                                          });
                                        }
                                      }
                                    } catch (error) {
                                      console.warn('Error fetching current refuel event:', error);
                                    }
                                  }
                                  
                                  // Combine past events with current refuel event
                                  let allEvents = [...pastEventsResult.events];
                                  if (currentRefuelEvent && !currentInPastEvents) {
                                    const currentEventTime = moment(currentRefuelEvent.date);
                                    const isDuplicate = allEvents.some(event => {
                                      const eventTime = moment(event.date);
                                      const timeDiff = Math.abs(eventTime.valueOf() - currentEventTime.valueOf());
                                      if (timeDiff < 60000) {
                                        if (currentRefuelEvent.voucherNo && event.voucherNo) {
                                          return currentRefuelEvent.voucherNo === event.voucherNo;
                                        }
                                        return true;
                                      }
                                      return false;
                                    });
                                    
                                    if (!isDuplicate) {
                                      allEvents.push(currentRefuelEvent);
                                    }
                                  }
                                  
                                  // Deduplicate events by voucher number (vouchers should be unique)
                                  const voucherMap = new Map();
                                  allEvents.forEach(event => {
                                    if (event.voucherNo) {
                                      const existingEvent = voucherMap.get(event.voucherNo);
                                      if (!existingEvent) {
                                        voucherMap.set(event.voucherNo, event);
                                      } else {
                                        const existingDate = moment(existingEvent.date);
                                        const currentDate = moment(event.date);
                                        if (currentDate.isAfter(existingDate)) {
                                          console.log(`Duplicate voucher ${event.voucherNo} found. Keeping newer event (${event.date} vs ${existingEvent.date})`);
                                          voucherMap.set(event.voucherNo, event);
                                        } else {
                                          console.log(`Duplicate voucher ${event.voucherNo} found. Keeping existing event (${existingEvent.date} vs ${event.date})`);
                                        }
                                      }
                                    } else {
                                      const eventTime = moment(event.date);
                                      const isTimeDuplicate = Array.from(voucherMap.values()).some(e => {
                                        if (!e.voucherNo) {
                                          const eTime = moment(e.date);
                                          return Math.abs(eTime.valueOf() - eventTime.valueOf()) < 60000;
                                        }
                                        return false;
                                      });
                                      if (!isTimeDuplicate) {
                                        voucherMap.set(`no-voucher-${event.date}`, event);
                                      }
                                    }
                                  });
                                  
                                  allEvents = Array.from(voucherMap.values())
                                    .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
                                  
                                  console.log(`After deduplication: ${allEvents.length} unique events`);
                                  
                                  // Extract standard rate and variance threshold
                                  let extractedStandardRate = standardRate;
                                  if (allEvents.length > 0) {
                                    const firstEntry = allEvents[0];
                                    if (firstEntry.assetModel && firstEntry.assetModel.standardFuelConsumptionRate) {
                                      const standardRates = firstEntry.assetModel.standardFuelConsumptionRate;
                                      if (standardRates.length > 0) {
                                        const rawRate = standardRates[0].fuelQty;
                                        const normalizedRate = normalizeStandardRateForDisplay(rawRate, mockAlert.standardUnit);
                                        if (normalizedRate !== null) {
                                          extractedStandardRate = normalizedRate;
                                          setStandardRate(normalizedRate);
                                        }
                                      }
                                    }
                                    
                                    // Extract variance threshold from asset model
                                    if (firstEntry.assetModel && firstEntry.assetModel.fuelMileageThreshold && Array.isArray(firstEntry.assetModel.fuelMileageThreshold) && firstEntry.assetModel.fuelMileageThreshold.length > 0) {
                                      const firstThreshold = firstEntry.assetModel.fuelMileageThreshold[0];
                                      if (firstThreshold && firstThreshold.value !== null && firstThreshold.value !== undefined && !isNaN(firstThreshold.value) && firstThreshold.value > 0) {
                                        setVarianceThreshold(firstThreshold.value);
                                        console.log(`Extracted variance threshold from asset model: ${firstThreshold.value}`);
                                      }
                                    }
                                  }
                                  const transformedData = transformToTrendData(allEvents, extractedStandardRate, mockAlert.standardUnit);
                                  setTrendData(transformedData);
                                }
                              } catch (error) {
                                setTrendDataError(error.message || 'Failed to load trend data');
                                setTrendData([]);
                              } finally {
                                setIsLoadingTrendData(false);
                              }
                            };
                            loadTrendData();
                          }}
                        >
                          Retry
                        </Button>
                      }
                    />
                  )}

                  {!isLoadingTrendData && !trendDataError && trendData.length > 0 && (
                    <Alert
                      message="Data Loaded from API"
                      description={`Loaded ${trendData.length} full tank events for asset ${mockAlert.assetCode}${mockAlert.altName ? ` / ${mockAlert.altName}` : ''}${mockAlert.ownerCode ? ` / ${mockAlert.ownerCode}` : ''}`}
                      type="success"
                      showIcon
                      closable
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* Trend filters */}
                  <Row type="flex" justify="space-between" style={{ marginBottom: 12 }}>
                    <Col>
                      <span style={{ marginRight: 8 }}>Show:</span>
                      <Select
                        value={trendPeriod}
                        onChange={(value) => {
                          console.log(`Trend period changed to: ${value}`);
                          setTrendPeriod(value);
                        }}
                        style={{ width: 120, marginRight: 8 }}
                      >
                        <Option value="7">Last 7</Option>
                        <Option value="14">Last 14</Option>
                        <Option value="30">Last 30</Option>
                      </Select>
                      <Button
                        type="primary"
                        icon="reload"
                        loading={isLoadingTrendData}
                        onClick={async () => {
                          if (!mockAlert.assetCode || !mockAlert.refuelTime) {
                            console.warn('Cannot load trend data: missing required mockAlert data');
                            return;
                          }
                          
                          setIsLoadingTrendData(true);
                          setTrendDataError(null);

                          try {
                            // Fetch last N full tank events before current refuel (based on trendPeriod)
                            const count = parseInt(trendPeriod, 10) || 7;
                            console.log(`Refreshing trend data: fetching ${count} full tank events for asset ${mockAlert.assetCode}`);
                            const pastEventsResult = await fetchLastFullTankEvents(
                              mockAlert.assetCode,
                              mockAlert.refuelTime,
                              count
                            );

                            if (!pastEventsResult.success) {
                              setTrendDataError(pastEventsResult.error || 'Failed to fetch trend data');
                              setTrendData([]);
                              setIsLoadingTrendData(false);
                              return;
                            }
                            
                            console.log(`Fetched ${pastEventsResult.events.length} past full tank events`);

                            // Fetch the current refuel event
                            let currentRefuelEvent = null;
                            const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');
                            const currentRefuelMomentValue = currentRefuelMoment.valueOf();
                            
                            // Check if current refuel event is already in past events
                            const currentInPastEvents = pastEventsResult.events.find(event => {
                              const eventMoment = moment(event.date);
                              const timeDiff = Math.abs(eventMoment.valueOf() - currentRefuelMomentValue);
                              return timeDiff < 300000; // 5 minutes
                            });
                            
                            if (currentInPastEvents) {
                              console.log('Current refuel event found in past events list');
                              currentRefuelEvent = currentInPastEvents;
                            } else {
                              // Try to fetch it separately
                              try {
                                const assetResult = await findAssetIdByCode(mockAlert.assetCode);
                                
                                if (assetResult.success) {
                                  const assetId = assetResult.assetId;
                                  
                                  // Update assetCodeMap with asset info
                                  if (!assetCodeMap[mockAlert.assetCode]) {
                                    assetCodeMap[mockAlert.assetCode] = {};
                                  }
                                  assetCodeMap[mockAlert.assetCode] = {
                                    ...assetCodeMap[mockAlert.assetCode],
                                    assetId: assetId,
                                    altName: assetResult.altName,
                                    ownerCode: assetResult.ownerCode,
                                    assetModel: assetResult.assetModel,
                                    location: assetResult.location,
                                    subLocation: assetResult.subLocation
                                  };
                                  setAssetCodeMap({ ...assetCodeMap });
                                  
                                  // Update mockAlert with asset info
                                  // Always update from API data, even if null (to clear old localStorage values)
                                  setMockAlert(prev => ({
                                    ...prev,
                                    altName: assetResult.altName !== undefined ? assetResult.altName : prev.altName,
                                    ownerCode: assetResult.ownerCode !== undefined ? assetResult.ownerCode : prev.ownerCode,
                                    location: assetResult.location !== undefined ? assetResult.location : prev.location,
                                    subLocation: assetResult.subLocation !== undefined ? assetResult.subLocation : prev.subLocation
                                  }));
                                  const startMoment = currentRefuelMoment.clone().subtract(2, 'days');
                                  const endMoment = currentRefuelMoment.clone().add(1, 'day');
                                  
                                  const currentRefuelResult = await fetchFuelEntries(
                                    assetId,
                                    startMoment.toISOString(),
                                    endMoment.toISOString()
                                  );
                                  
                                  if (currentRefuelResult.success && currentRefuelResult.data) {
                                    currentRefuelEvent = currentRefuelResult.data.find(entry => {
                                      if (!entry.isFullTank) return false;
                                      
                                      const entryMoment = moment(entry.date);
                                      const timeDiff = Math.abs(entryMoment.valueOf() - currentRefuelMomentValue);
                                      
                                      // Match by voucher number (most reliable)
                                      if (mockAlert.voucherNo && entry.voucherNo === mockAlert.voucherNo) {
                                        return true;
                                      }
                                      
                                      // Match by time (within 5 minutes)
                                      return timeDiff < 300000; // 5 minutes
                                    });
                                    
                                    if (currentRefuelEvent) {
                                      console.log('Current refuel event fetched separately');
                                    }
                                  }
                                }
                              } catch (error) {
                                console.error('Error fetching current refuel event:', error);
                              }
                            }

                            // Combine past events with current event
                            let allEvents = [...pastEventsResult.events];
                            if (currentRefuelEvent && !currentInPastEvents) {
                              allEvents.push(currentRefuelEvent);
                            }

                            // Deduplicate events by voucher number (vouchers should be unique)
                            // If multiple events have the same voucher number, keep the one with the most recent date
                            const voucherMap = new Map();
                            allEvents.forEach(event => {
                              if (event.voucherNo) {
                                const existingEvent = voucherMap.get(event.voucherNo);
                                if (!existingEvent) {
                                  voucherMap.set(event.voucherNo, event);
                                } else {
                                  const existingDate = moment(existingEvent.date);
                                  const currentDate = moment(event.date);
                                  if (currentDate.isAfter(existingDate)) {
                                    console.log(`Duplicate voucher ${event.voucherNo} found. Keeping newer event (${event.date} vs ${existingEvent.date})`);
                                    voucherMap.set(event.voucherNo, event);
                                  } else {
                                    console.log(`Duplicate voucher ${event.voucherNo} found. Keeping existing event (${existingEvent.date} vs ${event.date})`);
                                  }
                                }
                              } else {
                                const eventTime = moment(event.date);
                                const isTimeDuplicate = Array.from(voucherMap.values()).some(e => {
                                  if (!e.voucherNo) {
                                    const eTime = moment(e.date);
                                    return Math.abs(eTime.valueOf() - eventTime.valueOf()) < 60000;
                                  }
                                  return false;
                                });
                                if (!isTimeDuplicate) {
                                  voucherMap.set(`no-voucher-${event.date}`, event);
                                }
                              }
                            });
                            
                            allEvents = Array.from(voucherMap.values())
                              .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
                            
                            console.log(`After deduplication: ${allEvents.length} unique events`);

                            // Get standard rate from state or mockAlert
                            const currentStandardRate = standardRate || mockAlert.standardRate || null;

                            // Transform to trend data format
                            const transformed = transformToTrendData(allEvents, currentStandardRate, mockAlert.standardUnit);
                            
                            // Sort by date descending (newest first)
                            transformed.sort((a, b) => {
                              const dateA = moment(a.refuelTime, 'YYYY-MM-DD HH:mm:ss');
                              const dateB = moment(b.refuelTime, 'YYYY-MM-DD HH:mm:ss');
                              return dateB.valueOf() - dateA.valueOf();
                            });

                            // Post-process to fill in missing data for current refuel event from mockAlert
                            const currentRefuelMomentForRefresh = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss');
                            const transformedWithCurrent = transformed.map(item => {
                              const itemMoment = moment(item.refuelTime, 'YYYY-MM-DD HH:mm:ss');
                              const timeDiff = Math.abs(itemMoment.valueOf() - currentRefuelMomentForRefresh.valueOf());
                              
                              // Check if this is the current refuel event (within 5 minutes)
                              if (timeDiff < 300000) { // 5 minutes
                                // This is the current event - fill in missing data from mockAlert
                                const updatedItem = { ...item };
                                
                                // Fill in hoursRun, kmsRun, or kwhRun from mockAlert if missing
                                if (mockAlert.hasHours && mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
                                  updatedItem.hoursRun = mockAlert.hoursRun;
                                }
                                if (mockAlert.hasKms && mockAlert.kmsRun !== null && mockAlert.kmsRun !== undefined && mockAlert.kmsRun > 0) {
                                  updatedItem.kmsRun = mockAlert.kmsRun;
                                }
                                if (mockAlert.hasKwh && mockAlert.kwhRun !== null && mockAlert.kwhRun !== undefined && mockAlert.kwhRun > 0) {
                                  updatedItem.kwhRun = mockAlert.kwhRun;
                                }
                                
                                // Recalculate actualRate if we now have runValue and fuelAdded
                                // Use updatedItem.fuelAdded (from transformToTrendData) which includes intermediate non-full-tank entries
                                const runValue = mockAlert.hasHours ? updatedItem.hoursRun : (mockAlert.hasKms ? updatedItem.kmsRun : (mockAlert.hasKwh ? updatedItem.kwhRun : null));
                                const fuelAdded = updatedItem.fuelAdded ?? mockAlert.fuelAdded;
                                
                                if (runValue !== null && runValue > 0 && fuelAdded > 0) {
                                  if (mockAlert.hasKms) {
                                    // KM-based: KM/L = kmsRun / fuelAdded
                                    updatedItem.actualRate = runValue / fuelAdded;
                                  } else if (mockAlert.hasHours) {
                                    // HOURS-based: L/Hours = fuelAdded / hoursRun
                                    updatedItem.actualRate = fuelAdded / runValue;
                                  } else if (mockAlert.hasKwh) {
                                    // KWH-based: KWH/L = kwhRun / fuelAdded
                                    updatedItem.actualRate = runValue / fuelAdded;
                                  }
                                  
                                  // Recalculate deviation if standard rate is available
                                  if (updatedItem.actualRate !== null && standardRate !== null && standardRate > 0) {
                                    if (mockAlert.hasKwh || mockAlert.hasKms) {
                                      updatedItem.deviation = ((standardRate - updatedItem.actualRate) / standardRate) * 100;
                                    } else {
                                      updatedItem.deviation = ((updatedItem.actualRate - standardRate) / standardRate) * 100;
                                    }
                                  }
                                }
                                
                                // NOTE: Do NOT overwrite fuelAdded here - it already contains the correct total
                                // (full tank + intermediate entries) as calculated by transformToTrendData
                                // Only update fullTankFuel if it's missing or incorrect
                                if (mockAlert.fuelAdded && mockAlert.fuelAdded > 0 && !updatedItem.fullTankFuel) {
                                  updatedItem.fullTankFuel = mockAlert.fuelAdded;
                                }
                                
                                syncMeterRunsFromRunFields(updatedItem);

                                console.log('Updated current refuel event in trend data (refresh):', {
                                  refuelTime: updatedItem.refuelTime,
                                  hoursRun: updatedItem.hoursRun,
                                  kmsRun: updatedItem.kmsRun,
                                  fuelAdded: updatedItem.fuelAdded,
                                  fullTankFuel: updatedItem.fullTankFuel,
                                  intermediateFuelCount: updatedItem.intermediateFuelCount,
                                  actualRate: updatedItem.actualRate,
                                });
                                
                                return updatedItem;
                              }
                              
                              return item;
                            });

                            setTrendData(transformedWithCurrent);
                            console.log(`Loaded ${transformedWithCurrent.length} trend data entries (including current event)`);
                            
                            // Save to localStorage
                            saveApiDataToStorage({ trendData: transformedWithCurrent });
                            
                            setIsLoadingTrendData(false);
                          } catch (error) {
                            console.error('Error loading trend data:', error);
                            setTrendDataError(error.message || 'Failed to load trend data');
                            setIsLoadingTrendData(false);
                          }
                        }}
                      >
                        Refresh
                      </Button>
                    </Col>
                  </Row>

                  {/* Trend chart */}
                  {!isLoadingTrendData && filteredTrendData && filteredTrendData.length > 0 ? (
                    <ConsumptionTrendChart 
                      data={filteredTrendData} 
                      standardRate={resolvedStandardRate}
                      unit={mockAlert.hasKms ? 'KM/L' : (mockAlert.hasKwh ? 'KWH/L' : 'L/HOURS')}
                      operatingLevelUnit={operatingLevelUnit}
                      maxOperatingLevel={maxOperatingLevel}
                    />
                  ) : (
                  <div
                    style={{
                      height: 220,
                      border: '1px dashed #d9d9d9',
                      borderRadius: 4,
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#bfbfbf',
                      background: '#fafafa',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <Icon type="line-chart" style={{ fontSize: 32, marginBottom: 8 }} />
                      <div>Trend Chart (Actual vs Standard)</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                          {isLoadingTrendData ? 'Loading chart data...' : 'No data available. Click Refresh to load data.'}
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Trend table */}
                  {!isLoadingTrendData && (
                    <div>
                      {/* Exclusion info banner */}
                      {hasActiveExclusions && (
                        <Alert
                          message="Analysis Mode Active"
                          description={
                            <span>
                              Some full tank events are temporarily marked as non-full-tank for analysis. 
                              Consumption rates have been recalculated. 
                              <span style={{ color: '#ff4d4f', textDecoration: 'line-through', marginLeft: 8 }}>Red strikethrough</span> = excluded events, 
                              <span style={{ color: '#52c41a', marginLeft: 8, backgroundColor: '#f6ffed', padding: '0 4px' }}>Green background</span> = recalculated events.
                            </span>
                          }
                          type="info"
                          showIcon
                          style={{ marginBottom: 16 }}
                          action={
                            <Button size="small" onClick={clearAllExclusions}>
                              Clear All Exclusions
                            </Button>
                          }
                        />
                      )}
                      {/* Maximum Operating Level — manual entry until it lands on the asset model.
                          Empty by default: with no maximum stated no percentage is shown, rather
                          than a percentage measured against an assumed ceiling. */}
                      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ marginRight: 8, fontSize: 13, fontWeight: 500 }}>
                          Maximum Operating Level:
                        </span>
                        <InputNumber
                          size="small"
                          min={0}
                          style={{ width: 130 }}
                          value={maxOperatingLevel}
                          placeholder="Not set"
                          disabled={!operatingLevelUnit}
                          onChange={value => setMaxOperatingLevel(
                            typeof value === 'number' && Number.isFinite(value) ? value : null
                          )}
                        />
                        <span style={{ marginLeft: 8, fontSize: 13, color: '#595959' }}>
                          {operatingLevelUnit || '-'}
                        </span>
                        {maxOperatingLevel !== null && (
                          <Button
                            size="small"
                            type="link"
                            onClick={() => setMaxOperatingLevel(null)}
                          >
                            Clear
                          </Button>
                        )}
                        <span style={{ marginLeft: 12, fontSize: 12, color: '#8c8c8c' }}>
                          {!operatingLevelUnit
                            ? 'No Operating Level for this asset — it needs two meters recorded on the same entry.'
                            : maxOperatingLevel === null
                              ? `Enter the rated maximum in ${operatingLevelUnit} to show each row as a % of it. Nothing is assumed until you do.`
                              : maxOperatingLevel > 0
                                ? 'Manual entry for now — this will come from the asset model definition.'
                                : 'Maximum must be greater than 0 — no percentage is shown.'}
                        </span>
                      </div>
                      {/* Legend for the multi-meter columns */}
                      <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
                        <b>Meter Run</b> lists every meter recorded between two full tanks. The
                        <b style={{ color: '#262626' }}> bold </b>
                        reading is the priority meter (KWH &gt; KMs &gt; Hours) that Actual, Standard and
                        Deviation are calculated on; the readings below it are shown for reference only.
                        <b> Operating Level</b> divides the higher-priority run by the lower-priority run
                        for the same interval — KWH/Hour shows the power load on a DG, KM/Hour shows average
                        speed on a vehicle. It does not affect the rate or the alert status. Once a Maximum
                        Operating Level is entered above, the <b>percentage of that maximum becomes the headline
                        figure</b> in each row with the measured rate underneath it; anything over 100% is
                        flagged in red, and anything under 50% in orange — the asset ran lightly loaded.
                      </div>
                      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          {hasActiveExclusions && (
                            <Tag color="orange">
                              {Object.values(excludedFullTankEvents).filter(v => v).length} event(s) excluded
                            </Tag>
                          )}
                        </div>
                        <div>
                          {hasActiveExclusions && (
                            <Button
                              type="default"
                              icon="undo"
                              onClick={clearAllExclusions}
                              style={{ marginRight: 8 }}
                            >
                              Reset Exclusions
                            </Button>
                          )}
                          <Button
                            type="default"
                            icon="download"
                            onClick={() => {
                              convertTableToCSV(trendColumns, filteredTrendData, 'consumption_trend');
                            }}
                          >
                            Download CSV
                          </Button>
                        </div>
                      </div>
                      <Table
                        columns={trendColumns}
                        dataSource={filteredTrendData}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        loading={isLoadingTrendData}
                        onRow={record => ({
                          onClick: () => {
                            setSelectedTrendRow(record.id);
                            // In real app, update IoT chart range to that pair
                            console.log('select full tank pair', record);
                          },
                          style: {
                            cursor: 'pointer',
                            backgroundColor: record.isExcluded 
                              ? '#fff1f0' 
                              : record.hasRecalculation 
                                ? '#f6ffed' 
                                : selectedTrendRow === record.id 
                                  ? '#e6f7ff' 
                                  : 'white',
                            textDecoration: record.isExcluded ? 'line-through' : 'none',
                            opacity: record.isExcluded ? 0.6 : 1,
                          },
                        })}
                      />
                    </div>
                  )}
                </TabPane>

                {/* TAB 2: IoT CONSUMPTION DATA */}
                <TabPane
                  tab={
                    <span>
                      IoT Consumption Data
                      {iotConsumptionData && iotConsumptionData.length > 0 && (() => {
                        const validRates = iotConsumptionData
                          .filter(day => day.unit === iotChartUnit && day.consumptionRate !== null && day.consumptionRate !== undefined)
                          .map(day => day.consumptionRate);
                        const otherUnitDays = iotConsumptionData.filter(day => day.unit !== iotChartUnit).length;
                        if (validRates.length > 0) {
                          const average = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
                          return (
                            <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px', fontWeight: 'normal' }}>
                              (Avg: {average.toFixed(2)} {iotChartUnit}
                              {otherUnitDays > 0 ? `, ${otherUnitDays} day(s) in another unit excluded` : ''})
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </span>
                  }
                  key="iot-consumption"
                >
                  <Card
                    type="inner"
                    title={
                      <span>
                        <Icon type="line-chart" style={{ marginRight: 8 }} />
                        IoT Consumption Rate Analysis
                      </span>
                    }
                    style={{ marginBottom: 16 }}
                  >
                    <Row type="flex" justify="space-between" style={{ marginBottom: 12 }}>
                      <Col>
                        <span style={{ marginRight: 8 }}>Period:</span>
                        <Select
                          value={iotConsumptionPeriod}
                          onChange={setIotConsumptionPeriod}
                          style={{ width: 120, marginRight: 8 }}
                        >
                          <Select.Option value="7">7 Days</Select.Option>
                          <Select.Option value="14">14 Days</Select.Option>
                          <Select.Option value="30">30 Days</Select.Option>
                        </Select>
                        <Button
                          type="primary"
                          icon="reload"
                          loading={isLoadingIotConsumption}
                          onClick={async () => {
                            setIsLoadingIotConsumption(true);
                            try {
                              const endMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                              const days = parseInt(iotConsumptionPeriod, 10);
                              const startMoment = endMoment.clone().subtract(days, 'days');
                              
                              // For IoT Consumption Rate Analysis, we only need daily data
                              // Use daily summary API only (no need for period API)
                              const assetResult = await findAssetIdByCode(mockAlert.assetCode);
                              if (!assetResult.success) {
                                message.error('Failed to find asset ID');
                                setIsLoadingIotConsumption(false);
                                return;
                              }
                              
                              // Calculate date range for full days only
                              const startOfFirstDay = startMoment.clone().startOf('day');
                              const endOfLastDay = endMoment.clone().endOf('day');
                              const startTimestamp = startOfFirstDay.clone().utc().valueOf();
                              const endTimestamp = endOfLastDay.clone().utc().valueOf();
                              
                              // Fetch daily summaries only
                              const summaryResult = await fetchDailyIotSummary(assetResult.assetId, startTimestamp, endTimestamp);
                              
                              if (!summaryResult.success) {
                                message.error(`Failed to fetch daily IoT summaries: ${summaryResult.error || 'Unknown error'}`);
                                setIotConsumptionData([]);
                                setIsLoadingIotConsumption(false);
                                return;
                              }
                              
                              if (!summaryResult.data || summaryResult.data.length === 0) {
                                message.info('No IoT consumption data available for the selected period');
                                setIotConsumptionData([]);
                                setIsLoadingIotConsumption(false);
                                return;
                              }
                              
                              // Helper functions
                              const msToTimeString = (ms) => {
                                if (ms === null || ms === undefined) return null;
                                const totalSeconds = Math.floor(ms / 1000);
                                const hours = Math.floor(totalSeconds / 3600);
                                const minutes = Math.floor((totalSeconds % 3600) / 60);
                                const seconds = totalSeconds % 60;
                                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                              };
                              
                              const timeStringToHours = (timeStr) => {
                                if (!timeStr) return null;
                                const parts = timeStr.split(':');
                                if (parts.length !== 3) return null;
                                const hours = parseInt(parts[0], 10);
                                const minutes = parseInt(parts[1], 10);
                                const seconds = parseInt(parts[2], 10);
                                if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
                                return hours + (minutes / 60) + (seconds / 3600);
                              };
                              
                              // Process daily summaries to dayWiseData format
                              const dayWiseData = summaryResult.data.map((summary) => {
                                const processed = summary.deviceProcessedData || summary;
                                const newConsumed = processed.newConsumed || {};
                                
                                // Extract date
                                let dateString = null;
                                const dateTimestamp = summary.date_timestamp || processed.date_timestamp;
                                if (dateTimestamp) {
                                  const utcMoment = moment.utc(dateTimestamp);
                                  const istMoment = utcMoment.clone().utcOffset('+05:30');
                                  dateString = istMoment.format('YYYY-MM-DD');
                                } else {
                                  const rawDate = summary.date || processed.date;
                                  if (rawDate) {
                                    const parsedDate = moment.utc(rawDate);
                                    if (parsedDate.isValid()) {
                                      const istMoment = parsedDate.clone().utcOffset('+05:30');
                                      dateString = istMoment.format('YYYY-MM-DD');
                                    }
                                  }
                                }
                                
                                if (!dateString) return null;
                                
                                // Extract data
                                const movingTime = processed.moving_time || 0;
                                const idealTime = processed.ideal_time || 0;
                                const ignitionTime = processed.ignition_time || 0;
                                const distanceTravelledMeters = processed.distance_travelled || 0;
                                const distanceTravelled = distanceTravelledMeters / 1000;
                                const fuelConsumed = newConsumed.fuel_consumed || processed.fuel_consumed || 0;
                                const movingHours = movingTime / (1000 * 60 * 60);
                                const avgSpeedFromSummary = processed.average_speed ?? processed.avg_speed;
                                const averageSpeed = avgSpeedFromSummary !== undefined && avgSpeedFromSummary !== null && !isNaN(avgSpeedFromSummary)
                                  ? Number(avgSpeedFromSummary)
                                  : (movingHours > 0 && distanceTravelled > 0 ? (distanceTravelled / movingHours) : null);
                                
                                return {
                                  date: dateString,
                                  ignitionTime: ignitionTime > 0 ? msToTimeString(ignitionTime) : null,
                                  distanceTravelled: distanceTravelled > 0 ? distanceTravelled : null,
                                  fuelConsumed: fuelConsumed > 0 ? fuelConsumed : null,
                                  averageSpeed: averageSpeed,
                                };
                              }).filter(day => day !== null);
                              
                              const processedData = dayWiseData.map(day => {
                                let consumptionRate = null;
                                let unit = '';
                                
                                const isKmBased = mockAlert.hasKms;
                                const isHoursBased = mockAlert.hasHours;
                                const isKwhBased = mockAlert.hasKwh;
                                const standardUnit = mockAlert.standardUnit || '';
                                const isLPerHour = standardUnit === 'L_PER_HOUR' || standardUnit === 'LTR_PER_HOUR' || standardUnit.includes('HOUR');
                                const isHourPerL = standardUnit === 'HOUR_PER_L' || standardUnit === 'HOURS_PER_L';
                                
                                if (isKmBased && day.distanceTravelled && day.fuelConsumed && day.fuelConsumed > 0) {
                                  // Always KM/L, same as the initial load: the standard handed to
                                  // IoTConsumptionChart is already KM/L. 'L_PER_KM' only records how
                                  // the asset model stores the rate, not the display unit.
                                  consumptionRate = day.distanceTravelled / day.fuelConsumed;
                                  unit = 'KM/L';
                                } else if (isKwhBased) {
                                  // For KWH-based assets, try KWH first, fallback to hours if KWH data not available
                                  if (day.kwhRun && day.fuelConsumed && day.fuelConsumed > 0) {
                                    // KWH data available: calculate as KWH/L
                                    consumptionRate = day.kwhRun / day.fuelConsumed;
                                    unit = 'KWH/L';
                                  } else if (day.ignitionTime && day.fuelConsumed && day.fuelConsumed > 0) {
                                    // KWH data not available, fallback to hours-based calculation
                                    const hours = timeStringToHours(day.ignitionTime);
                                    if (hours && hours > 0) {
                                      if (isLPerHour || (!isHourPerL && !isLPerHour)) {
                                        consumptionRate = day.fuelConsumed / hours;
                                        unit = 'L/Hours';
                                      } else {
                                        consumptionRate = hours / day.fuelConsumed;
                                        unit = 'Hours/L';
                                      }
                                    }
                                  }
                                } else if (isHoursBased && day.ignitionTime && day.fuelConsumed && day.fuelConsumed > 0) {
                                  const hours = timeStringToHours(day.ignitionTime);
                                  if (hours && hours > 0) {
                                    if (isLPerHour || (!isHourPerL && !isLPerHour)) {
                                      consumptionRate = day.fuelConsumed / hours;
                                      unit = 'L/Hours';
                                    } else {
                                      consumptionRate = hours / day.fuelConsumed;
                                      unit = 'Hours/L';
                                    }
                                  }
                                }
                                
                                return {
                                  date: day.date || day.dateStr || '',
                                  consumptionRate: consumptionRate,
                                  unit: unit,
                                  fuelConsumed: day.fuelConsumed || 0,
                                  possibleRefuel: day.possibleRefuel ?? null,
                                  possibleDrain: day.possibleDrain ?? null,
                                  distanceTravelled: day.distanceTravelled || null,
                                  averageSpeed: day.averageSpeed ?? null,
                                  totalIgnitionTime: day.ignitionTime ? timeStringToHours(day.ignitionTime) * 60 : null, // Convert to minutes for display
                                  kwhRun: day.kwhRun || null,
                                };
                              }).filter(day => day.consumptionRate !== null)
                              .sort((a, b) => {
                                // Sort by date in descending order (newest first)
                                return moment(b.date).valueOf() - moment(a.date).valueOf();
                              });
                              
                              setIotConsumptionData(processedData);
                              message.success(`Loaded ${processedData.length} days of IoT consumption data`);
                            } catch (error) {
                              console.error('Error refreshing IoT consumption data:', error);
                              message.error('Failed to refresh IoT consumption data');
                            } finally {
                              setIsLoadingIotConsumption(false);
                            }
                          }}
                        >
                          Refresh
                        </Button>
                      </Col>
                    </Row>

                    {/* IoT Consumption Chart */}
                    {!isLoadingIotConsumption && iotConsumptionData && iotConsumptionData.length > 0 ? (
                      <IoTConsumptionChart 
                        data={iotConsumptionData} 
                        standardRate={resolvedStandardRate}
                        unit={iotChartUnit}
                      />
                    ) : (
                      <div
                        style={{
                          height: 220,
                          border: '1px dashed #d9d9d9',
                          borderRadius: 4,
                          marginBottom: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#bfbfbf',
                          background: '#fafafa',
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <Icon type="line-chart" style={{ fontSize: 32, marginBottom: 8 }} />
                          <div>IoT Consumption Rate Chart</div>
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            {isLoadingIotConsumption ? 'Loading chart data...' : 'No data available. Click Refresh to load data.'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* IoT Consumption Data Table */}
                    {!isLoadingIotConsumption && iotConsumptionData && iotConsumptionData.length > 0 && (
                      <div>
                        <div style={{ marginBottom: 16, textAlign: 'right' }}>
                          <Button
                            type="default"
                            icon="download"
                            onClick={() => {
                              const columns = [
                                { title: 'Date', dataIndex: 'date', key: 'date' },
                                { title: 'Consumption Rate', dataIndex: 'consumptionRate', key: 'consumptionRate', render: (val) => formatNumberSafe(val) },
                                { title: 'Rate Unit', dataIndex: 'unit', key: 'unit' },
                                { title: 'Fuel Consumed (L)', dataIndex: 'fuelConsumed', key: 'fuelConsumed', render: (val) => formatNumberSafe(val) },
                                { title: 'Possible Refuel (L)', dataIndex: 'possibleRefuel', key: 'possibleRefuel', render: (val) => formatNumberSafe(val) },
                                { title: 'Possible Drain (L)', dataIndex: 'possibleDrain', key: 'possibleDrain', render: (val) => formatNumberSafe(val) },
                                { title: 'Distance Travelled (KM)', dataIndex: 'distanceTravelled', key: 'distanceTravelled', render: (val) => formatNumberSafe(val) },
                                { title: 'Average Speed (KM/h)', dataIndex: 'averageSpeed', key: 'averageSpeed', render: (val) => formatNumberSafe(val) },
                                { title: 'Total Ignition Time (min)', dataIndex: 'totalIgnitionTime', key: 'totalIgnitionTime', render: (val) => formatNumberSafe(val) },
                              ];
                              convertTableToCSV(columns, iotConsumptionData, 'iot_consumption_data');
                            }}
                          >
                            Download CSV
                          </Button>
                        </div>
                        <Table
                          columns={[
                            {
                              title: 'Date',
                              dataIndex: 'date',
                              key: 'date',
                              width: 120,
                              render: (date) => moment(date).format('YYYY-MM-DD'),
                            },
                            {
                              title: 'Consumption Rate',
                              dataIndex: 'consumptionRate',
                              key: 'consumptionRate',
                              align: 'right',
                              width: 180,
                              // Each day carries its own unit; a day not in the tab's unit is flagged.
                              render: (rate, record) => (
                                <span style={record.unit !== iotChartUnit ? { color: '#fa8c16' } : undefined}>
                                  {formatNumberSafe(rate)} {record.unit || ''}
                                </span>
                              ),
                            },
                            {
                              title: 'Fuel Consumed (L)',
                              dataIndex: 'fuelConsumed',
                              key: 'fuelConsumed',
                              align: 'right',
                              width: 120,
                              render: (val) => formatNumberSafe(val),
                            },
                            {
                              title: 'Possible Refuel (L)',
                              dataIndex: 'possibleRefuel',
                              key: 'possibleRefuel',
                              align: 'right',
                              width: 140,
                              render: (val) => formatNumberSafe(val),
                            },
                            {
                              title: 'Possible Drain (L)',
                              dataIndex: 'possibleDrain',
                              key: 'possibleDrain',
                              align: 'right',
                              width: 140,
                              render: (val) => formatNumberSafe(val),
                            },
                            {
                              title: 'Distance Travelled (KM)',
                              dataIndex: 'distanceTravelled',
                              key: 'distanceTravelled',
                              align: 'right',
                              width: 150,
                              render: (val) => formatNumberSafe(val),
                            },
                            {
                              title: 'Average Speed (KM/h)',
                              dataIndex: 'averageSpeed',
                              key: 'averageSpeed',
                              align: 'right',
                              width: 160,
                              render: (val) => formatNumberSafe(val),
                            },
                            {
                              title: 'Total Ignition Time (min)',
                              dataIndex: 'totalIgnitionTime',
                              key: 'totalIgnitionTime',
                              align: 'right',
                              width: 150,
                              render: (val) => formatNumberSafe(val),
                            },
                          ]}
                          dataSource={iotConsumptionData}
                          rowKey="date"
                          size="small"
                          pagination={false}
                          loading={isLoadingIotConsumption}
                        />
                      </div>
                    )}
                  </Card>
                </TabPane>

                {/* TAB 3: IOT TIMELINE */}
                <TabPane
                  tab="IoT Timeline (Between Last & Current Full Tank)"
                  key="iot"
                >
                  {/* Refueling Time Alignment */}
                  <Card
                    type="inner"
                    title={
                      <span>
                        <Icon type="clock-circle-o" style={{ marginRight: 8 }} />
                        Refueling Time Alignment
                      </span>
                    }
                    style={{ marginBottom: 12 }}
                  >
                    <Row gutter={16} style={{ marginBottom: 8 }}>
                      <Col span={12}>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Manual Last Full Tank
                        </div>
                        <div>{mockAlert.lastFullTankTime ? moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss') : 'N/A'}</div>
                      </Col>
                      <Col span={12}>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Manual Current Full Tank
                        </div>
                        <div>{mockAlert.refuelTime ? moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss') : 'N/A'}</div>
                      </Col>
                    </Row>

                    {mockIotData.refuelEvents &&
                      mockIotData.refuelEvents.length > 0 && (
                        <Row style={{ marginBottom: 8 }}>
                          <Col span={24}>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#8c8c8c',
                                marginBottom: 4,
                              }}
                            >
                              Sensor-detected refuel events:
                            </div>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                              Click on the date pickers below to see detected refuel events
                            </div>
                          </Col>
                        </Row>
                      )}

                    <Row gutter={16}>
                      <Col span={12}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                          Starting Time (Last Full Tank)
                        </div>
                        <Row gutter={8}>
                          <Col span={18}>
                            <DatePicker
                              showTime
                              format="YYYY-MM-DD HH:mm:ss"
                              value={analysisRange.length > 0 && analysisRange[0] ? analysisRange[0] : (mockAlert.lastFullTankTime ? moment(mockAlert.lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30') : null)}
                              onChange={handleStartTimeChange}
                              style={{ width: '100%' }}
                              placeholder="Select start time"
                            />
                          </Col>
                          <Col span={6}>
                            <Button
                              type="default"
                              icon="search"
                              loading={isLoadingIotEvents}
                              onClick={async () => {
                                // Load events (will skip if already loaded for same asset/period)
                                await loadIotRefuelEvents(false);
                                setStartEventsModalVisible(true);
                              }}
                              style={{ width: '100%' }}
                            >
                              Events
                            </Button>
                          </Col>
                        </Row>
                        {selectedStartEventId && (
                          <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
                            <Icon type="check-circle" /> Using detected refuel event
                          </div>
                        )}
                      </Col>
                      <Col span={12}>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                          Ending Time (Current Full Tank)
                        </div>
                        <Row gutter={8}>
                          <Col span={18}>
                            <DatePicker
                              showTime
                              format="YYYY-MM-DD HH:mm:ss"
                              value={analysisRange.length > 1 && analysisRange[1] ? analysisRange[1] : (mockAlert.refuelTime ? moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30') : null)}
                              onChange={handleEndTimeChange}
                              style={{ width: '100%' }}
                              placeholder="Select end time"
                            />
                          </Col>
                          <Col span={6}>
                            <Button
                              type="default"
                              icon="search"
                              loading={isLoadingIotEvents}
                              onClick={async () => {
                                // Load events (will skip if already loaded for same asset/period)
                                await loadIotRefuelEvents(false);
                                setEndEventsModalVisible(true);
                              }}
                              style={{ width: '100%' }}
                            >
                              Events
                            </Button>
                          </Col>
                        </Row>
                        {selectedEndEventId && (
                          <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
                            <Icon type="check-circle" /> Using detected refuel event
                          </div>
                        )}
                      </Col>
                    </Row>
                    
                    {/* Fetch Data Button */}
                    <Row style={{ marginTop: 16 }}>
                      <Col span={24}>
                        <Button
                          type="primary"
                          icon="download"
                          loading={isLoadingData}
                          onClick={handleFetchData}
                          disabled={analysisRange.length !== 2 || !analysisRange[0] || !analysisRange[1]}
                          size="large"
                          block
                        >
                          {isLoadingData ? 'Loading Data...' : dataLoaded ? 'Reload Data' : 'Fetch IoT Data'}
                        </Button>
                        {dataLoaded && analysisRange.length === 2 && (
                          <div style={{ fontSize: 11, color: '#52c41a', marginTop: 8, textAlign: 'center' }}>
                            <Icon type="check-circle" /> Data loaded for period: {analysisRange[0].format('YYYY-MM-DD HH:mm:ss')} to {analysisRange[1].format('YYYY-MM-DD HH:mm:ss')}
                          </div>
                        )}
                      </Col>
                    </Row>
                    
                    {/* Refuel Events Modals */}
                    {renderRefuelEventsModal(
                      true,
                      startEventsModalVisible,
                      () => setStartEventsModalVisible(false),
                      handleSelectStartEvent
                    )}
                    {renderRefuelEventsModal(
                      false,
                      endEventsModalVisible,
                      () => setEndEventsModalVisible(false),
                      handleSelectEndEvent
                    )}

                    <Alert
                      type="info"
                      showIcon
                      style={{ marginTop: 8 }}
                      message="Select the analysis time range above, then click 'Fetch IoT Data' to load data for that period. You can adjust refueling times to match sensor-detected refuel events."
                    />
                  </Card>

                  {/* IoT Period Statistics */}
                  <Card
                    type="inner"
                    title={
                      <span>
                        <Icon type="dashboard" style={{ marginRight: 8 }} />
                        IoT Period Statistics
                      </span>
                    }
                    style={{ marginBottom: 12 }}
                  >
                    <Row gutter={16}>
                      {/* Left Column - IoT Statistics */}
                      <Col span={12}>
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              marginBottom: 12,
                              color: '#1890ff',
                            }}
                          >
                            IoT Sensor Data
                          </div>
                          <Table
                            dataSource={[
                              {
                                key: 'moving',
                                label: 'Total Moving Time',
                                value: mockIotData.iotPeriodStats.totalMovingTime,
                                valueDIN: mockIotData.iotPeriodStats.totalMovingTimeDIN,
                                unit: '',
                              },
                              {
                                key: 'pause',
                                label: 'Total Pause Time',
                                value: mockIotData.iotPeriodStats.totalPauseTime,
                                valueDIN: mockIotData.iotPeriodStats.totalPauseTimeDIN,
                                unit: '',
                              },
                              {
                                key: 'ignition',
                                label: 'Total Ignition Time',
                                value: mockIotData.iotPeriodStats.totalIgnitionTime,
                                valueDIN: mockIotData.iotPeriodStats.totalIgnitionTimeDIN,
                                unit: '',
                                compareWith: 'hoursRun',
                                compareLabel: 'Hours Run (Manual)',
                              },
                              {
                                key: 'stop',
                                label: 'Total Stop Time',
                                value: mockIotData.iotPeriodStats.totalStopTime,
                                valueDIN: mockIotData.iotPeriodStats.totalStopTimeDIN,
                                unit: '',
                              },
                              {
                                key: 'distance',
                                label: 'Distance Travelled',
                                value: mockIotData.iotPeriodStats.distanceTravelled,
                                unit: 'Kms',
                                compareWith: 'kmsRun',
                                compareLabel: 'KMs Run (Manual)',
                              },
                              {
                                key: 'averageSpeed',
                                label: 'Average Speed',
                                value: mockIotData.iotPeriodStats.averageSpeed,
                                unit: 'km/h',
                              },
                              {
                                key: 'fuelConsumed',
                                label: 'Fuel Consumed',
                                value: mockIotData.iotPeriodStats.fuelConsumed,
                                unit: 'ltr(s)',
                                compareWith: 'fuelAdded',
                                compareLabel: 'Fuel Added (Manual)',
                                highlight: true,
                              },
                              {
                                key: 'possibleRefuel',
                                label: 'Possible Refuel',
                                value: mockIotData.iotPeriodStats.possibleRefuel,
                                unit: 'ltr(s)',
                                compareWith: 'fuelAdded',
                                compareLabel: 'Fuel Added (Manual)',
                                highlight: true,
                              },
                              {
                                key: 'possibleDrain',
                                label: 'Possible Drain',
                                value: mockIotData.iotPeriodStats.possibleDrain,
                                unit: 'ltr(s)',
                                highlight: true,
                                warning: mockIotData.iotPeriodStats.possibleDrain > 0,
                              },
                              {
                                key: 'avgKmPerLtr',
                                label: 'Avg. Fuel Consumed',
                                value: mockIotData.iotPeriodStats.avgFuelConsumedKmPerLtr,
                                unit: 'Km/ltr',
                              },
                              {
                                key: 'avgLtrPerHr',
                                label: 'Avg. Fuel Consumed',
                                value: mockIotData.iotPeriodStats.avgFuelConsumedLtrPerHr,
                                valueDIN: mockIotData.iotPeriodStats.avgFuelConsumedLtrPerHrDIN,
                                unit: 'ltr./hr.',
                                compareWith: 'actualRate',
                                compareLabel: 'Actual Rate (Manual)',
                                highlight: true,
                              },
                            ]}
                            columns={[
                              {
                                title: 'Metric',
                                dataIndex: 'label',
                                key: 'label',
                                width: 180,
                                render: (text, record) => (
                                  <span
                                    style={{
                                      fontWeight: record.highlight ? 600 : 'normal',
                                    }}
                                  >
                                    {text}
                                  </span>
                                ),
                              },
                              {
                                title: 'Value',
                                key: 'value',
                                align: 'right',
                                render: (_, record) => {
                                  if (record.value === null || record.value === undefined || record.value === '') {
                                    return '-';
                                  }
                                  return (
                                  <div>
                                    <div>
                                      {typeof record.value === 'number'
                                        ? record.value.toFixed(2)
                                        : record.value}{' '}
                                      {record.unit}
                                    </div>
                                    {record.valueDIN && (
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: '#8c8c8c',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        ({record.valueDIN} DIN)
                                      </div>
                                    )}
                                  </div>
                                  );
                                },
                              },
                            ]}
                            pagination={false}
                            size="small"
                            rowClassName={record => {
                              if (record.warning) return 'warning-row';
                              if (record.highlight) return 'highlight-row';
                              return '';
                            }}
                            onRow={record => ({
                              style: {
                                backgroundColor: record.warning
                                  ? '#fff7e6'
                                  : record.highlight
                                  ? '#f0f9ff'
                                  : 'transparent',
                              },
                            })}
                          />
                        </div>
                      </Col>

                      {/* Right Column - Comparison with Manual Entry */}
                      <Col span={12}>
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              marginBottom: 12,
                              color: '#52c41a',
                            }}
                          >
                            Comparison with Full Tank Entry
                          </div>
                          <Table
                            dataSource={[
                              {
                                key: 'ignition',
                                iotLabel: 'Total Ignition Time',
                                iotValue: timeStringToHours(
                                  fetchedIotData?.iotPeriodStats?.totalIgnitionTime || mockIotData.iotPeriodStats.totalIgnitionTime
                                ),
                                iotValueDisplay: null, // Don't show HH:MM:SS format, show decimal hours instead (to match manual meter reading format)
                                manualLabel: 'Hours Run',
                                manualValue: (() => {
                                  // Calculate hoursRun for comparison - available for KWH, KM, and Hours assets
                                  // Get hoursRun from the current refuel event in consumption trend data
                                  if (trendData.length > 0) {
                                    // Find the current refuel event in trendData by matching refuelTime
                                    const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                    
                                    // Try exact time match first
                                    let currentEvent = trendData.find(e => {
                                      if (!e.refuelTime) return false;
                                      const eventTime = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                      return eventTime.isSame(currentRefuelMoment, 'minute');
                                    });
                                    
                                    // If not found by exact time, try matching by date (same day) - take the closest one
                                    if (!currentEvent) {
                                      const currentRefuelDate = currentRefuelMoment.format('YYYY-MM-DD');
                                      
                                      const sameDayEvents = trendData.filter(e => {
                                        if (!e.refuelTime) return false;
                                        const eventDate = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD');
                                        return eventDate === currentRefuelDate;
                                      });
                                      
                                      if (sameDayEvents.length > 0) {
                                        currentEvent = sameDayEvents.reduce((closest, current) => {
                                          const currentTime = moment(current.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const closestTime = moment(closest.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const currentDiff = Math.abs(currentTime.diff(currentRefuelMoment, 'minutes'));
                                          const closestDiff = Math.abs(closestTime.diff(currentRefuelMoment, 'minutes'));
                                          return currentDiff < closestDiff ? current : closest;
                                        });
                                      }
                                    }
                                    
                                    // Get hoursRun from the current event in trendData
                                    if (currentEvent && currentEvent.hoursRun !== null && currentEvent.hoursRun !== undefined && currentEvent.hoursRun > 0) {
                                      return parseFloat(currentEvent.hoursRun.toFixed(2));
                                    }
                                  }
                                  
                                  // Fallback: use hoursRun from mockAlert if available
                                  if (mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
                                    return parseFloat(mockAlert.hoursRun.toFixed(2));
                                  }
                                  
                                  // No data available - return null to show "N/A" in the table
                                  return null;
                                }
                                )(),
                                unit: 'HOURS',
                                available: mockAlert.hasHours, // Show for hours-based assets
                              },
                              {
                                key: 'distance',
                                iotLabel: 'Distance Travelled',
                                iotValue: fetchedIotData?.iotPeriodStats?.distanceTravelled || mockIotData.iotPeriodStats.distanceTravelled,
                                manualLabel: 'KMs Run',
                                manualValue: mockAlert.kmsRun,
                                unit: 'KM',
                                available: mockAlert.hasKms, // Only show if asset tracks KMs
                                note: null, // Remove "IoT sensor data only" note
                              },
                              // Hours comparison for KM assets (in addition to KM comparison)
                              {
                                key: 'ignitionKm',
                                iotLabel: 'Total Ignition Time',
                                iotValue: timeStringToHours(
                                  fetchedIotData?.iotPeriodStats?.totalIgnitionTime || mockIotData.iotPeriodStats.totalIgnitionTime
                                ),
                                iotValueDisplay: null,
                                manualLabel: 'Hours Run',
                                manualValue: (() => {
                                  // Get hoursRun from trendData for KM assets
                                  if (trendData.length > 0) {
                                    const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                    let currentEvent = trendData.find(e => {
                                      if (!e.refuelTime) return false;
                                      const eventTime = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                      return eventTime.isSame(currentRefuelMoment, 'minute');
                                    });
                                    
                                    if (!currentEvent) {
                                      const currentRefuelDate = currentRefuelMoment.format('YYYY-MM-DD');
                                      const sameDayEvents = trendData.filter(e => {
                                        if (!e.refuelTime) return false;
                                        const eventDate = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD');
                                        return eventDate === currentRefuelDate;
                                      });
                                      
                                      if (sameDayEvents.length > 0) {
                                        currentEvent = sameDayEvents.reduce((closest, current) => {
                                          const currentTime = moment(current.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const closestTime = moment(closest.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const currentDiff = Math.abs(currentTime.diff(currentRefuelMoment, 'minutes'));
                                          const closestDiff = Math.abs(closestTime.diff(currentRefuelMoment, 'minutes'));
                                          return currentDiff < closestDiff ? current : closest;
                                        });
                                      }
                                    }
                                    
                                    if (currentEvent && currentEvent.hoursRun !== null && currentEvent.hoursRun !== undefined && currentEvent.hoursRun > 0) {
                                      return parseFloat(currentEvent.hoursRun.toFixed(2));
                                    }
                                  }
                                  
                                  if (mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
                                    return parseFloat(mockAlert.hoursRun.toFixed(2));
                                  }
                                  
                                  return null;
                                })(),
                                unit: 'HOURS',
                                available: mockAlert.hasKms, // Show for KM assets (additional to KM comparison)
                                note: 'Hours comparison for KM-based assets',
                              },
                              {
                                key: 'kwhRun',
                                iotLabel: 'KWH Consumed (IoT)',
                                iotValue: mockIotData.iotMetrics.kwhRun,
                                manualLabel: 'KWH Consumed',
                                manualValue: mockAlert.kwhRun,
                                unit: 'KWH',
                                available: mockAlert.hasKwh,
                                note: 'KWH consumed from IoT vs Manual entry',
                              },
                              // Hours comparison for KWH assets (in addition to KWH comparison)
                              {
                                key: 'ignitionKwh',
                                iotLabel: 'Total Ignition Time',
                                iotValue: timeStringToHours(
                                  fetchedIotData?.iotPeriodStats?.totalIgnitionTime || mockIotData.iotPeriodStats.totalIgnitionTime
                                ),
                                iotValueDisplay: null,
                                manualLabel: 'Hours Run',
                                manualValue: (() => {
                                  // Get hoursRun from trendData for KWH assets
                                  if (trendData.length > 0) {
                                    const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                    let currentEvent = trendData.find(e => {
                                      if (!e.refuelTime) return false;
                                      const eventTime = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                      return eventTime.isSame(currentRefuelMoment, 'minute');
                                    });
                                    
                                    if (!currentEvent) {
                                      const currentRefuelDate = currentRefuelMoment.format('YYYY-MM-DD');
                                      const sameDayEvents = trendData.filter(e => {
                                        if (!e.refuelTime) return false;
                                        const eventDate = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD');
                                        return eventDate === currentRefuelDate;
                                      });
                                      
                                      if (sameDayEvents.length > 0) {
                                        currentEvent = sameDayEvents.reduce((closest, current) => {
                                          const currentTime = moment(current.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const closestTime = moment(closest.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const currentDiff = Math.abs(currentTime.diff(currentRefuelMoment, 'minutes'));
                                          const closestDiff = Math.abs(closestTime.diff(currentRefuelMoment, 'minutes'));
                                          return currentDiff < closestDiff ? current : closest;
                                        });
                                      }
                                    }
                                    
                                    if (currentEvent && currentEvent.hoursRun !== null && currentEvent.hoursRun !== undefined && currentEvent.hoursRun > 0) {
                                      return parseFloat(currentEvent.hoursRun.toFixed(2));
                                    }
                                  }
                                  
                                  if (mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
                                    return parseFloat(mockAlert.hoursRun.toFixed(2));
                                  }
                                  
                                  return null;
                                })(),
                                unit: 'HOURS',
                                available: mockAlert.hasKwh, // Show for KWH assets (additional to KWH comparison)
                                note: 'Hours comparison for KWH-based assets',
                              },
                              {
                                key: 'fuelConsumed',
                                iotLabel: 'Fuel Consumed',
                                iotValue: fetchedIotData?.iotPeriodStats?.fuelConsumed || mockIotData.iotPeriodStats.fuelConsumed,
                                manualLabel: 'Fuel Added',
                                manualValue: mockAlert.fuelAdded,
                                unit: 'L',
                                available: true,
                                note: 'Note: Fuel Consumed (IoT) vs Fuel Added (Manual)',
                              },
                              {
                                key: 'possibleRefuel',
                                iotLabel: 'Possible Refuel',
                                iotValue: fetchedIotData?.iotPeriodStats?.possibleRefuel || mockIotData.iotPeriodStats.possibleRefuel,
                                manualLabel: 'Fuel Added',
                                manualValue: mockAlert.fuelAdded,
                                unit: 'L',
                                available: true,
                                note: 'IoT detected refuel vs Manual entry',
                              },
                              // Consumption Rate - KM/L and L/KM (for vehicles with KMs)
                              // Show both formats when asset tracks KMs (hasKms = true)
                              {
                                key: 'avgKmPerLtr',
                                iotLabel: 'Avg. Fuel Consumed (KM/L | L/KM)',
                                iotValue: fetchedIotData?.iotPeriodStats?.avgFuelConsumedKmPerLtr || mockIotData.iotPeriodStats.avgFuelConsumedKmPerLtr,
                                iotValueSecondary: (() => {
                                  const kmPerL = fetchedIotData?.iotPeriodStats?.avgFuelConsumedKmPerLtr || mockIotData.iotPeriodStats.avgFuelConsumedKmPerLtr;
                                  if (kmPerL && kmPerL > 0) {
                                    return parseFloat((1 / kmPerL).toFixed(2)); // L/KM
                                  }
                                  return null;
                                })(),
                                manualLabel: 'Consumption Rate (KM/L | L/KM)',
                                manualValue: (() => {
                                  if (!mockAlert.hasKms || !mockAlert.fuelAdded || mockAlert.fuelAdded <= 0) return null;
                                  
                                  // If we have kmsRun, calculate both formats
                                  if (mockAlert.kmsRun && mockAlert.kmsRun > 0) {
                                    return {
                                      kmPerL: parseFloat((mockAlert.kmsRun / mockAlert.fuelAdded).toFixed(2)),
                                      lPerKm: parseFloat((mockAlert.fuelAdded / mockAlert.kmsRun).toFixed(2))
                                    };
                                  }
                                  
                                  // If actualRate is in L/KM format, convert to both
                                  const isLPerKm = mockAlert.standardUnit && (
                                    mockAlert.standardUnit === 'L_PER_KM' ||
                                    (mockAlert.standardUnit.includes('KM') && mockAlert.standardUnit.includes('L'))
                                  );
                                  if (isLPerKm && mockAlert.actualRate && mockAlert.actualRate > 0) {
                                    return {
                                      lPerKm: mockAlert.actualRate,
                                      kmPerL: parseFloat((1 / mockAlert.actualRate).toFixed(2))
                                    };
                                  }
                                  
                                  return null;
                                })(),
                                unit: 'KM/L',
                                unitSecondary: 'L/KM',
                                available: mockAlert.hasKms, // Only show for vehicles that track KMs
                                note: 'Average fuel consumption: KM/L and L/KM (IoT vs Manual)',
                              },
                              // Consumption Rate - L/Hours and Hours/L (for equipment with hours)
                              // Show both formats when asset tracks hours (hasHours = true)
                              // Also show for KWH and KM assets as additional comparison
                              {
                                key: 'avgLtrPerHr',
                                iotLabel: 'Avg. Fuel Consumed (L/Hours | Hours/L)',
                                iotValue: fetchedIotData?.iotPeriodStats?.avgFuelConsumedLtrPerHr || mockIotData.iotPeriodStats.avgFuelConsumedLtrPerHr,
                                iotValueSecondary: (() => {
                                  const lPerHour = fetchedIotData?.iotPeriodStats?.avgFuelConsumedLtrPerHr || mockIotData.iotPeriodStats.avgFuelConsumedLtrPerHr;
                                  if (lPerHour && lPerHour > 0) {
                                    return parseFloat((1 / lPerHour).toFixed(2)); // Hours/L
                                  }
                                  return null;
                                })(),
                                manualLabel: 'Actual Rate (L/Hours | Hours/L)',
                                manualValue: (() => {
                                  // Calculate for Hours, KWH, or KM assets
                                  if (!mockAlert.fuelAdded || mockAlert.fuelAdded <= 0) return null;
                                  
                                  // First, try to get hoursRun from trendData (for KWH/KM assets)
                                  let hoursRunForCalc = null;
                                  if (trendData.length > 0) {
                                    const currentRefuelMoment = moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                    
                                    // Try exact time match first
                                    let currentEvent = trendData.find(e => {
                                      if (!e.refuelTime) return false;
                                      const eventTime = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                      return eventTime.isSame(currentRefuelMoment, 'minute');
                                    });
                                    
                                    // If not found by exact time, try matching by date (same day)
                                    if (!currentEvent) {
                                      const currentRefuelDate = currentRefuelMoment.format('YYYY-MM-DD');
                                      const sameDayEvents = trendData.filter(e => {
                                        if (!e.refuelTime) return false;
                                        const eventDate = moment(e.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30').format('YYYY-MM-DD');
                                        return eventDate === currentRefuelDate;
                                      });
                                      
                                      if (sameDayEvents.length > 0) {
                                        currentEvent = sameDayEvents.reduce((closest, current) => {
                                          const currentTime = moment(current.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const closestTime = moment(closest.refuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
                                          const currentDiff = Math.abs(currentTime.diff(currentRefuelMoment, 'minutes'));
                                          const closestDiff = Math.abs(closestTime.diff(currentRefuelMoment, 'minutes'));
                                          return currentDiff < closestDiff ? current : closest;
                                        });
                                      }
                                    }
                                    
                                    if (currentEvent && currentEvent.hoursRun !== null && currentEvent.hoursRun !== undefined && currentEvent.hoursRun > 0) {
                                      hoursRunForCalc = currentEvent.hoursRun;
                                    }
                                  }
                                  
                                  // Fallback to mockAlert.hoursRun if not found in trendData
                                  if (hoursRunForCalc === null && mockAlert.hoursRun !== null && mockAlert.hoursRun !== undefined && mockAlert.hoursRun > 0) {
                                    hoursRunForCalc = mockAlert.hoursRun;
                                  }
                                  
                                  // For hours-based assets
                                  if (mockAlert.hasHours) {
                                  // Check if standardUnit indicates L/Hours format
                                  const isLPerHour = mockAlert.standardUnit && (
                                    mockAlert.standardUnit.includes('HOUR') || 
                                    mockAlert.standardUnit === 'L_PER_HOUR' ||
                                    mockAlert.standardUnit === 'LTR_PER_HOUR'
                                  );
                                  
                                  // If actualRate is already in L/Hours format, calculate both
                                  if (isLPerHour && mockAlert.actualRate) {
                                    return {
                                      lPerHour: mockAlert.actualRate,
                                      hourPerL: parseFloat((1 / mockAlert.actualRate).toFixed(2))
                                    };
                                  }
                                  
                                  // If actualRate is in HOURS/L format, convert to both
                                  const isHourPerL = mockAlert.standardUnit && (
                                    mockAlert.standardUnit === 'HOUR_PER_L' ||
                                    mockAlert.standardUnit === 'HOURS_PER_L'
                                  );
                                  if (isHourPerL && mockAlert.actualRate && mockAlert.actualRate > 0) {
                                    return {
                                      hourPerL: mockAlert.actualRate,
                                      lPerHour: parseFloat((1 / mockAlert.actualRate).toFixed(2))
                                    };
                                  }
                                  
                                  // Calculate from hoursRun if available
                                    if (hoursRunForCalc && hoursRunForCalc > 0) {
                                      const lPerHour = parseFloat((mockAlert.fuelAdded / hoursRunForCalc).toFixed(2));
                                    return {
                                      lPerHour: lPerHour,
                                        hourPerL: parseFloat((hoursRunForCalc / mockAlert.fuelAdded).toFixed(2))
                                      };
                                    }
                                  }
                                  
                                  // For KWH or KM assets, calculate from hoursRun if available
                                  if ((mockAlert.hasKwh || mockAlert.hasKms) && hoursRunForCalc && hoursRunForCalc > 0) {
                                    const lPerHour = parseFloat((mockAlert.fuelAdded / hoursRunForCalc).toFixed(2));
                                    return {
                                      lPerHour: lPerHour,
                                      hourPerL: parseFloat((hoursRunForCalc / mockAlert.fuelAdded).toFixed(2))
                                    };
                                  }
                                  
                                  return null;
                                })(),
                                unit: 'L/Hours',
                                unitSecondary: 'Hours/L',
                                available: mockAlert.hasHours || mockAlert.hasKwh || mockAlert.hasKms, // Show for Hours, KWH, and KM assets
                                note: mockAlert.hasKwh || mockAlert.hasKms 
                                  ? 'Hours-based consumption rate comparison (additional to primary metric)' 
                                  : 'Average fuel consumption: L/Hours and Hours/L (IoT vs Manual)',
                              },
                              // Consumption Rate - KWH/L (for DG sets)
                              // Show when asset tracks KWH (hasKwh = true) - for future use
                              {
                                key: 'avgKwhPerLtr',
                                iotLabel: 'Avg. Fuel Consumed',
                                iotValue: fetchedIotData?.iotPeriodStats?.avgFuelConsumedKwhPerLtr || mockIotData.iotPeriodStats.avgFuelConsumedKwhPerLtr || null,
                                manualLabel: 'Consumption Rate',
                                manualValue: mockAlert.kwhRun && mockAlert.fuelAdded && mockAlert.fuelAdded > 0
                                  ? parseFloat((mockAlert.kwhRun / mockAlert.fuelAdded).toFixed(2))
                                  : null,
                                unit: 'KWH/L',
                                available: mockAlert.hasKwh, // Only show for DG sets that track KWH
                                note: 'Average fuel consumption in KWH/L (IoT vs Manual)',
                              },
                            ].filter(item => item.available)}
                            columns={[
                              {
                                title: 'Metric',
                                key: 'metric',
                                width: 150,
                                render: (_, record) => (
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                                      {record.iotLabel}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                      vs {record.manualLabel}
                                    </div>
                                    {record.note && (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: '#1890ff',
                                          marginTop: 4,
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        {record.note}
                                      </div>
                                    )}
                                  </div>
                                ),
                              },
                              {
                                title: 'IoT',
                                key: 'iot',
                                align: 'right',
                                width: 120,
                                render: (_, record) => {
                                  if (record.iotValue === null || record.iotValue === undefined || record.iotValue === '') {
                                    return '-';
                                  }
                                  return (
                                  <div>
                                    <span style={{ color: '#1890ff', fontWeight: 600 }}>
                                      {record.iotValueDisplay
                                        ? record.iotValueDisplay
                                        : typeof record.iotValue === 'number'
                                        ? record.iotValue.toFixed(2)
                                        : record.iotValue}{' '}
                                      {record.unit}
                                    </span>
                                    {record.iotValueSecondary && (
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: '#8c8c8c',
                                          marginTop: 2,
                                        }}
                                      >
                                        {record.iotValueSecondary.toFixed(2)} {record.unitSecondary}
                                      </div>
                                    )}
                                    {record.iotValueDisplay &&
                                      typeof record.iotValue === 'number' && !record.iotValueSecondary && (
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: '#8c8c8c',
                                            marginTop: 2,
                                          }}
                                        >
                                          ({record.iotValue.toFixed(2)} HOURS)
                                        </div>
                                      )}
                                  </div>
                                  );
                                },
                              },
                              {
                                title: 'Manual',
                                key: 'manual',
                                align: 'right',
                                width: 120,
                                render: (_, record) => {
                                  if (
                                    record.manualValue === null ||
                                    record.manualValue === undefined ||
                                    record.manualValue === ''
                                  ) {
                                    return '-';
                                  }
                                  
                                  // Handle object format for consumption rates (with both formats)
                                  if (typeof record.manualValue === 'object' && record.manualValue !== null) {
                                    const hasKmPerL = 'kmPerL' in record.manualValue;
                                    const hasLPerKm = 'lPerKm' in record.manualValue;
                                    const hasLPerHour = 'lPerHour' in record.manualValue;
                                    const hasHourPerL = 'hourPerL' in record.manualValue;
                                    
                                    return (
                                      <div>
                                        {hasKmPerL && (
                                          <span style={{ color: '#52c41a', fontWeight: 600 }}>
                                            {record.manualValue.kmPerL.toFixed(2)} {record.unit}
                                          </span>
                                        )}
                                        {hasLPerKm && (
                                          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: hasKmPerL ? 2 : 0 }}>
                                            {record.manualValue.lPerKm.toFixed(2)} {record.unitSecondary}
                                          </div>
                                        )}
                                        {hasLPerHour && (
                                          <span style={{ color: '#52c41a', fontWeight: 600 }}>
                                            {record.manualValue.lPerHour.toFixed(2)} {record.unit}
                                          </span>
                                        )}
                                        {hasHourPerL && (
                                          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: hasLPerHour ? 2 : 0 }}>
                                            {record.manualValue.hourPerL.toFixed(2)} {record.unitSecondary}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <span style={{ color: '#52c41a', fontWeight: 600 }}>
                                      {typeof record.manualValue === 'number'
                                        ? record.manualValue.toFixed(2)
                                        : record.manualValue}{' '}
                                      {record.unit}
                                    </span>
                                  );
                                },
                              },
                              {
                                title: 'Difference',
                                key: 'difference',
                                align: 'right',
                                width: 120,
                                render: (_, record) => {
                                  const iot = record.iotValue;
                                  const manual = record.manualValue;

                                  // Handle object format for consumption rates (extract primary value)
                                  let iotValue = iot;
                                  let manualValue = manual;
                                  
                                  if (typeof manual === 'object' && manual !== null) {
                                    // Extract the primary value based on unit
                                    // For KM/L format, use kmPerL from manual
                                    if (record.unit === 'KM/L' && 'kmPerL' in manual) {
                                      manualValue = manual.kmPerL;
                                    } 
                                    // For L/KM format, use lPerKm from manual
                                    else if (record.unit === 'L/KM' && 'lPerKm' in manual) {
                                      manualValue = manual.lPerKm;
                                    } 
                                    // For L/Hours format, use lPerHour from manual
                                    else if (record.unit === 'L/Hours' && 'lPerHour' in manual) {
                                      manualValue = manual.lPerHour;
                                    } 
                                    // For Hours/L format, use hourPerL from manual
                                    else if (record.unit === 'Hours/L' && 'hourPerL' in manual) {
                                      manualValue = manual.hourPerL;
                                    } 
                                    // Fallback: try to get first numeric value
                                    else {
                                      const values = Object.values(manual).filter(v => typeof v === 'number' && !isNaN(v));
                                      manualValue = values.length > 0 ? values[0] : null;
                                    }
                                  }

                                  // Ensure both values are valid numbers
                                  if (
                                    manualValue === null ||
                                    manualValue === undefined ||
                                    isNaN(manualValue) ||
                                    iotValue === null ||
                                    iotValue === undefined ||
                                    isNaN(iotValue)
                                  ) {
                                    return <span style={{ color: '#bfbfbf' }}>-</span>;
                                  }

                                  // Convert to numbers to ensure proper calculation
                                  iotValue = typeof iotValue === 'number' ? iotValue : parseFloat(iotValue);
                                  manualValue = typeof manualValue === 'number' ? manualValue : parseFloat(manualValue);

                                  // Check again after conversion
                                  if (isNaN(iotValue) || isNaN(manualValue)) {
                                    return <span style={{ color: '#bfbfbf' }}>-</span>;
                                  }

                                  const diff = iotValue - manualValue;
                                  const absDiff = Math.abs(diff);
                                  const percentDiff =
                                    manualValue !== 0 && !isNaN(manualValue)
                                      ? ((diff / manualValue) * 100).toFixed(2)
                                      : '0.00';

                                  let color = '#52c41a';
                                  if (record.key === 'fuelConsumed' || record.key === 'possibleRefuel') {
                                    if (absDiff > 0.5 || Math.abs(percentDiff) > 2) {
                                      color = '#faad14';
                                    }
                                    if (absDiff > 2 || Math.abs(percentDiff) > 5) {
                                      color = '#f5222d';
                                    }
                                  } else {
                                    if (absDiff > 0.5 || Math.abs(percentDiff) > 5) {
                                      color = '#faad14';
                                    }
                                    if (absDiff > 1 || Math.abs(percentDiff) > 10) {
                                      color = '#f5222d';
                                    }
                                  }

                                  return (
                                    <div>
                                      <div style={{ color, fontWeight: 600 }}>
                                        {diff > 0 ? '+' : ''}
                                        {diff.toFixed(2)} {record.unit}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: '#8c8c8c',
                                          marginTop: 2,
                                        }}
                                      >
                                        ({percentDiff > 0 ? '+' : ''}
                                        {percentDiff}%)
                                      </div>
                                    </div>
                                  );
                                },
                              },
                            ]}
                            pagination={false}
                            size="small"
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Warning for Possible Drain */}
                    {mockIotData.iotPeriodStats.possibleDrain > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                        message={
                          <div>
                            <strong>Possible Drain Detected:</strong>{' '}
                            {mockIotData.iotPeriodStats.possibleDrain.toFixed(2)} liters
                            detected during this period. This may indicate fuel theft or
                            unauthorized fuel removal.
                          </div>
                        }
                      />
                    )}
                  </Card>

                  {/* Day-wise Breakdown */}
                  <Card
                    type="inner"
                    title={
                      <span>
                        <Icon type="calendar" style={{ marginRight: 8 }} />
                        Day-wise Breakdown
                      </span>
                    }
                    style={{ marginBottom: 12 }}
                  >
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        <div>
                          <div style={{ marginBottom: 4 }}>
                            Data is pulled from <strong>day_summary</strong> for full 24-hour
                            days and from <strong>IoT table</strong> for fractional days (first
                            and last day of the period).
                          </div>
                          <div style={{ fontSize: 12 }}>
                            Click "View Charts" for any day to load detailed charts for that
                            specific day (useful for large periods).
                          </div>
                        </div>
                      }
                    />
                    <div style={{ marginBottom: 16, textAlign: 'right' }}>
                      <Button
                        type="default"
                        icon="download"
                        onClick={() => {
                          const columns = [
                            { title: 'Date', dataIndex: 'date', key: 'date' },
                            { title: 'Data Source', dataIndex: 'dataSource', key: 'dataSource' },
                            { title: 'Time Period Start', key: 'dayStart', render: (_, record) => record.dayStart },
                            { title: 'Time Period End', key: 'dayEnd', render: (_, record) => record.dayEnd },
                            { title: 'Avg Rate (KM/L)', key: 'avgFuelKmPerL', render: (_, record) => record.avgFuelKmPerL ? record.avgFuelKmPerL.toFixed(2) : '-' },
                            { title: 'Avg Rate (L/KM)', key: 'avgFuelLPerKm', render: (_, record) => record.avgFuelLPerKm ? record.avgFuelLPerKm.toFixed(2) : '-' },
                            { title: 'Avg Rate (L/Hours)', key: 'avgFuelLPerHour', render: (_, record) => record.avgFuelLPerHour ? record.avgFuelLPerHour.toFixed(2) : '-' },
                            { title: 'Avg Rate (Hours/L)', key: 'avgFuelHourPerL', render: (_, record) => record.avgFuelHourPerL ? record.avgFuelHourPerL.toFixed(2) : '-' },
                            { title: 'Average Speed (km/h)', dataIndex: 'averageSpeed', key: 'averageSpeed', render: (val) => val != null ? Number(val).toFixed(2) : '-' },
                            { title: 'Distance Travelled (km)', dataIndex: 'distanceTravelled', key: 'distanceTravelled' },
                            { title: 'Fuel Consumed (L)', dataIndex: 'fuelConsumed', key: 'fuelConsumed' },
                            { title: 'Possible Refuel (L)', dataIndex: 'possibleRefuel', key: 'possibleRefuel' },
                            { title: 'Possible Drain (L)', dataIndex: 'possibleDrain', key: 'possibleDrain' },
                            { title: 'Total Moving Time', dataIndex: 'movingTime', key: 'movingTime' },
                            { title: 'Total Pause Time', dataIndex: 'pauseTime', key: 'pauseTime' },
                            { title: 'Total Ignition Time', dataIndex: 'ignitionTime', key: 'ignitionTime' },
                            { title: 'Total Stop Time', dataIndex: 'stopTime', key: 'stopTime' },
                            { title: 'Avg Fuel Ltr/Hr', dataIndex: 'avgFuelLtrPerHr', key: 'avgFuelLtrPerHr' },
                          ];
                          convertTableToCSV(columns, dayWiseBreakdown, 'day_wise_breakdown');
                        }}
                      >
                        Download CSV
                      </Button>
                    </div>
                    <Table
                      dataSource={dayWiseBreakdown}
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'date',
                          key: 'date',
                          width: 120,
                          render: (date, record) => (
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {moment(date).format('YYYY-MM-DD')}
                              </div>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                {record.isFullDay ? (
                                  <Tag color="green" size="small">Full Day</Tag>
                                ) : (
                                  <Tag color="orange" size="small">Partial</Tag>
                                )}
                              </div>
                            </div>
                          ),
                        },
                        {
                          title: 'Data Source',
                          dataIndex: 'dataSource',
                          key: 'dataSource',
                          width: 120,
                          render: (source) => (
                            <Tag color={source === 'day_summary' ? 'blue' : 'purple'}>
                              {source === 'day_summary' ? 'Day Summary' : 'IoT Table'}
                            </Tag>
                          ),
                        },
                        {
                          title: 'Time Period',
                          key: 'timePeriod',
                          width: 200,
                          render: (_, record) => (
                            <div style={{ fontSize: 12 }}>
                              <div>
                                <strong>Start:</strong> {record.dayStart}
                              </div>
                              <div>
                                <strong>End:</strong> {record.dayEnd}
                              </div>
                            </div>
                          ),
                        },
                        {
                          title: 'Avg. Rate',
                          key: 'avgRate',
                          align: 'right',
                          width: 180,
                          render: (_, record) => {
                            const hasKmData = record.avgFuelKmPerL != null && !isNaN(record.avgFuelKmPerL) && 
                                            record.avgFuelLPerKm != null && !isNaN(record.avgFuelLPerKm);
                            const hasHourData = record.avgFuelLPerHour != null && !isNaN(record.avgFuelLPerHour) && 
                                              record.avgFuelHourPerL != null && !isNaN(record.avgFuelHourPerL);
                            
                            // Show all available formats
                            if (hasKmData && hasHourData) {
                              // Both KM and Hours data available - show all formats
                              return (
                                <div>
                                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                    <div>{record.avgFuelKmPerL.toFixed(2)} KM/L</div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                      {record.avgFuelLPerKm.toFixed(2)} L/KM
                                    </div>
                                  </div>
                                  <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e8e8e8' }}>
                                    <div style={{ fontWeight: 600 }}>
                                      {record.avgFuelLPerHour.toFixed(2)} L/Hours
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                      {record.avgFuelHourPerL.toFixed(2)} Hours/L
                                    </div>
                                  </div>
                                </div>
                              );
                            } else if (hasKmData) {
                              // Only KM data available
                              return (
                                <div>
                                  <div style={{ fontWeight: 600 }}>
                                    {record.avgFuelKmPerL.toFixed(2)} KM/L
                                  </div>
                                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                    {record.avgFuelLPerKm.toFixed(2)} L/KM
                                  </div>
                                </div>
                              );
                            } else if (hasHourData) {
                              // Only Hours data available
                              return (
                                <div>
                                  <div style={{ fontWeight: 600 }}>
                                    {record.avgFuelLPerHour.toFixed(2)} L/Hours
                                  </div>
                                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                    {record.avgFuelHourPerL.toFixed(2)} Hours/L
                                  </div>
                                </div>
                              );
                            }
                            
                            // Fallback: try to calculate from avgFuelLtrPerHr if available
                            const val = record.avgFuelLtrPerHr;
                            if (val != null && val !== '') {
                              if (typeof val === 'string' && val.includes(':')) {
                                // Time string format - can't calculate rates
                                return val;
                              }
                              const numVal = typeof val === 'number' ? val : parseFloat(val);
                              if (!isNaN(numVal) && numVal > 0) {
                                return (
                                  <div>
                                    <div style={{ fontWeight: 600 }}>
                                      {numVal.toFixed(2)} L/Hours
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                      {(1 / numVal).toFixed(2)} Hours/L
                                    </div>
                                  </div>
                                );
                              }
                            }
                            
                            return '-';
                          },
                        },
                        {
                          title: 'Avg Speed (Km/h)',
                          dataIndex: 'averageSpeed',
                          key: 'averageSpeed',
                          align: 'right',
                          width: 120,
                          render: (val) => {
                            if (val == null || val === '') return '-';
                            const numVal = typeof val === 'number' ? val : parseFloat(val);
                            return isNaN(numVal) ? '-' : numVal.toFixed(2);
                          },
                        },
                        {
                          title: 'Moving Time',
                          dataIndex: 'movingTime',
                          key: 'movingTime',
                          align: 'right',
                          width: 100,
                        },
                        {
                          title: 'Ignition Time',
                          dataIndex: 'ignitionTime',
                          key: 'ignitionTime',
                          align: 'right',
                          width: 100,
                        },
                        {
                          title: 'Distance (Km)',
                          dataIndex: 'distanceTravelled',
                          key: 'distanceTravelled',
                          align: 'right',
                          width: 110,
                          render: (val) => {
                            if (val == null || val === '') return '-';
                            const numVal = typeof val === 'number' ? val : parseFloat(val);
                            return isNaN(numVal) ? '-' : numVal.toFixed(2);
                          },
                        },
                        {
                          title: 'Fuel Consumed (L)',
                          dataIndex: 'fuelConsumed',
                          key: 'fuelConsumed',
                          align: 'right',
                          width: 130,
                          render: (val) => {
                            if (val == null || val === '') return '-';
                            const numVal = typeof val === 'number' ? val : parseFloat(val);
                            return isNaN(numVal) ? '-' : numVal.toFixed(2);
                          },
                        },
                        {
                          title: 'Possible Refuel (L)',
                          dataIndex: 'possibleRefuel',
                          key: 'possibleRefuel',
                          align: 'right',
                          width: 130,
                          render: (val) => {
                            if (val == null || val === '') return '-';
                            const numVal = typeof val === 'number' ? val : parseFloat(val);
                            return isNaN(numVal) ? '-' : numVal.toFixed(2);
                          },
                        },
                        {
                          title: 'Possible Drain (L)',
                          dataIndex: 'possibleDrain',
                          key: 'possibleDrain',
                          align: 'right',
                          width: 130,
                          render: (val, record) => {
                            if (val == null || val === '') return '-';
                            const numVal = typeof val === 'number' ? val : parseFloat(val);
                            if (isNaN(numVal)) return '-';
                            return (
                              <span style={{ color: numVal > 0 ? '#f5222d' : '#595959' }}>
                                {numVal.toFixed(2)}
                              </span>
                            );
                          },
                        },
                        {
                          title: 'Action',
                          key: 'action',
                          width: 140,
                          fixed: 'right',
                          render: (_, record) => (
                            <Button
                              type="default"
                              size="small"
                              icon="line-chart"
                              onClick={() => handleViewDayCharts(record.date, record.dayStart, record.dayEnd)}
                            >
                              View Charts
                            </Button>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                      scroll={{ x: 1200 }}
                      rowKey="date"
                      onRow={(record) => ({
                        style: {
                          backgroundColor:
                            selectedDayForCharts === record.date ? '#e6f7ff' : 'white',
                        },
                      })}
                    />
                  </Card>

                  
                  {/* IoT Charts Modal */}
                  <Modal
                    visible={iotChartsModalVisible}
                    title={
                      <span>
                        <Icon type="line-chart" style={{ marginRight: 8 }} />
                        IoT Charts
                        {selectedDayForCharts && (
                          <span style={{ fontSize: 14, fontWeight: 'normal', marginLeft: 8 }}>
                            - {moment(selectedDayForCharts).format('YYYY-MM-DD')}
                          </span>
                        )}
                      </span>
                    }
                    onCancel={() => {
                      setIotChartsModalVisible(false);
                      setSelectedDayForCharts(null);
                    }}
                    footer={[
                      <Button
                        key="close"
                        onClick={() => {
                          setIotChartsModalVisible(false);
                          setSelectedDayForCharts(null);
                        }}
                      >
                        Close
                      </Button>,
                    ]}
                    width={1200}
                    style={{ top: 20 }}
                  >
                    {/* Channel filters inside modal */}
                    <Row
                      type="flex"
                      justify="space-between"
                      style={{ marginBottom: 16 }}
                    >
                      <Col>
                        <div style={{ marginBottom: 4 }}>Channels:</div>
                        <Checkbox.Group
                          options={[
                            { label: 'Fuel Level', value: 'fuel' },
                            { label: 'Speed', value: 'speed' },
                            { label: 'Engine On/Off', value: 'engine' },
                            { label: 'Location Events', value: 'location' },
                          ]}
                          value={selectedChannels}
                          onChange={setSelectedChannels}
                        />
                      </Col>
                    </Row>

                    {/* IoT charts */}
                    <div
                      style={{
                        height: 300,
                        border: '1px dashed #d9d9d9',
                        borderRadius: 4,
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#bfbfbf',
                        background: '#fafafa',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <Icon type="line-chart" style={{ fontSize: 32, marginBottom: 8 }} />
                        <div>Fuel Level vs Time Chart</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Shows fuel level over time with markers for refuel events
                        </div>
                        {selectedDayForCharts && (
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                            Data for: {moment(selectedDayForCharts).format('YYYY-MM-DD')}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        height: 250,
                        border: '1px dashed #d9d9d9',
                        borderRadius: 4,
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#bfbfbf',
                        background: '#fafafa',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <Icon type="line-chart" style={{ fontSize: 32, marginBottom: 8 }} />
                        <div>Speed / Engine State Chart</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Operational context (speed, engine on/off)
                        </div>
                        {selectedDayForCharts && (
                          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                            Data for: {moment(selectedDayForCharts).format('YYYY-MM-DD')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Event list (suspicious drops etc.) */}
                    <Card
                      title={
                        <span>
                          <Icon
                            type="warning"
                            style={{ color: '#faad14', marginRight: 8 }}
                          />
                          Detected Fuel Events
                        </span>
                      }
                      size="small"
                      bodyStyle={{ padding: 8 }}
                    >
                      {mockIotData.events && mockIotData.events.length ? (
                        mockIotData.events.map(event => (
                          <div
                            key={event.id}
                            style={{
                              padding: '8px 0',
                              borderBottom: '1px solid #f0f0f0',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleChartEventClick(event)}
                          >
                            <div>
                              <strong>{event.type}</strong> at {event.time}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                              Drop: {event.dropLitres} L, Location: {event.locationText}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          No significant fuel events detected between these refuels.
                        </div>
                      )}
                    </Card>
                  </Modal>
                </TabPane>
                
                {/* TAB 4: ALERT HISTORY */}
                <TabPane
                  tab={
                    <span>
                      <Icon type="history" style={{ marginRight: 4 }} />
                      Alert History
                      {alertHistory.length > 0 && (
                        <Badge 
                          count={alertHistory.length} 
                          style={{ marginLeft: 8, backgroundColor: '#1890ff' }}
                          overflowCount={99}
                        />
                      )}
                    </span>
                  }
                  key="alertHistory"
                >
                  <div style={{ marginBottom: 16 }}>
                    <Row gutter={16} align="middle" type="flex" style={{ rowGap: 8 }}>
                      <Col>
                        <span style={{ marginRight: 8 }}>Look back:</span>
                        <Select
                          value={alertHistoryLookback}
                          onChange={setAlertHistoryLookback}
                          style={{ width: 160 }}
                        >
                          {ALERT_HISTORY_LOOKBACK_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>{option.label}</Option>
                          ))}
                          <Option value={ALERT_HISTORY_CUSTOM_LOOKBACK}>Custom date range</Option>
                        </Select>
                      </Col>
                      {alertHistoryLookback === ALERT_HISTORY_CUSTOM_LOOKBACK && (
                        <Col>
                          {/* Both bounds are the user's. Nothing is queried until
                              a complete range is picked - no default fill-in. */}
                          <RangePicker
                            value={alertHistoryCustomRange}
                            onChange={setAlertHistoryCustomRange}
                            format="DD-MMM-YYYY"
                            allowClear={false}
                            style={{ width: 260 }}
                            disabledDate={current =>
                              !!current && !!mockAlert.refuelTime &&
                              current.isAfter(moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss'), 'day')
                            }
                          />
                        </Col>
                      )}
                      <Col>
                        <span style={{ marginRight: 8 }}>Status:</span>
                        <Select
                          value={alertHistoryLifecycle}
                          onChange={setAlertHistoryLifecycle}
                          style={{ width: 170 }}
                        >
                          <Option value="ALL">Open &amp; Closed ({alertHistoryCounts.total})</Option>
                          <Option value={ALERT_LIFECYCLE.OPEN}>Open only ({alertHistoryCounts.open})</Option>
                          <Option value={ALERT_LIFECYCLE.CLOSED}>Closed only ({alertHistoryCounts.closed})</Option>
                        </Select>
                      </Col>
                      <Col>
                        <span style={{ marginRight: 8 }}>Show:</span>
                        <Select
                          value={alertHistoryLimit}
                          onChange={setAlertHistoryLimit}
                          style={{ width: 150 }}
                        >
                          <Option value="7">Last 7 Alerts</Option>
                          <Option value="14">Last 14 Alerts</Option>
                          <Option value="30">Last 30 Alerts</Option>
                          <Option value="999">All In Window</Option>
                        </Select>
                      </Col>
                      <Col>
                        <Button
                          icon="reload"
                          onClick={fetchAlertHistory}
                          loading={isLoadingAlertHistory}
                        >
                          Refresh
                        </Button>
                      </Col>
                      <Col>
                        <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                          Showing {filteredAlertHistory.length} of {alertHistory.length} fuel alerts
                          {' '}({alertHistoryCounts.open} open, {alertHistoryCounts.closed} closed) for {mockAlert.assetCode}
                          {alertHistoryWindow && (
                            <span>
                              {' '}created {moment(alertHistoryWindow.fromMs).format('DD-MMM-YYYY')}
                              {' '}to {moment(alertHistoryWindow.toMs - 1).format('DD-MMM-YYYY HH:mm')}
                            </span>
                          )}
                        </span>
                      </Col>
                    </Row>
                  </div>

                  {/* The query window is a scope, not a filter: alerts older than
                      it were never fetched and must not read as "none exist". */}
                  {alertHistoryWindow && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`Open and closed alerts created between ${moment(alertHistoryWindow.fromMs).format('DD-MMM-YYYY')} and ${moment(alertHistoryWindow.toMs - 1).format('DD-MMM-YYYY HH:mm')} were requested (${alertHistoryWindow.label}). Alerts outside this window are not loaded - change "Look back" to include them.`}
                    />
                  )}

                  {/* One endpoint down means the list is a partial history. Say
                      which half is missing - a short list must never pass for a
                      complete one. */}
                  {alertHistoryWindow && (alertHistoryWindow.failedSources || []).length > 0 && (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Alert history is incomplete"
                      description={
                        `${alertHistoryWindow.failedSources.map(f => `${f.label} alerts could not be loaded (${f.error})`).join('; ')}. ` +
                        `The list below covers ${(alertHistoryWindow.loadedSources || []).join(' and ')} alerts only - treat the counts as a lower bound and use Refresh to retry.`
                      }
                      action={
                        <Button size="small" onClick={fetchAlertHistory}>
                          Retry
                        </Button>
                      }
                    />
                  )}

                  {/* A custom range ending after the refuel event is clamped to it,
                      because this tab only lists the asset's PAST alerts. Say so
                      rather than showing a narrower window than the user picked. */}
                  {alertHistoryWindow && alertHistoryWindow.clampedToRefuel && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`The selected range ends after this refuel event (${moment(mockAlert.refuelTime, 'YYYY-MM-DD HH:mm:ss').format('DD-MMM-YYYY HH:mm')}). Alert history covers past alerts only, so the window was cut off at the refuel event - alerts created after it were not requested.`}
                    />
                  )}

                  {alertHistoryIssues.missingCreatedAt > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`${alertHistoryIssues.missingCreatedAt} fuel alert(s) for this asset have no creation date and could not be placed against the refuel event. They are excluded from the list below.`}
                    />
                  )}

                  {/* An alert that closed between the two requests comes back
                      from both endpoints. The closed copy is kept; say how many
                      collided rather than silently collapsing rows. */}
                  {alertHistoryIssues.duplicateAcrossSets > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`${alertHistoryIssues.duplicateAcrossSets} alert(s) were returned by both the open and closed queries - they most likely closed between the two requests. The closed record is shown for each.`}
                    />
                  )}

                  {alertHistoryIssues.outOfWindow > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={`${alertHistoryIssues.outOfWindow} returned alert(s) have a creation date outside the requested window. They are listed below - the window shown may understate the period covered.`}
                    />
                  )}

                  {isLoadingAlertHistory ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 8, color: '#666' }}>Loading alert history...</div>
                    </div>
                  ) : alertHistoryError ? (
                    <Alert
                      message="Error"
                      description={alertHistoryError}
                      type="error"
                      showIcon
                      action={
                        <Button size="small" onClick={fetchAlertHistory}>
                          Retry
                        </Button>
                      }
                    />
                  ) : filteredAlertHistory.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span>
                          No {alertHistoryLifecycle === 'ALL' ? '' : `${alertHistoryLifecycle.toLowerCase()} `}fuel alerts found for {mockAlert.assetCode}
                          {alertHistoryLifecycle !== 'ALL' && alertHistoryCounts.total > 0 && (
                            <span> in this status - the window holds {alertHistoryCounts.total} alert(s) overall.</span>
                          )}
                          {alertHistoryWindow ? (
                            <span>
                              {' '}created between {moment(alertHistoryWindow.fromMs).format('DD-MMM-YYYY')}
                              {' '}and {moment(alertHistoryWindow.toMs - 1).format('DD-MMM-YYYY HH:mm')}.
                              {' '}Alerts outside this window were not requested.
                            </span>
                          ) : (
                            <span> - no alert history window has been loaded</span>
                          )}
                        </span>
                      }
                    />
                  ) : (
                    <Table
                      dataSource={filteredAlertHistory}
                      rowKey={record => record._id || record.alertId}
                      size="small"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} alerts`
                      }}
                      scroll={{ x: 1350 }}
                      columns={[
                        {
                          title: 'Alert ID',
                          dataIndex: 'alertId',
                          key: 'alertId',
                          width: 100,
                          fixed: 'left',
                          render: (text, record) => (
                            <Button
                              type="link"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => {
                                setSelectedHistoryAlert(record);
                                setAlertHistoryDrawerVisible(true);
                              }}
                            >
                              {text}
                            </Button>
                          )
                        },
                        {
                          title: 'Type',
                          dataIndex: 'alertType',
                          key: 'alertType',
                          width: 180,
                          render: alertType => {
                            const typeColor = getAlertTypeColor(alertType);
                            return (
                              <span
                                style={{
                                  fontSize: 11,
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  background: typeColor.bg,
                                  color: typeColor.color,
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {getAlertTypeLabel(alertType)}
                              </span>
                            );
                          }
                        },
                        {
                          title: 'Status',
                          key: 'lifecycle',
                          width: 150,
                          align: 'center',
                          filters: [
                            { text: 'Open', value: ALERT_LIFECYCLE.OPEN },
                            { text: 'Closed', value: ALERT_LIFECYCLE.CLOSED },
                          ],
                          onFilter: (value, record) => record._lifecycle === value,
                          render: (_, record) => {
                            // _lifecycle comes from the endpoint that returned the
                            // row; _status is the workflow stage within it, with
                            // Ignore closures resolved (the API reports those as
                            // ACTION_TAKEN).
                            const isOpen = record._lifecycle === ALERT_LIFECYCLE.OPEN;
                            const stage = getStatusInfo(record._status);
                            const derived =
                              record._status === 'IGNORED' && record.currentStatus === 'ACTION_TAKEN';
                            return (
                              <div>
                                <Tag color={isOpen ? 'volcano' : 'green'} style={{ marginRight: 0 }}>
                                  {isOpen ? 'OPEN' : 'CLOSED'}
                                </Tag>
                                {record._status && (
                                  <Tooltip
                                    title={
                                      derived
                                        ? 'API status: ACTION_TAKEN, closed with the remark "Ignore"'
                                        : null
                                    }
                                  >
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                      {stage.label}
                                      {derived && '*'}
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            );
                          }
                        },
                        {
                          title: 'Severity',
                          dataIndex: 'alertSeverity',
                          key: 'alertSeverity',
                          width: 100,
                          align: 'center',
                          render: severity => {
                            const info = getSeverityInfo(severity);
                            return <Tag color={info.color}>{info.label.toUpperCase()}</Tag>;
                          }
                        },
                        {
                          title: 'Title',
                          dataIndex: 'alertTitle',
                          key: 'alertTitle',
                          width: 250,
                          ellipsis: true,
                          render: (text, record) => (
                            <Tooltip title={text}>
                              <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setSelectedHistoryAlert(record);
                                  setAlertHistoryDrawerVisible(true);
                                }}
                              >
                                {text}
                              </span>
                            </Tooltip>
                          )
                        },
                        {
                          title: 'Action Taken',
                          key: 'actionTaken',
                          width: 200,
                          ellipsis: true,
                          render: (_, record) => {
                            const workflowInfo = extractWorkflowInfo(record);
                            return (
                              <Tooltip title={workflowInfo.actionTaken || workflowInfo.actionSuggested || 'N/A'}>
                                <span style={{ color: workflowInfo.actionTaken ? '#389e0d' : '#8c8c8c' }}>
                                  {workflowInfo.actionTaken || workflowInfo.actionSuggested || 'N/A'}
                                </span>
                              </Tooltip>
                            );
                          }
                        },
                        {
                          // "Still open" and "N/A" mean different things: not
                          // applicable yet vs. closed but with no recorded closer.
                          title: 'Closed By',
                          key: 'closedBy',
                          width: 120,
                          render: (_, record) => {
                            if (record._lifecycle === ALERT_LIFECYCLE.OPEN) {
                              return <span style={{ color: '#8c8c8c' }}>Still open</span>;
                            }
                            const workflowInfo = extractWorkflowInfo(record);
                            return workflowInfo.closedBy?.name || 'N/A';
                          }
                        },
                        {
                          title: 'Closed At',
                          key: 'closedAt',
                          width: 140,
                          render: (_, record) => {
                            if (record._lifecycle === ALERT_LIFECYCLE.OPEN) {
                              return <span style={{ color: '#8c8c8c' }}>Still open</span>;
                            }
                            const workflowInfo = extractWorkflowInfo(record);
                            return workflowInfo.closedAt
                              ? moment(workflowInfo.closedAt).format('DD MMM YYYY, HH:mm')
                              : 'N/A';
                          },
                          sorter: (a, b) => {
                            const aInfo = extractWorkflowInfo(a);
                            const bInfo = extractWorkflowInfo(b);
                            return moment(aInfo.closedAt || 0).unix() - moment(bInfo.closedAt || 0).unix();
                          },
                          defaultSortOrder: 'descend'
                        },
                        {
                          title: 'Created At',
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          width: 140,
                          render: date => date ? moment(date).format('DD MMM YYYY, HH:mm') : 'N/A',
                          sorter: (a, b) => moment(a.createdAt || 0).unix() - moment(b.createdAt || 0).unix()
                        }
                      ]}
                    />
                  )}
                  
                  {/* Alert Detail Drawer - Matching ClosedAlertsReport style */}
                  <Drawer
                    title={
                      selectedHistoryAlert ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontWeight: 600, fontSize: 16 }}>
                            {selectedHistoryAlert.alertId}
                          </span>
                          <Tag color={getSeverityInfo(selectedHistoryAlert.alertSeverity).color}>
                            {getSeverityInfo(selectedHistoryAlert.alertSeverity).label.toUpperCase()}
                          </Tag>
                          <Tag color={selectedHistoryAlert._lifecycle === ALERT_LIFECYCLE.OPEN ? 'volcano' : 'green'}>
                            {selectedHistoryAlert._lifecycle === ALERT_LIFECYCLE.OPEN ? 'OPEN' : 'CLOSED'}
                          </Tag>
                        </div>
                      ) : 'Alert Details'
                    }
                    placement="right"
                    closable={true}
                    onClose={() => {
                      setAlertHistoryDrawerVisible(false);
                      setSelectedHistoryAlert(null);
                    }}
                    visible={alertHistoryDrawerVisible}
                    width={700}
                    zIndex={1400}
                    bodyStyle={{ padding: 24 }}
                  >
                    {selectedHistoryAlert && (() => {
                      const workflow = extractWorkflowInfo(selectedHistoryAlert);
                      const assetInfo = extractAssetInfo(selectedHistoryAlert);
                      const statusHistory = selectedHistoryAlert.statusHistory || [];
                      // Index of the closure entry that produced the current status -
                      // the same entry wasIgnoredClosure() inspects.
                      const lastActionTakenIndex = statusHistory
                        .map(entry => entry && entry.status)
                        .lastIndexOf('ACTION_TAKEN');
                      
                      return (
                        <div>
                          {/* Alert Type Badge */}
                          <div style={{ marginBottom: 16 }}>
                            {(() => {
                              const typeColor = getAlertTypeColor(selectedHistoryAlert.alertType);
                              return (
                                <span
                                  style={{
                                    fontSize: 12,
                                    padding: '4px 12px',
                                    borderRadius: 999,
                                    background: typeColor.bg,
                                    color: typeColor.color,
                                    fontWeight: 600
                                  }}
                                >
                                  {getAlertTypeLabel(selectedHistoryAlert.alertType)}
                                </span>
                              );
                            })()}
                          </div>

                          {/* Title */}
                          <Typography.Title level={4} style={{ marginBottom: 16 }}>
                            {selectedHistoryAlert.alertTitle}
                          </Typography.Title>

                          {/* Alert Details */}
                          <Card size="small" title="Alert Details" style={{ marginBottom: 16 }}>
                            <Descriptions column={2} size="small" bordered>
                              <Descriptions.Item label="Alert ID">{selectedHistoryAlert.alertId}</Descriptions.Item>
                              <Descriptions.Item label="Status">
                                {selectedHistoryAlert._status ? (
                                  <span>
                                    <Tag color={getStatusInfo(selectedHistoryAlert._status).color}>
                                      {getStatusInfo(selectedHistoryAlert._status).label}
                                    </Tag>
                                    {/* Ignored is derived from the closure remark, so
                                        the raw API value is stated, not replaced. */}
                                    {selectedHistoryAlert._status === 'IGNORED' &&
                                      selectedHistoryAlert.currentStatus === 'ACTION_TAKEN' && (
                                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                          API: ACTION_TAKEN, closed with remark "Ignore"
                                        </Typography.Text>
                                      )}
                                  </span>
                                ) : (
                                  <Tag>No status returned</Tag>
                                )}
                              </Descriptions.Item>
                              <Descriptions.Item label="Asset Code">{assetInfo.assetCode}</Descriptions.Item>
                              <Descriptions.Item label="Alt Name">{assetInfo.altName || 'N/A'}</Descriptions.Item>
                              <Descriptions.Item label="Owner Code">{assetInfo.ownerCode || 'N/A'}</Descriptions.Item>
                              <Descriptions.Item label="Asset Model">{assetInfo.assetModel || 'N/A'}</Descriptions.Item>
                              <Descriptions.Item label="Location">{assetInfo.location}</Descriptions.Item>
                              <Descriptions.Item label="Sub Location">{assetInfo.subLocation || 'N/A'}</Descriptions.Item>
                              <Descriptions.Item label="Assigned To">
                                {selectedHistoryAlert.assignedTo?.name || 'Unassigned'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Created At">
                                {selectedHistoryAlert.createdAt 
                                  ? moment(selectedHistoryAlert.createdAt).format('DD MMM YYYY, HH:mm:ss')
                                  : 'N/A'}
                              </Descriptions.Item>
                            </Descriptions>
                          </Card>

                          {/* Alert Description */}
                          {selectedHistoryAlert.alertDescription && (
                            <Card size="small" title="Alert Description" style={{ marginBottom: 16 }}>
                              <div
                                style={{
                                  maxHeight: 300,
                                  overflow: 'auto',
                                  fontSize: 13,
                                  lineHeight: 1.6
                                }}
                                dangerouslySetInnerHTML={{ __html: selectedHistoryAlert.alertDescription }}
                              />
                            </Card>
                          )}

                          {/* Workflow Information */}
                          <Card size="small" title="Action Summary" style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 16 }}>
                              <Typography.Text strong>Analysis:</Typography.Text>
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 12,
                                  background: '#fafafa',
                                  borderRadius: 4,
                                  fontSize: 13
                                }}
                              >
                                {workflow.analysis || <Typography.Text type="secondary">No analysis recorded</Typography.Text>}
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: 16 }}>
                              <Typography.Text strong>Action Suggested:</Typography.Text>
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 12,
                                  background: '#fff7e6',
                                  borderRadius: 4,
                                  fontSize: 13
                                }}
                              >
                                {workflow.actionSuggested || <Typography.Text type="secondary">No action suggested</Typography.Text>}
                              </div>
                            </div>
                            
                            <div>
                              <Typography.Text strong>Action Taken:</Typography.Text>
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 12,
                                  background: '#f6ffed',
                                  borderRadius: 4,
                                  fontSize: 13,
                                  border: workflow.actionTaken ? '1px solid #b7eb8f' : 'none'
                                }}
                              >
                                {workflow.actionTaken || <Typography.Text type="secondary">No action taken recorded</Typography.Text>}
                              </div>
                            </div>
                            
                            {workflow.closedBy && (
                              <div style={{ marginTop: 16, color: '#8c8c8c', fontSize: 12 }}>
                                <Icon type="user" style={{ marginRight: 4 }} />
                                Closed by <strong>{workflow.closedBy.name}</strong>
                                {workflow.closedAt && (
                                  <span> on {moment(workflow.closedAt).format('DD MMM YYYY, HH:mm:ss')}</span>
                                )}
                              </div>
                            )}
                          </Card>

                          {/* Status History Timeline */}
                          {statusHistory.length > 0 && (
                            <Card size="small" title="Status History">
                              <Timeline mode="left">
                                {statusHistory.map((entry, index) => {
                                  // The terminal ACTION_TAKEN entry of an Ignore
                                  // closure is labelled Ignored so the timeline
                                  // agrees with the Status column; its raw remark
                                  // still prints beside it.
                                  const isIgnoreClosure =
                                    entry.status === 'ACTION_TAKEN' &&
                                    index === lastActionTakenIndex &&
                                    selectedHistoryAlert._status === 'IGNORED';
                                  const statusInfo = getStatusInfo(
                                    isIgnoreClosure ? 'IGNORED' : entry.status
                                  );
                                  return (
                                    <Timeline.Item
                                      key={index}
                                      color={statusInfo.color || 'gray'}
                                      label={
                                        entry.date 
                                          ? moment(entry.date).format('DD MMM YYYY, HH:mm')
                                          : ''
                                      }
                                    >
                                      <div>
                                        <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                                        {entry.remarks && (
                                          <span style={{ marginLeft: 8, color: '#595959' }}>
                                            - {entry.remarks}
                                          </span>
                                        )}
                                      </div>
                                      {entry.updatedBy && (
                                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                          By: {entry.updatedBy.name}
                                        </div>
                                      )}
                                    </Timeline.Item>
                                  );
                                })}
                              </Timeline>
                            </Card>
                          )}
                        </div>
                      );
                    })()}
                  </Drawer>
                </TabPane>
              </Tabs>
            </Card>
          </Col>

        </Row>

        {/* Asset Selection Modal */}
        <Modal
          title="Select Asset and Load Fuel Alert Data"
          visible={assetSelectionModalVisible}
          onCancel={() => {
            setAssetSelectionModalVisible(false);
            setSelectedAssetCode(null);
            setDateRange([null, null]);
            setFuelEntriesList([]);
            setFullTankEvents([]);
            setSelectedFullTankEvent(null);
          }}
          width={1000}
          footer={null}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Asset Code:</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Search by Asset Code, Owner Code, or Alt Name"
                value={selectedAssetCode}
                onChange={(value) => {
                  setSelectedAssetCode(value);
                  setFuelEntriesList([]);
                  setFullTankEvents([]);
                  setSelectedFullTankEvent(null);
                }}
                showSearch
                loading={loadingAssets}
                filterOption={(input, option) => {
                  if (!input || String(input).trim() === '') return true;
                  const searchText = String(input).toLowerCase().trim();
                  const displayText = option.children || option.props?.children || '';
                  const displayTextLower = String(displayText).toLowerCase();
                  const code = option.value || option.props?.value || '';
                  const codeLower = String(code).toLowerCase();
                  const assetInfo = assetCodeMap[code] || {};
                  const ownerCode = String(assetInfo.ownerCode || '').toLowerCase();
                  const altName = String(assetInfo.altName || '').toLowerCase();
                  return displayTextLower.includes(searchText) ||
                         codeLower.includes(searchText) ||
                         ownerCode.includes(searchText) ||
                         altName.includes(searchText);
                }}
              >
                {assetList.map(code => {
                  const assetInfo = assetCodeMap[code] || {};
                  const displayText = `${code}${assetInfo.ownerCode ? ` / ${assetInfo.ownerCode}` : ''}${assetInfo.altName ? ` / ${assetInfo.altName}` : ''}`;
                  return (
                    <Option key={code} value={code}>
                      {displayText}
                    </Option>
                  );
                })}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Date Range:</Text>
              <RangePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: '100%', marginTop: 8 }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [null, null])}
                placeholder={['From Date & Time', 'To Date & Time']}
              />
            </div>

            <Button
              type="primary"
              onClick={handleFetchFuelEntries}
              loading={loadingFuelEntries}
              disabled={!selectedAssetCode || !dateRange || !dateRange[0] || !dateRange[1]}
              style={{ width: '100%', marginBottom: 16 }}
            >
              Fetch Fuel Entries
            </Button>

            {fullTankEvents.length > 0 && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Select Full Tank Event ({fullTankEvents.length} found):
                </Text>
                <Table
                  dataSource={fullTankEvents.map((e, idx) => ({
                    ...e,
                    key: e._id || idx,
                    index: idx + 1,
                  }))}
                  columns={[
                    {
                      title: '#',
                      dataIndex: 'index',
                      width: 50,
                    },
                    {
                      title: 'Voucher No',
                      dataIndex: 'voucherNo',
                      width: 120,
                    },
                    {
                      title: 'Date & Time',
                      key: 'date',
                      width: 180,
                      render: (text, record) => {
                        const date = moment(record.date || record.datetime);
                        return date.format('YYYY-MM-DD HH:mm:ss');
                      },
                    },
                    {
                      title: 'Fuel Quantity (L)',
                      dataIndex: 'quantity',
                      width: 120,
                      align: 'right',
                    },
                    {
                      title: 'Meter Reading',
                      key: 'meter',
                      width: 150,
                      render: (text, record) => {
                        if (record.mileage && record.mileage.length > 0) {
                          const mileage = record.mileage[0];
                          return `${mileage.name || 'N/A'}: ${mileage.reading || 'N/A'}`;
                        }
                        return 'N/A';
                      },
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      width: 100,
                      render: (text, record) => (
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSelectFullTankEvent(record)}
                        >
                          Select
                        </Button>
                      ),
                    },
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ y: 300 }}
                />
              </div>
            )}

            {loadingFuelEntries && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Spin tip="Loading fuel entries..." />
              </div>
            )}

            {!loadingFuelEntries && fuelEntriesList.length > 0 && fullTankEvents.length === 0 && (
              <Alert
                message="No Full Tank Events"
                description="No full tank events found in the selected period. Please select a different date range."
                type="warning"
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        </Modal>

        {/* Data Input Modal */}
        <Modal
          visible={dataInputModalVisible}
          title={
            <span>
              <Icon type="file-text" style={{ marginRight: 8 }} />
              Load Fuel Alert Data
            </span>
          }
          onCancel={() => {
            setDataInputModalVisible(false);
            setDataInputText('');
          }}
          onOk={handleDataInputSubmit}
          okText="Load Data"
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Alert
              type="info"
              message="Paste the fuel alert data in the text field below. The format should match the example shown."
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginBottom: 8 }}>
              <strong>Example Format:</strong>
            </div>
            <div style={{ 
              padding: 12, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 4, 
              fontSize: 12, 
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              marginBottom: 16
            }}>
              Asset Code/Alt Name/Owner Code: 1WT-89 / NL01AF9143 / AL-05{'\n'}
              Asset Model: ASHOK LEYLAND 2825TN{'\n'}
              Asset Sub-Type: 10 WHEEL TIPPER{'\n'}
              Voucher No: FL-32666{'\n'}
              Refueling Date and Time: 12-12-2025 15:58:34 GMT{'\n'}
              Location of Refueling: 26.103312,91.813283{'\n'}
              Fuel Added: 127 litre{'\n'}
              Refueled Odometer Reading: 29168 KMS{'\n'}
              Last Full Tank Odometer Reading: 29095 KMS (29095 KMS){'\n'}
              Last Full Tank Date: 13-09-2025 04:57:58 GMT{'\n'}
              KMS-run: 73.00 KMS{'\n'}
              Consumption Rate Calculation: 1.74 Qty/KMS (0.57 KMS/Qty){'\n'}
              Standard Consumption Rate: 1.25 Qty/KMS (0.80 KMS/Qty){'\n'}
              Deviation from Standard: 39.18% KMS
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Paste Data Here:</strong>
            </div>
            <TextArea
              rows={15}
              value={dataInputText}
              onChange={(e) => setDataInputText(e.target.value)}
              placeholder="Paste the fuel alert data here..."
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        </Modal>

        {/* Photo Viewer Modal */}
        <Modal
          visible={photoViewerVisible}
          title={
            <span>
              <Icon type="picture" style={{ marginRight: 8 }} />
              Photos for Voucher {selectedVoucherNo}
            </span>
          }
          onCancel={() => {
            setPhotoViewerVisible(false);
            setSelectedVoucherPhotos([]);
            setSelectedVoucherNo(null);
            setFailedPhotoUrls(new Set());
          }}
          footer={[
            <Button key="close" onClick={() => {
              setPhotoViewerVisible(false);
              setSelectedVoucherPhotos([]);
              setSelectedVoucherNo(null);
              setFailedPhotoUrls(new Set());
            }}>
              Close
            </Button>,
          ]}
          width={800}
        >
          {selectedVoucherPhotos && selectedVoucherPhotos.length > 0 ? (
            <Row gutter={16}>
              {selectedVoucherPhotos.map((photo, index) => (
                <Col span={12} key={photo.id} style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      border: '1px solid #e8e8e8',
                      borderRadius: 4,
                      padding: 8,
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        height: 200,
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 8,
                        borderRadius: 4,
                        border: '1px dashed #d9d9d9',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {photo.url && !failedPhotoUrls.has(photo.url) ? (
                        <img
                          src={photo.url}
                          alt={`Photo ${index + 1}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            cursor: 'pointer',
                          }}
                          onError={() => {
                            // Mark this photo URL as failed
                            setFailedPhotoUrls(prev => new Set([...prev, photo.url]));
                          }}
                          onClick={() => {
                            window.open(photo.url, '_blank');
                          }}
                        />
                      ) : (
                      <div>
                        <Icon type="picture" style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 8 }} />
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          Photo {index + 1}
                        </div>
                        <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 4 }}>
                            {photo.url ? 'Failed to load image' : 'No image URL available'}
                        </div>
                      </div>
                      )}
                    </div>
                    <Button
                      type="link"
                      size="small"
                      icon="eye"
                      onClick={() => {
                        // In real app, open full-size photo viewer
                        window.open(photo.url, '_blank');
                      }}
                    >
                      View Full Size
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
              <Icon type="picture" style={{ fontSize: 48, marginBottom: 8 }} />
              <div>No photos available for this voucher</div>
            </div>
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default FuelAlertAnalysis;
